import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import {
  Box,
  Typography,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Menu,
  Switch,
  Tooltip,
  Collapse,
  Autocomplete
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeIcon from "@mui/icons-material/Home";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import LockIcon from "@mui/icons-material/Lock";
import LogoutIcon from "@mui/icons-material/Logout";
import SortIcon from "@mui/icons-material/Sort";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import InfoIcon from "@mui/icons-material/Info";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { apiFetch } from "../lib/supabase";
import OTPInput from "../components/OTPInput";
import { UnavailabilityCalendar } from "../components/UnavailabilityCalendar";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, parse, isValid } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { loadSettings, saveSettings, DEFAULT_SETTINGS } from "../lib/settings";
import * as XLSX from "xlsx";
import { useSettings } from "../context/SettingsContext";
import { useAdminAuth } from "../context/AdminAuthContext";
import SettingsIcon from "@mui/icons-material/Settings";
import SaveIcon from "@mui/icons-material/Save";
import SyncIcon from "@mui/icons-material/Sync";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChatIcon from "@mui/icons-material/Chat";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BadgeIcon from "@mui/icons-material/Badge";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import FilterListIcon from "@mui/icons-material/FilterList";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PaymentIcon from "@mui/icons-material/Payment";
import LoginIcon from "@mui/icons-material/Login";
import UpdateIcon from "@mui/icons-material/Update";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import UserHeader from "../components/UserHeader";
import "./admin.css";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  width: '100%',
  maxWidth: '100%',
  margin: '0 auto',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    borderRadius: '12px',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
  color: '#0d2c54',
  fontFamily: "'Chillax', sans-serif",
  fontWeight: 500,
}));

const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const MemberCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  border: '1px solid #e0e0e0',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    borderColor: '#dbf14e',
  },
}));

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const fieldLabels = {
  first_name: "First Name",
  name: "Last Name",
  email: "Email",
  alt_email: "Alternative email for duty group",
  address: "Address",
  phone_number: "Mobile Number",
  duty_type: "Duty Type",
  first_training_weekend: "1st Training Weekend",
  first_pool_session: "1st Pool Session",
  second_pool_session: "2nd Pool Session",
  second_theory_session: "2nd Theory Session",
  friday_duty: "Friday Duty",
  saturday_duty: "Saturday Duty",
  sunday_duty: "Sunday Duty",
  monday_duty: "Monday Duty",
  tuesday_duty: "Tuesday Duty",
  wednesday_duty: "Wednesday Duty",
  school_hol_thursday_duty: "School Hol. Thursday Duty",
  school_hol_tuesday_duty: "School Hol. Tuesday Duty",
  holiday_periods: "Holiday Periods",
  incl_phone_in_rota: "Include Phone in Rota",
  incl_sms_reminders: "SMS Duty Reminders",
  payment_success: "Payment Success",
  do_not_delete: "Do Not Delete",
  family_members: "Household Members",
  created_time: "Created Time",
  last_edited_by: "Last Edited By",
  last_edited_time: "Last Edited Time"
};

const dutyTypes = ["Administrator", "1st Safety Officer", "2nd Safety Officer"];
const yesNoOptions = ["Yes", "No"];

const calculateAge = (dobString) => {
  if (!dobString) return null;
  const dob = new Date(dobString);
  if (isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
};

const parseFamilyMembers = (familyMembersStr) => {
  if (!familyMembersStr || familyMembersStr.trim() === "") return [];
  const entries = familyMembersStr.split(/[,\n]/).map(s => s.trim()).filter(s => s.length > 0);
  return entries.map((entry, index) => {
    const dobMatch = entry.match(/\((\d{4}-\d{2}-\d{2})/);
    const dob = dobMatch ? dobMatch[1] : null;
    const age = calculateAge(dob);
    const name = entry.replace(/\s*\([^)]*\)/g, '').replace(/Swimming permission|Accompanied by adult/gi, '').trim();
    const isMinor = age !== null && age < 18;
    const status = entry.includes('Swimming permission') ? 'Swimming permission' : 
                   entry.includes('Accompanied by adult') ? 'Accompanied by adult' : null;
    return { id: index, name, dob, age, isMinor, status, original: entry };
  });
};

const parseHolidayPeriods = (holidayPeriodsStr) => {
  if (!holidayPeriodsStr || holidayPeriodsStr.trim() === "" || holidayPeriodsStr.trim() === " ") return [];
  console.log('Parsing holiday periods:', holidayPeriodsStr);
  
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
  
  const results = holidayPeriodsStr.split(',')
    .map(s => s.trim())
    .filter(s => s && s.length > 0 && s !== " ")
    .map(s => {
      if (isoDateRegex.test(s)) {
        return s;
      }
      const date = new Date(s);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        console.log('Converted date:', s, 'to', `${year}-${month}-${day}`);
        return `${year}-${month}-${day}`;
      }
      console.log('Date rejected (unparseable):', s);
      return null;
    })
    .filter(s => s !== null);
  
  console.log('Parsed results:', results);
  return results;
};

const formatHolidayPeriodsForSave = (periods) => {
  return periods.join(', ');
};

const formatFamilyMembersForSave = (members) => {
  return members.map(m => m.original).join(', ');
};

export default function Admin() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated, adminEmail, loading: authLoading, requestCode, verifyCode, logout } = useAdminAuth();
  const { getTrainingWeekends, getTheorySessions, getPoolSessions, getSafetyOfficerDates, getSeasonConfig } = useSettings();
  const SAFETY_OFFICER_DATES = getSafetyOfficerDates();
  const SEASON_CONFIG = getSeasonConfig();
  const currentYear = new Date().getFullYear();
  const [activeTab, setActiveTab] = useState(0);
  const [adminRole, setAdminRole] = useState('full');

  const PERMISSION_DEFS = [
    { id: 'members', label: 'Members' },
    { id: 'rota', label: 'Rota' },
    { id: 'settings', label: 'Settings' },
    { id: 'chatbot', label: 'Chatbot' },
    { id: 'help', label: 'Help Triggers' },
    { id: 'activity', label: 'Activity Log' },
    { id: 'users', label: 'Users' },
  ];
  const adminPermissions = adminRole === 'full'
    ? PERMISSION_DEFS.map(p => p.id)
    : (adminRole || '').split(',').filter(Boolean);
  const hasPermission = (perm) => adminRole === 'full' || adminPermissions.includes(perm);
  const visibleAdminTabs = PERMISSION_DEFS.filter(t => hasPermission(t.id));
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabId = visibleAdminTabs[activeTab]?.id || visibleAdminTabs[0]?.id || 'members';
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  // Unified search: which field the search box matches. 'name' uses the chip
  // multi-select (memberNameFilter); 'email'/'phone' use searchTerm text;
  // 'duty_date' matches members rota'd on the date in searchTerm.
  const [searchField, setSearchField] = useState("name");
  const [dutyTypeFilter, setDutyTypeFilter] = useState("All");
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    dutyDay: "All",
    hasPhone: "All",
    hasFamily: "All",
    phoneInRota: "All",
    hadPriorTraining: "All",
    paymentStatus: "All",
    hasTrainingSession: "All",
    addedBy: "All",
    currentSeason: "All",
    rotaInPublished: "All",
    emailBounced: "All",
  });
  const [membersInPublishedRota, setMembersInPublishedRota] = useState(null);
  const [membersWithUpcomingDuties, setMembersWithUpcomingDuties] = useState(null);
  const [rotaPublishedNone, setRotaPublishedNone] = useState(false);
  const [rotaConflicts, setRotaConflicts] = useState(null);
  const [rotaConflictsExpanded, setRotaConflictsExpanded] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [memberYearsMap, setMemberYearsMap] = useState({});
  const [sortBy, setSortBy] = useState("date");
  const [memberEdited, setMemberEdited] = useState(false);
  const [memberDuties, setMemberDuties] = useState(null);
  const [memberDutiesLoading, setMemberDutiesLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [activeEmailJob, setActiveEmailJob] = useState(null);
  const [holidayPeriods, setHolidayPeriods] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [editingFamilyMember, setEditingFamilyMember] = useState(null);
  const [familyMemberForm, setFamilyMemberForm] = useState({ name: "", dob: "", status: "" });
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [distListSyncing, setDistListSyncing] = useState(false);
  const [distListResults, setDistListResults] = useState(null);
  
  const [loginEmail, setLoginEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [loginStep, setLoginStep] = useState("email");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuMember, setMenuMember] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [membershipYears, setMembershipYears] = useState([]);
  const [newYear, setNewYear] = useState("");
  const [dialogTab, setDialogTab] = useState(0);
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailHistoryLoading, setEmailHistoryLoading] = useState(false);
  const [expandedEmailId, setExpandedEmailId] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [activityLogLoading, setActivityLogLoading] = useState(false);
  const [chatbotUsage, setChatbotUsage] = useState(null);
  const [chatbotPrompts, setChatbotPrompts] = useState([]);
  const [chatbotLoading, setChatbotLoading] = useState(false);
  const [chatbotQuestions, setChatbotQuestions] = useState([]);
  const [chatbotQuestionsLoading, setChatbotQuestionsLoading] = useState(false);
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [clearQuestionsConfirmOpen, setClearQuestionsConfirmOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [promptForm, setPromptForm] = useState({ system_prompt: '', greeting_message: '' });
  const [helpTriggers, setHelpTriggers] = useState([]);
  const [helpTriggersLoading, setHelpTriggersLoading] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState(null);
  const [triggerForm, setTriggerForm] = useState({ label: '', question: '', page: 'registration' });
  const [showNewTriggerForm, setShowNewTriggerForm] = useState(false);
  const [newTriggerForm, setNewTriggerForm] = useState({ trigger_key: '', label: '', question: '', page: 'registration' });
  const [renewalConfirmOpen, setRenewalConfirmOpen] = useState(false);
  const [rotaDutiesConfirmOpen, setRotaDutiesConfirmOpen] = useState(false);
  const [rotaDutiesSingleMember, setRotaDutiesSingleMember] = useState(null);
  const [rotaPreviewOpen, setRotaPreviewOpen] = useState(false);
  const [rotaPreviewTab, setRotaPreviewTab] = useState('1st Safety Officer');
  const [rotaPreviewHtml, setRotaPreviewHtml] = useState(null);
  const [rotaPreviewSubject, setRotaPreviewSubject] = useState(null);
  const [rotaPreviewLoading, setRotaPreviewLoading] = useState(false);
  const [rotaPreviewMessage, setRotaPreviewMessage] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [singleEmailMember, setSingleEmailMember] = useState(null);
  const [composeForm, setComposeForm] = useState({ subject: '', body: '', scheduleDate: '', scheduleTime: '' });
  const [composeSending, setComposeSending] = useState(false);
  const [composeConfirmOpen, setComposeConfirmOpen] = useState(false);
  const [composeConfirmLoading, setComposeConfirmLoading] = useState(false);
  const [composeConfirmData, setComposeConfirmData] = useState(null);
  const [excludedMemberIds, setExcludedMemberIds] = useState(new Set());
  // "By name" member filter (multi-select). When non-empty, the member list is
  // restricted to exactly these members — another way of building the filtered
  // list that every send action (Compose, Send Rota, Opening Times, …) uses.
  const [memberNameFilter, setMemberNameFilter] = useState([]);
  const [recipientsExpanded, setRecipientsExpanded] = useState(false);
  const quillRef = useRef(null);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [globalActivityLog, setGlobalActivityLog] = useState([]);
  const [globalActivityLogLoading, setGlobalActivityLogLoading] = useState(false);
  const [activityDateFrom, setActivityDateFrom] = useState('');
  const [activityDateTo, setActivityDateTo] = useState('');
  const [activityTypeFilter, setActivityTypeFilter] = useState('all');
  const [expandedActivityIds, setExpandedActivityIds] = useState(new Set());

  const [rotaMonth, setRotaMonth] = useState(new Date());
  const [rotaAssignments, setRotaAssignments] = useState([]);
  const [rotaAllAssignments, setRotaAllAssignments] = useState([]);
  const [rotaLoading, setRotaLoading] = useState(false);
  const [rotaSelectedDate, setRotaSelectedDate] = useState(null);
  const [rotaEditDialog, setRotaEditDialog] = useState(null);
  const [rotaMembers, setRotaMembers] = useState([]);
  const [rotaTotalDutyCounts, setRotaTotalDutyCounts] = useState({});
  const [rotaSort, setRotaSort] = useState({ column: 'date', direction: 'asc' });
  const [rotaFilterMode, setRotaFilterMode] = useState('future');
  const [rotaFilterStart, setRotaFilterStart] = useState('');
  const [rotaFilterEnd, setRotaFilterEnd] = useState('');
  const [rotaSearch, setRotaSearch] = useState('');
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [rotaInfoPanelOpen, setRotaInfoPanelOpen] = useState(false);
  const [rotaHoveredMemberId, setRotaHoveredMemberId] = useState(null);
  const [rotaReminderSending, setRotaReminderSending] = useState(null); // "date|time" key
  const [rotaHolidaysExpanded, setRotaHolidaysExpanded] = useState(false);
  const [rotaClosedDatesExpanded, setRotaClosedDatesExpanded] = useState(false);
  // Opening-times broadcast email (Members tab — behaves like Send Rota Duties)
  const [openingEmailOpen, setOpeningEmailOpen] = useState(false);
  const [openingEmailSending, setOpeningEmailSending] = useState(false);
  // Record-swap dialog state
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [swapMemberA, setSwapMemberA] = useState(null);
  const [swapAssignmentA, setSwapAssignmentA] = useState(null);
  const [swapMemberB, setSwapMemberB] = useState(null);
  const [swapAssignmentB, setSwapAssignmentB] = useState(null);
  const [swapSaving, setSwapSaving] = useState(false);
  const [swapShowPast, setSwapShowPast] = useState(false);
  const [swapNotifyOpen, setSwapNotifyOpen] = useState(false);
  const [swapNotifyMemberIds, setSwapNotifyMemberIds] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPerms, setNewUserPerms] = useState([]);

  const fetchRotaMonth = async (date) => {
    setRotaLoading(true);
    try {
      const y = date.getFullYear();
      const m = date.getMonth() + 1;
      const res = await fetch(`/api/rota/month?year=${y}&month=${m}&admin=1`, {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` }
      });
      if (!res.ok) throw new Error('Failed to load rota');
      const data = await res.json();
      setRotaAssignments(data.assignments || []);
    } catch (err) {
      console.error('Failed to fetch rota', err);
      setSnackbar({ open: true, message: 'Failed to load rota data', severity: 'error' });
    } finally {
      setRotaLoading(false);
    }
  };

  const fetchRotaRange = async (start, end) => {
    setRotaLoading(true);
    try {
      const adminToken = sessionStorage.getItem('adminToken');
      const res = await fetch(`/api/rota/range?startDate=${start}&endDate=${end}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (!res.ok) throw new Error('Failed to load rota');
      const data = await res.json();
      setRotaAllAssignments(data.assignments || []);
    } catch (err) {
      console.error('Failed to fetch rota range', err);
      setSnackbar({ open: true, message: 'Failed to load rota data', severity: 'error' });
    } finally {
      setRotaLoading(false);
    }
  };

  const fetchRotaMembers = async () => {
    try {
      const res = await fetch('/api/rota/members', {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` }
      });
      if (!res.ok) throw new Error('Failed to load members');
      const data = await res.json();
      setRotaMembers(data.members || []);
    } catch (err) {
      console.error('Failed to fetch rota members', err);
      setSnackbar({ open: true, message: 'Failed to load rota members', severity: 'error' });
    }
  };

  const fetchRotaDutyCounts = async () => {
    try {
      const res = await fetch('/api/rota/duty-counts');
      if (!res.ok) throw new Error('Failed to load duty counts');
      const data = await res.json();
      setRotaTotalDutyCounts(data.counts || {});
    } catch (err) {
      console.error('Failed to fetch duty counts', err);
    }
  };

  // Toggle a whole session date closed/open (cancellation). Closed dates are
  // hidden as needing cover, skip reminders, and show "Closed" on the home page.
  const toggleSessionClosed = async (date) => {
    const current = Array.isArray(settings.pool_closed_dates) ? settings.pool_closed_dates : [];
    const isClosed = current.includes(date);
    const updated = isClosed ? current.filter(d => d !== date) : [...current, date].sort();
    try {
      const res = await fetch('/api/admin/rota/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` },
        body: JSON.stringify({ pool_closed_dates: updated }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed');
      setSettings(prev => ({ ...prev, pool_closed_dates: updated }));
      setSnackbar({ open: true, message: isClosed ? 'Session reopened' : 'Session marked closed', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: `Error: ${e.message}`, severity: 'error' });
    }
  };

  // A session is "fixed" once its rota half is published — it can then only be
  // changed via a swap, not the inline dropdown. Mirrors the server-side guard.
  const isRotaDateFixed = (dateStr) => {
    const { first_half_published, second_half_published, first_half_end, second_half_start } = settings || {};
    if (!dateStr) return false;
    if (first_half_end && dateStr <= first_half_end) return !!first_half_published;
    if (second_half_start && dateStr >= second_half_start) return !!second_half_published;
    if (!first_half_end && !second_half_start) return !!(first_half_published || second_half_published);
    return false;
  };

  const handleRotaAssign = async (assignment) => {
    const adminToken = sessionStorage.getItem('adminToken');
    try {
      const res = await fetch('/api/admin/rota/assign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(assignment)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSnackbar({ open: true, message: 'Assignment saved', severity: 'success' });
      // Patch only the changed row into local state instead of refetching the
      // whole season — a full refetch toggles the loading state and rebuilds the
      // list, scrolling the page back to the top. Fall back to a refetch only if
      // the updated row isn't returned.
      if (data.assignment) {
        const a = data.assignment;
        setRotaAllAssignments(prev => {
          const idx = prev.findIndex(x =>
            (a.id && x.id === a.id) ||
            (x.session_date === a.session_date && x.session_time === a.session_time &&
             x.role_type === a.role_type && (x.slot_number || 1) === (a.slot_number || 1)));
          if (idx === -1) return [...prev, a];
          const next = prev.slice();
          next[idx] = { ...next[idx], ...a };
          return next;
        });
      } else if (settings.season_start && settings.season_end) {
        fetchRotaRange(settings.season_start, settings.season_end);
      }
      fetchRotaDutyCounts();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleRotaDelete = async (id) => {
    const adminToken = sessionStorage.getItem('adminToken');
    try {
      const res = await fetch(`/api/admin/rota/assign/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (!res.ok) throw new Error('Failed to delete');
      setSnackbar({ open: true, message: 'Assignment removed', severity: 'success' });
      if (settings.season_start && settings.season_end) fetchRotaRange(settings.season_start, settings.season_end);
      fetchRotaDutyCounts();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  // Maps the rota_assignment.role_type values to the duty_type stored on
  // CCSCv3_members, so we can filter Member B to people who do the same role.
  const ROLE_TYPE_TO_DUTY_TYPE = {
    '1st_safety_officer': '1st Safety Officer',
    '2nd_safety_officer': '2nd Safety Officer',
    'administrator': 'Administrator',
  };
  const ROLE_TYPE_LABEL = {
    '1st_safety_officer': '1st Safety Officer',
    '2nd_safety_officer': '2nd Safety Officer',
    'administrator': 'Administrator',
  };

  const formatAssignmentLabel = (a) => {
    if (!a) return '';
    const d = new Date(a.session_date + 'T12:00:00');
    const dateStr = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    const time = (a.session_time || '').split('-')[0].slice(0, 5);
    const role = ROLE_TYPE_LABEL[a.role_type] || a.role_type;
    return `${dateStr} · ${time} · ${role}`;
  };

  const resetSwapDialog = () => {
    setSwapMemberA(null);
    setSwapAssignmentA(null);
    setSwapMemberB(null);
    setSwapAssignmentB(null);
    setSwapShowPast(false);
  };

  // Validate the swap/cover dialog. Returns { mode: 'swap'|'cover' } when valid,
  // else { error }. Both duties filled = swap; exactly one filled = a one-way
  // cover where the member on the empty side takes the chosen duty.
  const swapCoverValidation = () => {
    const a = swapMemberA, b = swapMemberB, da = swapAssignmentA, db = swapAssignmentB;
    if (!a || !b) return { error: 'Pick both members.' };
    if (a.id === b.id) return { error: 'Pick two different members.' };
    if (!da && !db) return { error: 'Pick at least one duty to move.' };
    if (da && db) {
      if (da.id === db.id) return { error: 'A duty cannot be swapped with itself.' };
      if (da.role_type !== db.role_type) return { error: 'Both duties must be the same role to swap.' };
      return { mode: 'swap' };
    }
    const moving = da || db;
    const taker = da ? b : a;
    const need = ROLE_TYPE_TO_DUTY_TYPE[moving.role_type];
    if (need && (taker.duty_type || '').trim() !== need) {
      return { error: `${taker.first_name} ${taker.name} must be a ${need} to take this duty.` };
    }
    return { mode: 'cover' };
  };

  const handleSubmitSwap = async () => {
    const v = swapCoverValidation();
    if (!v.mode) return;
    setSwapSaving(true);
    try {
      let result;
      if (v.mode === 'swap') {
        result = await apiFetch('/api/admin/rota/swap', {
          method: 'POST',
          body: { assignmentAId: swapAssignmentA.id, assignmentBId: swapAssignmentB.id },
        });
      } else {
        const movingAssignment = swapAssignmentA || swapAssignmentB;
        const taker = swapAssignmentA ? swapMemberB : swapMemberA;
        result = await apiFetch('/api/admin/rota/cover', {
          method: 'POST',
          body: { assignmentId: movingAssignment.id, takerMemberId: taker.id },
        });
      }
      const memberAId = result.memberAId;
      const memberBId = result.memberBId;
      setSnackbar({ open: true, message: v.mode === 'swap' ? 'Duty swap recorded.' : 'Duty cover recorded.', severity: 'success' });
      if (settings.season_start && settings.season_end) {
        await fetchRotaRange(settings.season_start, settings.season_end);
      }
      setSwapDialogOpen(false);
      resetSwapDialog();
      setSwapNotifyMemberIds([memberAId, memberBId].filter(Boolean));
      setSwapNotifyOpen(true);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to record', severity: 'error' });
    } finally {
      setSwapSaving(false);
    }
  };

  const handleNotifyAfterSwap = async () => {
    const memberIds = swapNotifyMemberIds || [];
    setSwapNotifyOpen(false);
    setSwapNotifyMemberIds(null);
    if (memberIds.length === 0) return;
    try {
      const result = await apiFetch('/api/admin/send-rota-duties', {
        method: 'POST',
        body: { memberIds },
      });
      setSnackbar({
        open: true,
        message: result.message || `Sending updated duties to ${memberIds.length} members…`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to send updated duties', severity: 'error' });
    }
  };

  const rotaPreviewAbortRef = useRef(null);

  const fetchRotaEmailPreview = async (dutyType) => {
    if (rotaPreviewAbortRef.current) {
      rotaPreviewAbortRef.current.abort();
    }
    const controller = new AbortController();
    rotaPreviewAbortRef.current = controller;

    setRotaPreviewLoading(true);
    setRotaPreviewHtml(null);
    setRotaPreviewSubject(null);
    setRotaPreviewMessage(null);
    try {
      const response = await fetch(`/api/admin/preview-rota-email?dutyType=${encodeURIComponent(dutyType)}`, {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` },
        signal: controller.signal
      });
      const data = await response.json();
      if (controller.signal.aborted) return;
      if (!response.ok) {
        setRotaPreviewMessage(data.error || 'Failed to load preview');
      } else if (!data.html) {
        setRotaPreviewMessage(`No published upcoming duties found for "${dutyType}" — publish the rota or assign duties first.`);
      } else {
        setRotaPreviewHtml(data.html);
        setRotaPreviewSubject(data.subject || null);
        setRotaPreviewMessage(null);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setRotaPreviewMessage('Failed to load preview: ' + err.message);
    } finally {
      if (!controller.signal.aborted) setRotaPreviewLoading(false);
    }
  };

  const handleExportXLS = () => {
    const membersToExport = filteredMembers;

    const rows = membersToExport.map(m => ({
      "Last Name": m.name || "",
      "First Name": m.first_name || "",
      "Email": m.email || "",
      "Phone": m.phone_number || "",
      "Address": m.address || "",
      "Membership Code": m.membership_code || "",
      "Duty Type": m.duty_type || "",
      "Include Phone in Rota": m.incl_phone_in_rota || "",
      "Had Prior Training": m.had_prior_training || "",
      "1st Training Weekend": m.first_training_weekend || "",
      "1st Pool Session": m.first_pool_session || "",
      "2nd Theory Session": m.second_theory_session || "",
      "2nd Pool Session": m.second_pool_session || "",
      "Family Members": m.family_members || "",
      "Unavailable Dates": Array.isArray(m.unavailable_dates) ? m.unavailable_dates.join(", ") : (m.unavailable_dates || ""),
      "Membership Years": memberYearsMap[m.id] ? memberYearsMap[m.id].map(y => y.year).sort().join(", ") : "",
      "Added By": m.added_by || "",
      "Created": m.created_at ? format(new Date(m.created_at), "dd MMM yyyy") : "",
      "Last Updated": m.updated_at ? format(new Date(m.updated_at), "dd MMM yyyy") : "",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const colWidths = Object.keys(rows[0] || {}).map(key => ({
      wch: Math.max(key.length, ...rows.map(r => String(r[key] || "").length).slice(0, 50)) + 2
    }));
    ws["!cols"] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Members");
    XLSX.writeFile(wb, `CCSC_Members_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
  };


  const handleRequestCode = async () => {
    if (!loginEmail) {
      setLoginError("Please enter your email address");
      return;
    }
    setLoginLoading(true);
    setLoginError("");
    const result = await requestCode(loginEmail);
    setLoginLoading(false);
    if (result.success) {
      setLoginStep("code");
    } else {
      setLoginError(result.error || "Failed to send verification code");
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setLoginError("Please enter the verification code");
      return;
    }
    setLoginLoading(true);
    setLoginError("");
    const result = await verifyCode(loginEmail, verificationCode);
    setLoginLoading(false);
    if (!result.success) {
      setLoginError(result.error || "Invalid verification code");
    }
  };

  const handleLogout = () => {
    logout();
    setLoginEmail("");
    setVerificationCode("");
    setLoginStep("email");
  };

  // A 401 means the admin token is missing or expired. Drop to the login screen
  // (rendered when !isAuthenticated) instead of surfacing a raw "Unauthorized".
  const handleAuthError = (err) => {
    if (err && err.status === 401) {
      logout();
      return true;
    }
    return false;
  };

  const fetchMembers = async () => {
    setLoading(true);
    setError(null);
    let lastErr = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { members } = await apiFetch("/api/admin/members");
        setMembers(members || []);
        setLoading(false);
        return;
      } catch (err) {
        lastErr = err;
        if (handleAuthError(err)) { setLoading(false); return; }
        if (attempt < 2) {
          console.log(`Retrying fetchMembers... (${2 - attempt} retries left)`);
          await new Promise(r => setTimeout(r, 1000));
        }
      }
    }
    setError(lastErr?.message || "Failed to fetch members");
    console.error("Error fetching members:", lastErr);
    setLoading(false);
  };

  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const data = await loadSettings();
      try {
        const rotaResp = await fetch('/api/admin/rota/settings');
        if (rotaResp.ok) {
          const rotaConfig = await rotaResp.json();
          data.rota_pool_days = rotaConfig.rota_pool_days;
          data.rota_school_holidays = rotaConfig.rota_school_holidays;
          data.pool_closed_dates = rotaConfig.pool_closed_dates || [];
          if (rotaConfig.first_half_start_date) data.first_half_start_date = rotaConfig.first_half_start_date;
          if (rotaConfig.first_half_end_date) data.first_half_end_date = rotaConfig.first_half_end_date;
          if (rotaConfig.second_half_start_date) data.second_half_start_date = rotaConfig.second_half_start_date;
          if (rotaConfig.second_half_end_date) data.second_half_end_date = rotaConfig.second_half_end_date;
          data.first_half_published = rotaConfig.first_half_published || false;
          data.second_half_published = rotaConfig.second_half_published || false;
          data.first_half_end = rotaConfig.first_half_end || '';
          data.second_half_start = rotaConfig.second_half_start || '';
          data.first_half_generated_at = rotaConfig.first_half_generated_at || '';
          data.second_half_generated_at = rotaConfig.second_half_generated_at || '';
        }
      } catch (e) { /* rota settings not critical */ }
      try {
        const ms = await apiFetch('/api/admin/member-swaps-config');
        if (ms) data.member_swaps_test_emails = ms.member_swaps_test_emails || '';
      } catch (e) { /* member-swaps config not critical */ }
      setSettings(data);
    } catch (err) {
      console.error("Error loading settings:", err);
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchAvailableYears = async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { membershipYears: data } = await apiFetch("/api/admin/membership-years");

        const yearsSet = new Set();
        const yearsByMember = {};
        
        (data || []).forEach(row => {
          yearsSet.add(row.year);
          if (!yearsByMember[row.member_id]) {
            yearsByMember[row.member_id] = [];
          }
          yearsByMember[row.member_id].push({ year: row.year, created_at: row.created_at });
        });
        
        const sortedYears = Array.from(yearsSet).sort((a, b) => b - a);
        setAvailableYears(sortedYears);
        setMemberYearsMap(yearsByMember);
        return;
      } catch (err) {
        if (handleAuthError(err)) return;
        if (attempt < 2) {
          console.log(`Retrying fetchAvailableYears... (${2 - attempt} retries left)`);
          await new Promise(r => setTimeout(r, 1000));
        } else {
          console.error("Error fetching available years:", err);
        }
      }
    }
  };

  const fetchMembersInPublishedRota = async () => {
    try {
      const token = sessionStorage.getItem('adminToken');
      if (!token) return;
      const res = await fetch('/api/admin/rota/members-with-published-duties', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setRotaPublishedNone(!!data.nonePublished);
      setMembersInPublishedRota(new Set(data.memberIds || []));
      setMembersWithUpcomingDuties(new Set(data.upcomingMemberIds || []));
    } catch (err) {
      console.error('Error fetching published rota member list:', err);
    }
  };

  const fetchRotaConflicts = async () => {
    try {
      const token = sessionStorage.getItem('adminToken');
      if (!token) return;
      const res = await fetch('/api/admin/rota/conflicts', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setRotaConflicts(data.conflicts || []);
    } catch (err) {
      console.error('Error fetching rota conflicts:', err);
    }
  };

  // Opening-times email respects the current Members-tab filter, exactly like
  // the custom-email / rota-duties sends (filteredMembers, with an email address).
  const openingRecipientList = () => filteredMembers.filter(m => m.email && m.email.trim());

  const previewOpeningPdf = async () => {
    try {
      const res = await fetch('/api/admin/opening-times-email/pdf', {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` }
      });
      if (!res.ok) throw new Error('Failed to generate PDF');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (err) {
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
    }
  };

  const sendOpeningTimesEmail = async () => {
    const recipients = openingRecipientList();
    if (recipients.length === 0) {
      setSnackbar({ open: true, message: 'No members with an email match the current filter.', severity: 'warning' });
      return;
    }
    setOpeningEmailSending(true);
    try {
      const res = await fetch('/api/admin/send-opening-times', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` },
        body: JSON.stringify({ memberIds: recipients.map(m => m.id) })
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to send');
      setOpeningEmailOpen(false);
      setSnackbar({ open: true, message: result.message, severity: 'info' });
      if (result.jobId) {
        setActiveEmailJob({ jobId: result.jobId, type: 'Opening Times', total: result.total });
        const pollInterval = setInterval(async () => {
          try {
            const sr = await fetch(`/api/admin/email-job/${result.jobId}`, {
              headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` }
            });
            if (!sr.ok) { clearInterval(pollInterval); setActiveEmailJob(null); return; }
            const job = await sr.json();
            if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
              clearInterval(pollInterval);
              setActiveEmailJob(null);
              if (job.status === 'cancelled') return;
              let message = `Sent ${job.sent} opening-times email(s)`;
              if (job.failed > 0) message += `, ${job.failed} failed`;
              setSnackbar({ open: true, message, severity: job.failed > 0 ? 'warning' : 'success' });
            }
          } catch (e) {
            clearInterval(pollInterval); setActiveEmailJob(null);
          }
        }, 3000);
      }
    } catch (err) {
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
    } finally {
      setOpeningEmailSending(false);
    }
  };

  const fetchChatbotUsage = async () => {
    try {
      const res = await fetch('/api/chatbot/status');
      const data = await res.json();
      setChatbotUsage(data.usage);
    } catch (err) {
      console.error("Error fetching chatbot usage:", err);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    const result = await saveSettings(settings);
    setSettingsSaving(false);
    if (result.success) {
      setSnackbar({ open: true, message: "Settings saved successfully!", severity: "success" });
    } else {
      setSnackbar({ open: true, message: `Error saving settings: ${result.error}`, severity: "error" });
    }
  };

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSyncDistributionLists = async () => {
    setDistListSyncing(true);
    setDistListResults(null);
    try {
      const response = await fetch('/api/admin/sync-all-distribution-lists', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Sync failed');
      setDistListResults(data.results);
      setSnackbar({ open: true, message: `Distribution list sync complete: ${data.results.synced} synced, ${data.results.skipped} skipped, ${data.results.failed} failed`, severity: data.results.failed > 0 ? "warning" : "success" });
    } catch (err) {
      setSnackbar({ open: true, message: `Error syncing distribution lists: ${err.message}`, severity: "error" });
    } finally {
      setDistListSyncing(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchMembers();
    fetchSettings();
    fetchChatbotUsage();
    fetchAvailableYears();
    fetchScheduledEmails();
    fetchMembersInPublishedRota();
    fetchRotaConflicts();
  }, [isAuthenticated]);

  useEffect(() => {
    const editMemberId = searchParams.get('editMember');
    const tabParam = searchParams.get('tab');
    if (editMemberId && members.length > 0 && !selectedMember) {
      if (tabParam) {
        const tabIdx = visibleAdminTabs.findIndex(t => t.id === tabParam);
        if (tabIdx >= 0) setActiveTab(tabIdx);
      }
      const member = members.find(m => String(m.id) === editMemberId);
      if (member) {
        handleCardClick(member);
      } else {
        setSnackbar({ open: true, message: 'Member not found — they may have been deleted or the link is invalid.', severity: 'warning' });
      }
      setSearchParams({}, { replace: true });
    }
  }, [members, searchParams]);

  useEffect(() => {
    const fetchAdminInfo = async () => {
      const token = sessionStorage.getItem('adminToken');
      if (!token || !isAuthenticated) return;
      
      try {
        const response = await fetch('/api/admin/info', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAdminRole(data.role || 'full');
        }
      } catch (err) {
        console.error('Failed to fetch admin info:', err);
      }
    };
    fetchAdminInfo();
  }, [isAuthenticated]);

  const dutyTypes = ["All", "Administrator", "1st Safety Officer", "2nd Safety Officer"];

  const dutyDayFields = {
    "Monday": "monday_duty",
    "Tuesday": "tuesday_duty",
    "Wednesday": "wednesday_duty",
    "Friday": "friday_duty",
    "Saturday": "saturday_duty",
    "Sunday": "sunday_duty",
    "School Hol. Thursday": "school_hol_thursday_duty",
    "School Hol. Tuesday": "school_hol_tuesday_duty",
  };

  const activeAdvancedFilterCount = Object.values(advancedFilters).filter(v => v !== "All").length;

  const handleAdvancedFilterChange = (filterName, value) => {
    setAdvancedFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const handleClearAdvancedFilters = () => {
    setAdvancedFilters({
      dutyDay: "All",
      hasPhone: "All",
      hasFamily: "All",
      phoneInRota: "All",
      hadPriorTraining: "All",
      paymentStatus: "All",
      hasTrainingSession: "All",
      addedBy: "All",
      currentSeason: "All",
      rotaInPublished: "All",
      emailBounced: "All",
    });
  };

  const handleSearchFieldChange = (val) => {
    setSearchField(val);
    setSearchTerm("");
    setMemberNameFilter([]);
    // Duty-date search needs the rota; load the season's assignments on demand.
    if (val === 'duty_date' && rotaAllAssignments.length === 0 && settings.season_start && settings.season_end) {
      fetchRotaRange(settings.season_start, settings.season_end);
    }
  };

  const memberNameFilterIds = new Set(memberNameFilter.map(m => m.id));
  const searchLower = searchTerm.trim().toLowerCase();
  // For duty-date search: the set of member IDs on duty on the chosen date.
  const dutyDateMemberIds = (searchField === 'duty_date' && searchTerm)
    ? new Set(rotaAllAssignments.filter(a => a.session_date === searchTerm && a.member_id).map(a => a.member_id))
    : null;

  const filteredMembers = members
    .filter((member) => {
      // Unified search-by-field.
      if (searchField === 'name') {
        if (memberNameFilterIds.size > 0 && !memberNameFilterIds.has(member.id)) return false;
      } else if (searchField === 'email') {
        if (searchLower && !(member.email || '').toLowerCase().includes(searchLower)) return false;
      } else if (searchField === 'phone') {
        if (searchLower && !(member.phone_number || '').toLowerCase().includes(searchLower)) return false;
      } else if (searchField === 'duty_date') {
        if (searchTerm && (!dutyDateMemberIds || !dutyDateMemberIds.has(member.id))) return false;
      }
      const matchesSearch = true;
      const matchesDutyType = dutyTypeFilter === "All" || member.duty_type === dutyTypeFilter;
      const matchesYear = yearFilter === "All" || (memberYearsMap[member.id] && memberYearsMap[member.id].some(y => y.year === parseInt(yearFilter)));

      if (advancedFilters.dutyDay !== "All") {
        const dayField = dutyDayFields[advancedFilters.dutyDay];
        if (dayField && !member[dayField]) return false;
      }
      if (advancedFilters.hasPhone !== "All") {
        const has = !!(member.phone_number && member.phone_number.trim());
        if (advancedFilters.hasPhone === "Yes" && !has) return false;
        if (advancedFilters.hasPhone === "No" && has) return false;
      }
      if (advancedFilters.hasFamily !== "All") {
        const has = !!(member.family_members && member.family_members.trim());
        if (advancedFilters.hasFamily === "Yes" && !has) return false;
        if (advancedFilters.hasFamily === "No" && has) return false;
      }
      if (advancedFilters.phoneInRota !== "All") {
        const val = member.incl_phone_in_rota;
        if (advancedFilters.phoneInRota === "Yes" && val !== "Yes") return false;
        if (advancedFilters.phoneInRota === "No" && val === "Yes") return false;
      }
      if (advancedFilters.hadPriorTraining !== "All") {
        const val = (member.had_prior_training || "").toLowerCase();
        if (advancedFilters.hadPriorTraining === "Yes" && val !== "yes") return false;
        if (advancedFilters.hadPriorTraining === "No" && val !== "no") return false;
      }
      if (advancedFilters.paymentStatus !== "All") {
        const paid = !!(member.payment_success);
        if (advancedFilters.paymentStatus === "Paid" && !paid) return false;
        if (advancedFilters.paymentStatus === "Unpaid" && paid) return false;
      }
      if (advancedFilters.emailBounced !== "All") {
        const bounced = !!member.email_bounced;
        if (advancedFilters.emailBounced === "Yes" && !bounced) return false;
        if (advancedFilters.emailBounced === "No" && bounced) return false;
      }
      if (advancedFilters.hasTrainingSession !== "All") {
        const hasTraining = !!(
          member.first_pool_session || member.first_training_weekend || 
          member.second_theory_session || member.second_pool_session
        );
        if (advancedFilters.hasTrainingSession === "Yes" && !hasTraining) return false;
        if (advancedFilters.hasTrainingSession === "No" && hasTraining) return false;
      }
      if (advancedFilters.addedBy !== "All") {
        const addedBy = (member.added_by || "").toLowerCase();
        if (advancedFilters.addedBy === "Admin" && !addedBy.includes("admin")) return false;
        if (advancedFilters.addedBy === "Self-registered" && addedBy.includes("admin")) return false;
      }
      if (advancedFilters.currentSeason !== "All") {
        const hasCurrentYear = memberYearsMap[member.id] && memberYearsMap[member.id].some(y => y.year === currentYear);
        if (advancedFilters.currentSeason === "Yes" && !hasCurrentYear) return false;
        if (advancedFilters.currentSeason === "No" && hasCurrentYear) return false;
      }
      if (advancedFilters.rotaInPublished !== "All" && membersInPublishedRota !== null) {
        const inRota = membersInPublishedRota.has(member.id);
        const hasUpcoming = membersWithUpcomingDuties !== null && membersWithUpcomingDuties.has(member.id);
        if (advancedFilters.rotaInPublished === "Yes" && !inRota) return false;
        if (advancedFilters.rotaInPublished === "No" && inRota) return false;
        if (advancedFilters.rotaInPublished === "Upcoming" && !hasUpcoming) return false;
        if (advancedFilters.rotaInPublished === "NoUpcoming" && (!inRota || hasUpcoming)) return false;
      }

      return matchesSearch && matchesDutyType && matchesYear;
    })
    .sort((a, b) => {
      if (sortBy === "date") {
        const getJoinDate = (m) => {
          const originalJoinDate = m.created_time ? new Date(m.created_time) : null;
          const recordCreatedDate = m.created_at ? new Date(m.created_at) : null;
          const mYears = memberYearsMap[m.id] || [];
          const currentYear = new Date().getFullYear();
          const currentYearEntry = mYears.find(y => y.year === currentYear);
          const hasMultipleYears = mYears.length > 1;
          const isReturning = hasMultipleYears && currentYearEntry;
          return isReturning && currentYearEntry.created_at
            ? new Date(currentYearEntry.created_at)
            : (originalJoinDate || recordCreatedDate || new Date(0));
        };
        return getJoinDate(b) - getJoinDate(a);
      }
      const surnameA = (a.name || "").toLowerCase();
      const surnameB = (b.name || "").toLowerCase();
      if (surnameA < surnameB) return -1;
      if (surnameA > surnameB) return 1;
      const firstNameA = (a.first_name || "").toLowerCase();
      const firstNameB = (b.first_name || "").toLowerCase();
      return firstNameA.localeCompare(firstNameB);
    });

  const handleCardClick = async (member) => {
    // Reset all dialog state first to avoid stale data
    setDialogTab(0);
    setEmailHistory([]);
    setActivityLog([]);
    setMembershipYears([]);
    setHolidayPeriods([]);
    setFamilyMembers([]);
    setEditingFamilyMember(null);
    setExpandedEmailId(null);
    
    // Now set the new member data
    setSelectedMember(member);
    setEditForm({ ...member });
    const unavailDates = member.unavailable_dates;
    let parsedDates = [];
    if (Array.isArray(unavailDates)) {
      parsedDates = unavailDates;
    } else if (typeof unavailDates === 'string' && unavailDates.trim()) {
      parsedDates = parseHolidayPeriods(unavailDates);
    }
    console.log('Member unavailable_dates:', unavailDates, 'Parsed:', parsedDates);
    setHolidayPeriods(parsedDates);
    setFamilyMembers(parseFamilyMembers(member.family_members));

    // Fetch this member's rota duties (upcoming + past).
    setMemberDuties(null);
    setMemberDutiesLoading(true);
    fetch(`/api/admin/member/${member.id}/duties`, {
      headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` }
    })
      .then(r => r.ok ? r.json() : { duties: [] })
      .then(d => setMemberDuties(d.duties || []))
      .catch(() => setMemberDuties([]))
      .finally(() => setMemberDutiesLoading(false));

    // Fetch membership years and email history in parallel (lazy loading)
    const fetchMembershipYears = async () => {
      try {
        const { years } = await apiFetch(`/api/member/${member.id}/years`);
        setMembershipYears(years || []);
      } catch (err) {
        console.error("Error fetching membership years:", err);
        setMembershipYears([]);
      }
    };

    // Start fetching email history and activity log in background
    fetchMembershipYears();
    fetchEmailHistory(member.email);
    fetchActivityLog(member.id);
  };

  const handleCloseDialog = () => {
    Object.keys(fieldDebounceRef.current).forEach(field => {
      if (fieldDebounceRef.current[field]) {
        clearTimeout(fieldDebounceRef.current[field]);
        delete fieldDebounceRef.current[field];
        const currentValue = editForm[field];
        if (currentValue !== undefined) {
          saveFieldToDb(field, currentValue);
        }
      }
    });
    if (memberEdited) {
      fetchMembers();
      setMemberEdited(false);
    }
    setSelectedMember(null);
    setEditForm({});
    setHolidayPeriods([]);
    setFamilyMembers([]);
    setEditingFamilyMember(null);
    setMembershipYears([]);
    setNewYear("");
    setDialogTab(0);
    setEmailHistory([]);
    setExpandedEmailId(null);
    setActivityLog([]);
  };

  const fetchEmailHistory = async (email) => {
    if (!email) return;
    setEmailHistoryLoading(true);
    try {
      const response = await fetch(`/api/emails/sent/${encodeURIComponent(email)}`);
      if (!response.ok) throw new Error('Failed to fetch emails');
      const data = await response.json();
      setEmailHistory(data.emails || []);
    } catch (err) {
      console.error('Error fetching email history:', err);
      setEmailHistory([]);
    } finally {
      setEmailHistoryLoading(false);
    }
  };

  const formatActivityDetails = (activityType, details) => {
    if (!details) return '';
    
    switch (activityType) {
      case 'admin_update':
        const fieldName = details.field?.replace(/_/g, ' ') || 'field';
        if (details.from !== undefined && details.to !== undefined) {
          return `${fieldName}: "${details.from}" → "${details.to}"`;
        }
        const newValue = details.new_value;
        if (newValue === null || newValue === '') {
          return `Cleared ${fieldName}`;
        }
        if (typeof newValue === 'boolean') {
          return `Set ${fieldName} to ${newValue ? 'Yes' : 'No'}`;
        }
        if (Array.isArray(newValue)) {
          return `Updated ${fieldName} to: ${newValue.join(', ') || 'none'}`;
        }
        return `Changed ${fieldName} to: ${newValue}`;
      
      case 'payment_attempt':
        const parts = [];
        if (details.total) parts.push(`Amount: £${details.total}`);
        if (details.membership_type) parts.push(`Type: ${details.membership_type}`);
        if (details.is_safety_officer) parts.push('(50% Safety Officer discount applied)');
        return parts.join(' • ');
      
      case 'payment_success':
        return details.amount ? `Payment of £${details.amount} completed` : 'Payment completed successfully';
      
      case 'payment_failed':
        return details.reason || 'Payment was unsuccessful';
      
      case 'details_update':
        if (details.changes && typeof details.changes === 'object' && Object.keys(details.changes).length > 0) {
          return Object.entries(details.changes)
            .map(([field, change]) => `${field}: "${change.from}" → "${change.to}"`)
            .join('\n');
        }
        if (details.updated_fields && Array.isArray(details.updated_fields)) {
          const fields = details.updated_fields.map(f => f.replace(/_/g, ' ')).join(', ');
          return `Updated fields: ${fields}`;
        }
        return 'Member details were updated';
      
      case 'registration':
        return details.membership_type ? `Registered as ${details.membership_type} member` : 'New member registered';
      
      case 'renewal':
        return details.year ? `Renewed membership for ${details.year}` : 'Membership renewed';
      
      default:
        if (typeof details === 'string') return details;
        const summary = Object.entries(details)
          .map(([key, val]) => `${key.replace(/_/g, ' ')}: ${val}`)
          .join(', ');
        return summary;
    }
  };

  const fetchActivityLog = async (memberId) => {
    if (!memberId) return;
    setActivityLogLoading(true);
    try {
      const response = await fetch(`/api/member/${memberId}/activity-log`, {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` }
      });
      if (!response.ok) throw new Error('Failed to fetch activity log');
      const data = await response.json();
      setActivityLog(data || []);
    } catch (err) {
      console.error('Error fetching activity log:', err);
      setActivityLog([]);
    } finally {
      setActivityLogLoading(false);
    }
  };

  const fetchChatbotQuestions = async (flagged = false) => {
    setChatbotQuestionsLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (flagged) params.append('flagged', 'true');
      const response = await fetch(`/api/chatbot/questions?${params}`, {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` }
      });
      if (!response.ok) throw new Error('Failed to fetch questions');
      const data = await response.json();
      setChatbotQuestions(data.questions || []);
    } catch (err) {
      console.error('Error fetching chatbot questions:', err);
      setSnackbar({ open: true, message: 'Failed to load question log', severity: 'error' });
    } finally {
      setChatbotQuestionsLoading(false);
    }
  };

  const handleClearQuestions = async () => {
    try {
      const response = await fetch('/api/chatbot/questions', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` }
      });
      if (!response.ok) throw new Error('Failed to clear questions');
      setChatbotQuestions([]);
      setClearQuestionsConfirmOpen(false);
      setSnackbar({ open: true, message: 'Question log cleared', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to clear question log', severity: 'error' });
    }
  };

  const fetchChatbotPrompts = async () => {
    setChatbotLoading(true);
    try {
      const response = await fetch('/api/chatbot/prompts');
      if (!response.ok) throw new Error('Failed to fetch prompts');
      const data = await response.json();
      setChatbotPrompts(data.prompts || []);
    } catch (err) {
      console.error('Error fetching chatbot prompts:', err);
      setSnackbar({ open: true, message: 'Failed to load chatbot prompts', severity: 'error' });
    } finally {
      setChatbotLoading(false);
    }
  };

  const handleEditPrompt = (prompt) => {
    setEditingPrompt(prompt);
    setPromptForm({
      system_prompt: prompt.system_prompt || '',
      greeting_message: prompt.greeting_message || ''
    });
  };

  const handleSavePrompt = async () => {
    if (!editingPrompt) return;
    const adminToken = sessionStorage.getItem('adminToken');
    try {
      const response = await fetch(`/api/chatbot/prompts/${editingPrompt.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(promptForm)
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to save prompt');
      }
      const data = await response.json();
      setChatbotPrompts(prev => prev.map(p => p.id === editingPrompt.id ? data.prompt : p));
      setEditingPrompt(null);
      setSnackbar({ open: true, message: 'Prompt saved successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to save prompt', severity: 'error' });
    }
  };

  const handleTogglePromptActive = async (prompt) => {
    const adminToken = sessionStorage.getItem('adminToken');
    try {
      const response = await fetch(`/api/chatbot/prompts/${prompt.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({ is_active: !prompt.is_active })
      });
      if (!response.ok) throw new Error('Failed to update prompt');
      const data = await response.json();
      setChatbotPrompts(prev => prev.map(p => p.id === prompt.id ? data.prompt : p));
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update prompt', severity: 'error' });
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    if (activeTabId === 'chatbot' && chatbotPrompts.length === 0) {
      fetchChatbotPrompts();
    }
    if (activeTabId === 'chatbot') {
      fetchChatbotQuestions(showFlaggedOnly);
    }
    if (activeTabId === 'help' && helpTriggers.length === 0) {
      fetchHelpTriggers();
    }
    if (activeTabId === 'activity') {
      fetchGlobalActivityLog();
    }
    if (activeTabId === 'rota') {
      if (settings.season_start && settings.season_end) {
        fetchRotaRange(settings.season_start, settings.season_end);
      }
      fetchRotaDutyCounts();
      if (rotaMembers.length === 0) fetchRotaMembers();
    }
    if (activeTabId === 'users') {
      fetchAdminUsers();
    }
  }, [activeTab, activeTabId, isAuthenticated]);

  const fetchAdminUsers = async () => {
    setAdminUsersLoading(true);
    try {
      const response = await fetch('/api/admin/users', {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminUsers(data);
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
    }
    setAdminUsersLoading(false);
  };

  const fetchHelpTriggers = async () => {
    setHelpTriggersLoading(true);
    try {
      const response = await fetch('/api/admin/help-triggers', {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}`
        }
      });
      const data = await response.json();
      setHelpTriggers(data.triggers || []);
    } catch (error) {
      console.error('Error fetching help triggers:', error);
    }
    setHelpTriggersLoading(false);
  };

  const fetchGlobalActivityLog = async () => {
    setGlobalActivityLogLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (activityDateFrom) params.append('dateFrom', activityDateFrom);
      if (activityDateTo) params.append('dateTo', activityDateTo);
      const response = await fetch(`/api/admin/activity-log?${params}`, {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` }
      });
      if (!response.ok) throw new Error('Failed to fetch activity log');
      const data = await response.json();
      setGlobalActivityLog(data || []);
    } catch (err) {
      console.error('Error fetching global activity log:', err);
      setGlobalActivityLog([]);
    } finally {
      setGlobalActivityLogLoading(false);
    }
  };

  const getActivityTypeIcon = (type) => {
    switch (type) {
      case 'registration': return <HowToRegIcon fontSize="small" sx={{ color: '#4caf50' }} />;
      case 'renewal': return <AutorenewIcon fontSize="small" sx={{ color: '#2196f3' }} />;
      case 'payment_success': return <PaymentIcon fontSize="small" sx={{ color: '#4caf50' }} />;
      case 'payment_failed': return <ErrorOutlineIcon fontSize="small" sx={{ color: '#f44336' }} />;
      case 'update': return <UpdateIcon fontSize="small" sx={{ color: '#ff9800' }} />;
      case 'login': return <LoginIcon fontSize="small" sx={{ color: '#9c27b0' }} />;
      case 'email_sent': return <EmailIcon fontSize="small" sx={{ color: '#00bcd4' }} />;
      case 'admin_action': return <BadgeIcon fontSize="small" sx={{ color: '#795548' }} />;
      default: return <EventNoteIcon fontSize="small" sx={{ color: '#607d8b' }} />;
    }
  };

  const getActivityTypeColor = (type) => {
    switch (type) {
      case 'registration': return '#e8f5e9';
      case 'renewal': return '#e3f2fd';
      case 'payment_success': return '#e8f5e9';
      case 'payment_failed': return '#ffebee';
      case 'update': return '#fff3e0';
      case 'login': return '#f3e5f5';
      case 'email_sent': return '#e0f7fa';
      case 'admin_action': return '#efebe9';
      default: return '#eceff1';
    }
  };

  const activityTypeLabels = {
    all: 'All Events',
    registration: 'Registration',
    renewal: 'Renewal',
    payment_success: 'Payment Success',
    payment_failed: 'Payment Failed',
    update: 'Detail Updates',
    login: 'Logins',
    email_sent: 'Emails Sent',
    admin_action: 'Admin Actions',
  };

  const filteredGlobalActivityLog = activityTypeFilter === 'all'
    ? globalActivityLog
    : globalActivityLog.filter(e => e.activity_type === activityTypeFilter);

  const groupedByDay = filteredGlobalActivityLog.reduce((acc, entry) => {
    const day = new Date(entry.created_at).toLocaleDateString('en-GB', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
    if (!acc[day]) acc[day] = [];
    acc[day].push(entry);
    return acc;
  }, {});

  const handleUpdateTrigger = async (triggerId, updates) => {
    try {
      const response = await fetch(`/api/admin/help-triggers/${triggerId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        const updated = await response.json();
        setHelpTriggers(prev => prev.map(t => t.id === triggerId ? updated : t));
        setEditingTrigger(null);
      }
    } catch (error) {
      console.error('Error updating trigger:', error);
    }
  };

  const handleCreateTrigger = async () => {
    if (!newTriggerForm.trigger_key || !newTriggerForm.label || !newTriggerForm.question) return;
    try {
      const response = await fetch('/api/admin/help-triggers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(newTriggerForm)
      });
      if (response.ok) {
        const created = await response.json();
        setHelpTriggers(prev => [...prev, created]);
        setShowNewTriggerForm(false);
        setNewTriggerForm({ trigger_key: '', label: '', question: '', page: 'registration' });
      }
    } catch (error) {
      console.error('Error creating trigger:', error);
    }
  };

  const handleDeleteTrigger = async (triggerId) => {
    if (!window.confirm('Are you sure you want to delete this help trigger?')) return;
    try {
      const response = await fetch(`/api/admin/help-triggers/${triggerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}`
        }
      });
      if (response.ok) {
        setHelpTriggers(prev => prev.filter(t => t.id !== triggerId));
      }
    } catch (error) {
      console.error('Error deleting trigger:', error);
    }
  };

  const handleAddYear = async (year) => {
    if (!year || !selectedMember?.id) return;
    if (membershipYears.includes(year)) return;

    try {
      await apiFetch(`/api/admin/member/${selectedMember.id}/years`, {
        method: 'POST',
        body: { year },
      });
      setMembershipYears([year, ...membershipYears].sort((a, b) => b - a));
      setSnackbar({ open: true, message: `Added ${year}`, severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: `Error adding year: ${err.message}`, severity: "error" });
    }
  };

  const handleRemoveYear = async (year) => {
    if (!selectedMember?.id) return;

    try {
      await apiFetch(`/api/admin/member/${selectedMember.id}/years/${year}`, {
        method: 'DELETE',
      });
      setMembershipYears(membershipYears.filter(y => y !== year));
      setSnackbar({ open: true, message: `Removed ${year}`, severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: `Error removing year: ${err.message}`, severity: "error" });
    }
  };

  const handleMenuOpen = (event, member) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuMember(member);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuMember(null);
  };

  const handleDeleteClick = () => {
    setMemberToDelete(menuMember);
    setDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!memberToDelete?.id) return;
    try {
      const response = await fetch(`/api/admin/member/${memberToDelete.id}/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete member');
      
      setMembers(members.filter(m => m.id !== memberToDelete.id));
      setSelectedMember(null);
      setSnackbar({ open: true, message: "Member deleted successfully", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: `Error deleting member: ${err.message}`, severity: "error" });
    } finally {
      setDeleteConfirmOpen(false);
      setMemberToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setMemberToDelete(null);
  };

  const handleSendRenewalNotification = async (memberIds) => {
    handleMenuClose();
    
    try {
      setSnackbar({ 
        open: true, 
        message: `Starting to send ${memberIds.length} renewal invitation(s)...`, 
        severity: "info" 
      });

      const response = await fetch('/api/admin/send-renewal-invitation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({ memberIds })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send renewal invitations');
      }

      // If we got a jobId, poll for status
      if (result.jobId) {
        let skipMessage = result.skipped > 0 ? ` (${result.skipped} skipped - no email)` : '';
        setSnackbar({ 
          open: true, 
          message: `Sending ${result.total} email(s) in background...${skipMessage}`, 
          severity: "info" 
        });
        setActiveEmailJob({ jobId: result.jobId, type: 'Renewal Invitations', total: result.total });

        // Poll for job completion
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await fetch(`/api/admin/email-job/${result.jobId}`, {
              headers: {
                'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}`
              }
            });
            
            if (!statusResponse.ok) {
              clearInterval(pollInterval);
              setActiveEmailJob(null);
              return;
            }
            
            const job = await statusResponse.json();
            
            if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
              clearInterval(pollInterval);
              setActiveEmailJob(null);
              
              if (job.status === 'cancelled') return;
              let message = `Sent ${job.sent} renewal invitation(s)`;
              if (job.failed > 0) {
                message += `, ${job.failed} failed`;
              }
              if (job.skipped > 0) {
                message += `, ${job.skipped} skipped (no email)`;
              }

              setSnackbar({ 
                open: true, 
                message, 
                severity: job.failed > 0 ? "warning" : "success" 
              });
            }
          } catch (pollError) {
            console.error('Error polling job status:', pollError);
            clearInterval(pollInterval);
            setActiveEmailJob(null);
          }
        }, 3000); // Poll every 3 seconds
      }
    } catch (error) {
      console.error('Error sending renewal invitations:', error);
      setSnackbar({ 
        open: true, 
        message: error.message || 'Failed to send renewal invitations', 
        severity: "error" 
      });
    }
  };

  const handleSendRotaDuties = async (memberIds) => {
    handleMenuClose();

    try {
      setSnackbar({
        open: true,
        message: `Starting to send ${memberIds.length} rota duty schedule(s)...`,
        severity: "info"
      });

      const response = await fetch('/api/admin/send-rota-duties', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({ memberIds })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send rota duty emails');
      }

      if (result.jobId) {
        let skipMessage = result.skipped > 0 ? ` (${result.skipped} skipped - no email)` : '';
        setSnackbar({
          open: true,
          message: `Sending ${result.total} email(s) in background...${skipMessage}`,
          severity: "info"
        });
        setActiveEmailJob({ jobId: result.jobId, type: 'Rota Duties', total: result.total });

        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await fetch(`/api/admin/email-job/${result.jobId}`, {
              headers: {
                'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}`
              }
            });

            if (!statusResponse.ok) {
              clearInterval(pollInterval);
              setActiveEmailJob(null);
              return;
            }

            const job = await statusResponse.json();

            if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
              clearInterval(pollInterval);
              setActiveEmailJob(null);

              if (job.status === 'cancelled') return;
              let message = `Sent ${job.sent} rota duty email(s)`;
              if (job.failed > 0) {
                message += `, ${job.failed} failed`;
              }
              if (job.skipped > 0) {
                message += `, ${job.skipped} skipped (no duties or no email)`;
              }

              setSnackbar({
                open: true,
                message,
                severity: job.failed > 0 ? "warning" : "success"
              });
            }
          } catch (pollError) {
            console.error('Error polling job status:', pollError);
            clearInterval(pollInterval);
            setActiveEmailJob(null);
          }
        }, 3000);
      }
    } catch (error) {
      console.error('Error sending rota duty emails:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to send rota duty emails',
        severity: "error"
      });
    }
  };

  const handleSendSessionReminders = async (sessionDate, sessionTime) => {
    const key = `${sessionDate}|${sessionTime}`;
    setRotaReminderSending(key);
    try {
      const res = await fetch('/api/admin/rota/send-reminders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({ sessionDate, sessionTime })
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to send reminders');
      const parts = [];
      if (result.emailSent > 0) parts.push(`${result.emailSent} email${result.emailSent > 1 ? 's' : ''}`);
      if (result.smsSent > 0) parts.push(`${result.smsSent} SMS`);
      if (result.emailFailed > 0) parts.push(`${result.emailFailed} email(s) failed`);
      if (result.smsFailed > 0) parts.push(`${result.smsFailed} SMS failed`);
      const anyFailed = result.emailFailed > 0 || result.smsFailed > 0;
      setSnackbar({
        open: true,
        message: parts.length > 0 ? `Reminders sent: ${parts.join(', ')}` : 'No reminders sent (no contact details)',
        severity: anyFailed ? 'warning' : 'success'
      });
    } catch (err) {
      console.error('Send session reminders error:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to send reminders', severity: 'error' });
    } finally {
      setRotaReminderSending(null);
    }
  };

  const getCurrentFilterCriteria = () => ({
    searchTerm,
    dutyTypeFilter,
    yearFilter,
    advancedFilters,
  });

  const fetchScheduledEmails = async () => {
    try {
      const response = await fetch('/api/admin/scheduled-emails', {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setScheduledEmails(data.scheduledEmails || []);
      }
    } catch (err) {
      console.error('Error fetching scheduled emails:', err);
    }
  };

  const insertPlaceholder = useCallback((placeholder) => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      if (range) {
        editor.insertText(range.index, placeholder);
        editor.setSelection(range.index + placeholder.length);
      } else {
        const length = editor.getLength();
        editor.insertText(length - 1, placeholder);
        editor.setSelection(length - 1 + placeholder.length);
      }
    }
  }, []);

  const handleConfirmPrepare = async () => {
    const bodyText = composeForm.body.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    if (!composeForm.subject.trim() || !bodyText) {
      setSnackbar({ open: true, message: 'Subject and body are required', severity: 'error' });
      return;
    }

    const isScheduled = !singleEmailMember && !!(composeForm.scheduleDate && composeForm.scheduleTime);

    if (singleEmailMember) {
      setComposeConfirmData({
        type: 'single',
        subject: composeForm.subject,
        count: 1,
        sampleNames: [`${singleEmailMember.first_name || ''} ${singleEmailMember.name || ''}`.trim()],
        email: singleEmailMember.email,
        scheduledAt: null,
      });
      setComposeConfirmOpen(true);
      return;
    }

    if (!isScheduled) {
      // Immediate bulk — count from filtered list minus manually excluded members
      const recipients = filteredMembers.filter(m => m.email && m.email.trim() && !excludedMemberIds.has(m.id));
      setComposeConfirmData({
        type: 'immediate',
        subject: composeForm.subject,
        count: recipients.length,
        sampleNames: recipients.slice(0, 5).map(m => `${m.first_name || ''} ${m.name || ''}`.trim()).filter(Boolean),
        scheduledAt: null,
        excludedCount: excludedMemberIds.size,
      });
      setComposeConfirmOpen(true);
      return;
    }

    // Scheduled — ask the server for today's count (filter re-evaluated server-side)
    setComposeConfirmLoading(true);
    try {
      const filterCriteria = getCurrentFilterCriteria();
      const res = await fetch('/api/admin/preview-email-recipients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` },
        body: JSON.stringify({ filterCriteria }),
      });
      const data = await res.json();
      const scheduledAt = new Date(`${composeForm.scheduleDate}T${composeForm.scheduleTime}`);
      setComposeConfirmData({
        type: 'scheduled',
        subject: composeForm.subject,
        count: data.count || 0,
        sampleNames: data.sampleNames || [],
        scheduledAt,
      });
      setComposeConfirmOpen(true);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to preview recipients. Please try again.', severity: 'error' });
    } finally {
      setComposeConfirmLoading(false);
    }
  };

  const handleSendCustomEmail = async () => {
    const bodyText = composeForm.body.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    if (!composeForm.subject.trim() || !bodyText) {
      setSnackbar({ open: true, message: 'Subject and body are required', severity: 'error' });
      return;
    }

    setComposeSending(true);
    try {
      const filterCriteria = singleEmailMember ? null : getCurrentFilterCriteria();

      let scheduledAt = null;
      if (!singleEmailMember && composeForm.scheduleDate && composeForm.scheduleTime) {
        scheduledAt = new Date(`${composeForm.scheduleDate}T${composeForm.scheduleTime}`).toISOString();
      }

      const sanitizedBody = DOMPurify.sanitize(composeForm.body, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'a', 'span'],
        ALLOWED_ATTR: ['href', 'target', 'style', 'class'],
        ALLOW_DATA_ATTR: false
      });

      const htmlBody = sanitizedBody;

      const response = await fetch('/api/admin/send-custom-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({
          subject: composeForm.subject,
          htmlBody,
          filterCriteria,
          scheduledAt,
          // For immediate sends: always pass memberIds directly from the frontend-filtered list
          // so the server uses exactly what the admin sees (including all advanced filters).
          // For scheduled sends: omit memberIds so the server re-evaluates filterCriteria at send time.
          memberIds: singleEmailMember
            ? [singleEmailMember.id]
            : scheduledAt
            ? undefined
            : filteredMembers.filter(m => m.email && m.email.trim() && !excludedMemberIds.has(m.id)).map(m => m.id)
        })
      });

      let result;
      try {
        result = await response.json();
      } catch (parseErr) {
        throw new Error(`Server error (${response.status}). Please republish the app to enable this feature.`);
      }

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send email');
      }

      if (result.scheduled) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        fetchScheduledEmails();
      } else if (result.jobId) {
        setSnackbar({ open: true, message: result.message, severity: 'info' });
        setActiveEmailJob({ jobId: result.jobId, type: 'Custom Email', total: result.total });

        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await fetch(`/api/admin/email-job/${result.jobId}`, {
              headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` }
            });
            if (!statusResponse.ok) { clearInterval(pollInterval); setActiveEmailJob(null); return; }
            const job = await statusResponse.json();
            if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
              clearInterval(pollInterval);
              setActiveEmailJob(null);
              if (job.status === 'cancelled') return;
              let message = `Sent ${job.sent} email(s)`;
              if (job.failed > 0) message += `, ${job.failed} failed`;
              setSnackbar({ open: true, message, severity: job.failed > 0 ? 'warning' : 'success' });
            }
          } catch (pollError) {
            console.error('Error polling job status:', pollError);
            clearInterval(pollInterval);
            setActiveEmailJob(null);
          }
        }, 3000);
      }

      setComposeOpen(false);
      setSingleEmailMember(null);
      setComposeForm({ subject: '', body: '', scheduleDate: '', scheduleTime: '' });
      setExcludedMemberIds(new Set());
    } catch (error) {
      console.error('Error sending custom email:', error);
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setComposeSending(false);
    }
  };

  const handleCancelEmailJob = async () => {
    if (!activeEmailJob) return;
    try {
      const response = await fetch(`/api/admin/email-job/${activeEmailJob.jobId}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` }
      });
      const result = await response.json();
      if (response.ok) {
        setSnackbar({ open: true, message: `Email job cancelled — ${result.sent} email(s) already sent`, severity: 'warning' });
        setActiveEmailJob(null);
      } else {
        setSnackbar({ open: true, message: result.error || 'Failed to cancel job', severity: 'error' });
      }
    } catch (err) {
      console.error('Error cancelling email job:', err);
      setSnackbar({ open: true, message: 'Failed to cancel job', severity: 'error' });
    }
  };

  const handleCancelScheduledEmail = async (id) => {
    try {
      const response = await fetch(`/api/admin/scheduled-emails/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` }
      });
      if (response.ok) {
        setSnackbar({ open: true, message: 'Scheduled email cancelled', severity: 'success' });
        fetchScheduledEmails();
      }
    } catch (err) {
      console.error('Error cancelling scheduled email:', err);
      setSnackbar({ open: true, message: 'Failed to cancel scheduled email', severity: 'error' });
    }
  };

  const openSingleMemberEmail = (member) => {
    handleMenuClose();
    setSingleEmailMember(member);
    setComposeForm({ subject: '', body: '', scheduleDate: '', scheduleTime: '' });
    setRecipientsExpanded(false);
    setComposeOpen(true);
  };

  const handlePrintMembershipCard = async (memberIds) => {
    handleMenuClose();
    if (!memberIds || memberIds.length === 0) return;
    const memberId = memberIds[0];
    try {
      setSnackbar({ open: true, message: "Generating membership card...", severity: "info" });
      const token = sessionStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/member/${memberId}/membership-card`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setSnackbar({ open: true, message: err.error || "Failed to generate card", severity: "error" });
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const memberName = menuMember ? `${menuMember.first_name || ''}_${menuMember.name || ''}`.trim().replace(/\s+/g, '_') : memberId;
      a.download = `CCSC_Card_${memberName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: "Membership card downloaded", severity: "success" });
    } catch (err) {
      console.error("Membership card error:", err);
      setSnackbar({ open: true, message: "Failed to generate membership card", severity: "error" });
    }
  };

  const handleDownloadDirectory = async () => {
    const ids = filteredMembers.map(m => m.id).filter(Boolean);
    if (ids.length === 0) return;
    try {
      setSnackbar({ open: true, message: `Generating member directory…`, severity: "info" });
      const token = sessionStorage.getItem('adminToken');
      const res = await fetch('/api/admin/members/directory-pdf', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ memberIds: ids })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setSnackbar({ open: true, message: err.error || "Failed to generate directory", severity: "error" });
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CCSC_Member_Directory.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: "Member directory downloaded", severity: "success" });
    } catch (err) {
      console.error("Member directory error:", err);
      setSnackbar({ open: true, message: "Failed to generate member directory", severity: "error" });
    }
  };

  const handlePrintAllCards = async () => {
    const ids = filteredMembers.map(m => m.id).filter(Boolean);
    if (ids.length === 0) return;
    try {
      setSnackbar({ open: true, message: `Generating ${ids.length} membership card${ids.length > 1 ? 's' : ''}…`, severity: "info" });
      const token = sessionStorage.getItem('adminToken');
      const res = await fetch('/api/admin/members/bulk-membership-cards', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ memberIds: ids })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setSnackbar({ open: true, message: err.error || "Failed to generate cards", severity: "error" });
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CCSC_Cards_Bulk.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: `${ids.length} membership card${ids.length > 1 ? 's' : ''} downloaded`, severity: "success" });
    } catch (err) {
      console.error("Bulk membership card error:", err);
      setSnackbar({ open: true, message: "Failed to generate membership cards", severity: "error" });
    }
  };

  const saveFieldToDb = async (field, value) => {
    if (!selectedMember?.id) return;
    try {
      const response = await fetch(`/api/admin/member/${selectedMember.id}/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ field, value, adminEmail })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save');
      }
      setMemberEdited(true);
    } catch (err) {
      setSnackbar({ open: true, message: `Error saving: ${err.message}`, severity: "error" });
    }
  };

  const handleHolidayPeriodsChange = (newDates) => {
    setHolidayPeriods(newDates);
    setEditForm(prev => ({ ...prev, unavailable_dates: newDates }));
    saveFieldToDb("unavailable_dates", newDates);
  };

  const handleAddFamilyMember = () => {
    if (familyMemberForm.name.trim()) {
      const original = familyMemberForm.dob 
        ? `${familyMemberForm.name.trim()} (${familyMemberForm.dob})${familyMemberForm.status ? ' - ' + familyMemberForm.status : ''}`
        : `${familyMemberForm.name.trim()}${familyMemberForm.status ? ' - ' + familyMemberForm.status : ''}`;
      const newMember = {
        id: familyMembers.length,
        name: familyMemberForm.name.trim(),
        dob: familyMemberForm.dob || null,
        age: calculateAge(familyMemberForm.dob),
        isMinor: familyMemberForm.dob ? calculateAge(familyMemberForm.dob) < 18 : false,
        status: familyMemberForm.status || null,
        original
      };
      const updated = [...familyMembers, newMember];
      setFamilyMembers(updated);
      const formatted = formatFamilyMembersForSave(updated);
      setEditForm(prev => ({ ...prev, family_members: formatted }));
      saveFieldToDb("family_members", formatted);
      setFamilyMemberForm({ name: "", dob: "", status: "" });
    }
  };

  const handleRemoveFamilyMember = (idToRemove) => {
    const updated = familyMembers.filter(m => m.id !== idToRemove);
    setFamilyMembers(updated);
    const formatted = formatFamilyMembersForSave(updated);
    setEditForm(prev => ({ ...prev, family_members: formatted }));
    saveFieldToDb("family_members", formatted);
  };

  const handleEditFamilyMember = (member) => {
    setEditingFamilyMember(member.id);
    setFamilyMemberForm({ name: member.name, dob: member.dob || "", status: member.status || "" });
  };

  const handleSaveFamilyMember = () => {
    const updated = familyMembers.map(m => {
      if (m.id === editingFamilyMember) {
        const original = familyMemberForm.dob 
          ? `${familyMemberForm.name.trim()} (${familyMemberForm.dob})${familyMemberForm.status ? ' - ' + familyMemberForm.status : ''}`
          : `${familyMemberForm.name.trim()}${familyMemberForm.status ? ' - ' + familyMemberForm.status : ''}`;
        return {
          ...m,
          name: familyMemberForm.name.trim(),
          dob: familyMemberForm.dob || null,
          age: calculateAge(familyMemberForm.dob),
          isMinor: familyMemberForm.dob ? calculateAge(familyMemberForm.dob) < 18 : false,
          status: familyMemberForm.status || null,
          original
        };
      }
      return m;
    });
    setFamilyMembers(updated);
    const formatted = formatFamilyMembersForSave(updated);
    setEditForm(prev => ({ ...prev, family_members: formatted }));
    saveFieldToDb("family_members", formatted);
    setEditingFamilyMember(null);
    setFamilyMemberForm({ name: "", dob: "", status: "" });
  };

  const fieldDebounceRef = useRef({});

  const immediateFields = new Set([
    'duty_type', 'incl_phone_in_rota', 'incl_sms_reminders',
    'monday_duty', 'tuesday_duty', 'wednesday_duty',
    'friday_duty', 'saturday_duty', 'sunday_duty',
    'school_hol_tuesday_duty', 'school_hol_thursday_duty',
    'more_duties', 'had_prior_training',
    'first_pool_session', 'second_theory_session', 'second_pool_session',
  ]);

  const handleFieldChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
    if (immediateFields.has(field)) {
      saveFieldToDb(field, value);
    } else {
      if (fieldDebounceRef.current[field]) clearTimeout(fieldDebounceRef.current[field]);
      fieldDebounceRef.current[field] = setTimeout(() => {
        saveFieldToDb(field, value);
      }, 800);
    }
  };

  const handleDone = () => {
    fetchMembers();
    handleCloseDialog();
  };

  const renderField = (field) => {
    const value = editForm[field] || "";
    const label = fieldLabels[field] || field;

    if (field === "duty_type") {
      return (
        <FormControl fullWidth size="small" key={field}>
          <InputLabel>{label}</InputLabel>
          <Select
            value={value}
            label={label}
            onChange={(e) => handleFieldChange(field, e.target.value)}
          >
            {dutyTypes.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (["friday_duty", "saturday_duty", "sunday_duty", "monday_duty", "tuesday_duty", 
         "wednesday_duty", "school_hol_thursday_duty", "school_hol_tuesday_duty",
         "incl_phone_in_rota", "payment_success", "do_not_delete"].includes(field)) {
      return (
        <FormControl fullWidth size="small" key={field}>
          <InputLabel>{label}</InputLabel>
          <Select
            value={value}
            label={label}
            onChange={(e) => handleFieldChange(field, e.target.value)}
          >
            {yesNoOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (field === "alt_email") {
      const invalid = value.trim() !== "" && !isValidEmail(value.trim());
      return (
        <TextField
          key={field}
          label={label}
          value={value}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          fullWidth
          size="small"
          type="email"
          error={invalid}
          helperText={invalid ? "Enter a valid email address" : "Admin-only. If set, this address is used for the member's Microsoft duty group instead of their primary. Normal app emails still go to the primary."}
        />
      );
    }

    if (field === "family_members" || field === "holiday_periods" || field === "address") {
      return (
        <TextField
          key={field}
          label={label}
          value={value}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          fullWidth
          size="small"
          multiline
          rows={3}
        />
      );
    }

    return (
      <TextField
        key={field}
        label={label}
        value={value}
        onChange={(e) => handleFieldChange(field, e.target.value)}
        fullWidth
        size="small"
      />
    );
  };

  if (authLoading) {
    return (
      <div className="admin-page">
        <div className="admin-hero">
          <div className="admin-hero-content">
            <h1 className="admin-hero-title">
              <span>Member</span>
              <br />
              <span>Administration</span>
            </h1>
            <p className="admin-hero-subtitle">Manage CCSC Members</p>
          </div>
        </div>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-page">
        <div className="admin-hero">
          <div className="admin-hero-content">
            <h1 className="admin-hero-title">
              <span>Member</span>
              <br />
              <span>Administration</span>
            </h1>
            <p className="admin-hero-subtitle">Manage CCSC Members</p>
          </div>
        </div>
        <Box sx={{ maxWidth: 400, margin: "0 auto", padding: "32px 24px" }}>
          <StyledPaper elevation={3}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <LockIcon sx={{ fontSize: 48, color: "#0d2c54", mb: 2 }} />
              <Typography variant="h5" sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: "#0d2c54" }}>
                Admin Login
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {loginStep === "email" 
                  ? "Enter your admin email to receive a verification code"
                  : "Enter the 6-digit code sent to your email. Not received the code? Check your spam folder before contacting contact@comberton-pool.org.uk"}
              </Typography>
            </Box>

            {loginError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {loginError}
              </Alert>
            )}

            {loginStep === "email" ? (
              <>
                <TextField
                  label="Email Address"
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: "#0d2c54" }} />
                      </InputAdornment>
                    ),
                  }}
                  onKeyPress={(e) => e.key === "Enter" && handleRequestCode()}
                />
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleRequestCode}
                  disabled={loginLoading || !isValidEmail(loginEmail)}
                  sx={{
                    backgroundColor: "#dbf14e",
                    color: "#121212",
                    fontWeight: 600,
                    borderRadius: "100px",
                    py: 1.5,
                    "&:hover": { backgroundColor: "#c5d946" }
                  }}
                >
                  {loginLoading ? <CircularProgress size={24} /> : "Send Verification Code"}
                </Button>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 2, textAlign: "center" }}>
                  Code sent to: <strong>{loginEmail}</strong>
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <OTPInput
                    value={verificationCode}
                    onChange={setVerificationCode}
                    disabled={loginLoading}
                  />
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleVerifyCode}
                  disabled={loginLoading || verificationCode.length !== 6}
                  sx={{
                    backgroundColor: "#dbf14e",
                    color: "#121212",
                    fontWeight: 600,
                    borderRadius: "100px",
                    py: 1.5,
                    mb: 2,
                    "&:hover": { backgroundColor: "#c5d946" }
                  }}
                >
                  {loginLoading ? <CircularProgress size={24} /> : "Verify Code"}
                </Button>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="text"
                    onClick={handleRequestCode}
                    disabled={loginLoading}
                    sx={{ color: "#0d2c54", fontWeight: 600 }}
                  >
                    Resend Code
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => { setLoginStep("email"); setVerificationCode(""); setLoginError(""); }}
                    sx={{ color: "#666" }}
                  >
                    Use a different email
                  </Button>
                </Box>
              </>
            )}
          </StyledPaper>
        </Box>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-hero">
        <div className="admin-hero-content">
          <h1 className="admin-hero-title">
            <span>Member</span>
            <br />
            <span>Administration</span>
          </h1>
          <p className="admin-hero-subtitle">Manage CCSC Members</p>
        </div>
      </div>

      <UserHeader />
      <Box sx={{ maxWidth: activeTabId === 'rota' ? (rotaInfoPanelOpen ? 1600 : 1200) : 900, width: '100%', margin: "0 auto", padding: { xs: "16px 12px", sm: "32px 24px" }, boxSizing: 'border-box', overflowX: 'clip', transition: 'max-width 0.3s ease' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTab-root": {
                fontFamily: "'Chillax', sans-serif",
                fontWeight: 600,
                fontSize: { xs: "0.85rem", sm: "1rem" },
                textTransform: "none",
                minWidth: { xs: "auto", sm: "120px" },
                px: { xs: 1, sm: 2 },
              },
              "& .Mui-selected": {
                color: "#0d2c54",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#0d2c54",
              },
            }}
          >
            {(() => {
              const tabIcons = {
                members: <PersonIcon />,
                settings: <SettingsIcon />,
                chatbot: <ChatIcon />,
                help: <HelpOutlineIcon />,
                activity: <EventNoteIcon />,
                rota: <CalendarTodayIcon />,
                users: <AdminPanelSettingsIcon />,
              };
              return visibleAdminTabs.map(t => (
                <Tab key={t.id} label={t.label} icon={tabIcons[t.id]} iconPosition="start" />
              ));
            })()}
          </Tabs>
        </Box>

        {activeEmailJob && (
          <Alert
            severity="warning"
            sx={{ mb: 2, borderRadius: 2, alignItems: 'center' }}
            action={
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={handleCancelEmailJob}
                sx={{ fontWeight: 600, borderColor: 'currentColor', whiteSpace: 'nowrap' }}
              >
                Stop sending
              </Button>
            }
          >
            <strong>{activeEmailJob.type}</strong> — sending emails in background ({activeEmailJob.total} total). Click to stop immediately.
          </Alert>
        )}

        {activeTabId === 'members' && (
        <StyledPaper elevation={3}>
          {(() => {
            const bouncedCount = members.filter(m => m.email_bounced).length;
            if (bouncedCount === 0) return null;
            const showingBounced = advancedFilters.emailBounced === 'Yes';
            return (
              <Alert
                severity="warning"
                icon={<ErrorOutlineIcon />}
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" sx={{ fontWeight: 600 }}
                    onClick={() => { setShowAdvancedFilters(true); handleAdvancedFilterChange('emailBounced', showingBounced ? 'All' : 'Yes'); }}>
                    {showingBounced ? 'Clear filter' : 'View'}
                  </Button>
                }
              >
                <strong>{bouncedCount}</strong> member{bouncedCount === 1 ? ' has' : 's have'} a bounced email address that may be invalid — please check and correct.
              </Alert>
            );
          })()}
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{
              fontFamily: "'Chillax', sans-serif",
              fontWeight: 500,
              color: '#0d2c54',
              marginBottom: 3,
            }}
          >
            Members Directory
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: "24px",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", width: "100%", maxWidth: "520px", gap: 0 }}>
              <FormControl size="small" sx={{ minWidth: 130, flexShrink: 0 }}>
                <Select
                  value={searchField}
                  onChange={(e) => handleSearchFieldChange(e.target.value)}
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px 0 0 8px", borderRight: "none" },
                    borderRadius: "8px 0 0 8px",
                  }}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="phone">Phone</MenuItem>
                  <MenuItem value="duty_date">Duty date</MenuItem>
                </Select>
              </FormControl>
              {searchField === 'name' ? (
                <Autocomplete
                  multiple
                  size="small"
                  fullWidth
                  options={members}
                  getOptionLabel={(m) => `${m.first_name || ''} ${m.name || ''}`.trim() || m.email || `#${m.id}`}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={memberNameFilter}
                  onChange={(_e, val) => setMemberNameFilter(val)}
                  filterSelectedOptions
                  renderOption={(props, m) => (
                    <li {...props} key={m.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{`${m.first_name || ''} ${m.name || ''}`.trim()}</Typography>
                        <Typography variant="caption" sx={{ color: '#888' }}>{m.email || 'no email'}</Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={memberNameFilter.length ? '' : 'Type a name…'}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "0 8px 8px 0" } }}
                    />
                  )}
                  sx={{ flex: 1 }}
                />
              ) : (
                <TextField
                  size="small"
                  fullWidth
                  type={searchField === 'duty_date' ? 'date' : 'text'}
                  placeholder={searchField === 'email' ? 'Search by email…' : searchField === 'phone' ? 'Search by phone…' : ''}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputLabelProps={searchField === 'duty_date' ? { shrink: true } : undefined}
                  InputProps={{
                    startAdornment: searchField !== 'duty_date' ? (
                      <InputAdornment position="start"><SearchIcon /></InputAdornment>
                    ) : undefined,
                  }}
                  sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "0 8px 8px 0" } }}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => window.location.href = '/register?adminAdd=true'}
                size="small"
                sx={{
                  backgroundColor: "#0d2c54",
                  color: "#fff",
                  borderRadius: "100px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.5, sm: 0.75 },
                  "&:hover": { backgroundColor: "#1a4778" },
                  whiteSpace: "nowrap",
                }}
              >
                Add Member
              </Button>
              <Button
                variant="contained"
                startIcon={<EmailIcon />}
                onClick={() => { setSingleEmailMember(null); setRecipientsExpanded(false); setComposeOpen(true); }}
                size="small"
                sx={{
                  backgroundColor: "#0d2c54",
                  color: "#fff",
                  borderRadius: "100px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.5, sm: 0.75 },
                  "&:hover": { backgroundColor: "#1a4778" },
                  whiteSpace: "nowrap",
                }}
              >
                Compose Email
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 1, flexWrap: "wrap", marginBottom: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#666" }}>Show:</Typography>
            {dutyTypes.map((type) => (
              <Chip
                key={type}
                label={type}
                clickable
                onClick={() => setDutyTypeFilter(type)}
                sx={{
                  backgroundColor: dutyTypeFilter === type ? "#0d2c54" : "#f5f5f5",
                  color: dutyTypeFilter === type ? "#fff" : "#333",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: dutyTypeFilter === type ? "#0d2c54" : "#e0e0e0",
                  },
                }}
              />
            ))}
            {availableYears.length > 0 && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#666" }}>Year:</Typography>
                <Chip
                  label="All"
                  clickable
                  onClick={() => setYearFilter("All")}
                  sx={{
                    backgroundColor: yearFilter === "All" ? "#0d2c54" : "#f5f5f5",
                    color: yearFilter === "All" ? "#fff" : "#333",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: yearFilter === "All" ? "#0d2c54" : "#e0e0e0",
                    },
                  }}
                />
                {availableYears.map((year) => (
                  <Chip
                    key={year}
                    label={year}
                    clickable
                    onClick={() => setYearFilter(String(year))}
                    sx={{
                      backgroundColor: yearFilter === String(year) ? "#0d2c54" : "#f5f5f5",
                      color: yearFilter === String(year) ? "#fff" : "#333",
                      fontWeight: 500,
                      "&:hover": {
                        backgroundColor: yearFilter === String(year) ? "#0d2c54" : "#e0e0e0",
                      },
                    }}
                  />
                ))}
              </>
            )}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 1, flexWrap: "wrap", marginBottom: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#666" }}>Sort:</Typography>
            <Chip
              icon={<SortIcon sx={{ fontSize: 16 }} />}
              label="A-Z"
              clickable
              onClick={() => setSortBy("alphabetical")}
              sx={{
                backgroundColor: sortBy === "alphabetical" ? "#0d2c54" : "#f5f5f5",
                color: sortBy === "alphabetical" ? "#fff" : "#333",
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: sortBy === "alphabetical" ? "#0d2c54" : "#e0e0e0",
                },
                "& .MuiChip-icon": {
                  color: sortBy === "alphabetical" ? "#fff" : "#666",
                },
              }}
            />
            <Chip
              icon={<SortIcon sx={{ fontSize: 16 }} />}
              label="Latest First"
              clickable
              onClick={() => setSortBy("date")}
              sx={{
                backgroundColor: sortBy === "date" ? "#0d2c54" : "#f5f5f5",
                color: sortBy === "date" ? "#fff" : "#333",
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: sortBy === "date" ? "#0d2c54" : "#e0e0e0",
                },
                "& .MuiChip-icon": {
                  color: sortBy === "date" ? "#fff" : "#666",
                },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 1, marginBottom: 2 }}>
            <Button
              size="small"
              startIcon={<FilterListIcon />}
              endIcon={showAdvancedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              sx={{
                textTransform: "none",
                color: activeAdvancedFilterCount > 0 ? "#0d2c54" : "#666",
                fontWeight: activeAdvancedFilterCount > 0 ? 600 : 400,
                borderColor: activeAdvancedFilterCount > 0 ? "#0d2c54" : "#ccc",
                backgroundColor: activeAdvancedFilterCount > 0 ? "rgba(13, 44, 84, 0.06)" : "transparent",
              }}
              variant="outlined"
            >
              Advanced Filters{activeAdvancedFilterCount > 0 ? ` (${activeAdvancedFilterCount})` : ""}
            </Button>
            {activeAdvancedFilterCount > 0 && (
              <Button size="small" onClick={handleClearAdvancedFilters} sx={{ textTransform: "none", color: "#999", fontSize: "0.8rem" }}>
                Clear all
              </Button>
            )}
          </Box>

          {showAdvancedFilters && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: "#e0e0e0", backgroundColor: "#fafafa" }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={3}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Duty Day</InputLabel>
                    <Select value={advancedFilters.dutyDay} label="Duty Day" onChange={(e) => handleAdvancedFilterChange("dutyDay", e.target.value)}>
                      <MenuItem value="All">All</MenuItem>
                      {Object.keys(dutyDayFields).map(day => (
                        <MenuItem key={day} value={day}>{day}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Has Phone</InputLabel>
                    <Select value={advancedFilters.hasPhone} label="Has Phone" onChange={(e) => handleAdvancedFilterChange("hasPhone", e.target.value)}>
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Has Household</InputLabel>
                    <Select value={advancedFilters.hasFamily} label="Has Household" onChange={(e) => handleAdvancedFilterChange("hasFamily", e.target.value)}>
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Phone in Rota</InputLabel>
                    <Select value={advancedFilters.phoneInRota} label="Phone in Rota" onChange={(e) => handleAdvancedFilterChange("phoneInRota", e.target.value)}>
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Prior Training</InputLabel>
                    <Select value={advancedFilters.hadPriorTraining} label="Prior Training" onChange={(e) => handleAdvancedFilterChange("hadPriorTraining", e.target.value)}>
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Payment</InputLabel>
                    <Select value={advancedFilters.paymentStatus} label="Payment" onChange={(e) => handleAdvancedFilterChange("paymentStatus", e.target.value)}>
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="Paid">Paid</MenuItem>
                      <MenuItem value="Unpaid">Unpaid</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Training Booked</InputLabel>
                    <Select value={advancedFilters.hasTrainingSession} label="Training Booked" onChange={(e) => handleAdvancedFilterChange("hasTrainingSession", e.target.value)}>
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Added By</InputLabel>
                    <Select value={advancedFilters.addedBy} label="Added By" onChange={(e) => handleAdvancedFilterChange("addedBy", e.target.value)}>
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="Admin">Admin</MenuItem>
                      <MenuItem value="Self-registered">Self-registered</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Joined {currentYear}</InputLabel>
                    <Select value={advancedFilters.currentSeason} label={`Joined ${currentYear}`} onChange={(e) => handleAdvancedFilterChange("currentSeason", e.target.value)}>
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <FormControl size="small" fullWidth disabled={rotaPublishedNone || membersInPublishedRota === null}>
                    <InputLabel>In Published Rota</InputLabel>
                    <Select value={advancedFilters.rotaInPublished} label="In Published Rota" onChange={(e) => handleAdvancedFilterChange("rotaInPublished", e.target.value)}>
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="Yes">Yes — has duties</MenuItem>
                      <MenuItem value="No">No duties this season</MenuItem>
                      <MenuItem value="Upcoming">Has upcoming duties</MenuItem>
                      <MenuItem value="NoUpcoming">No upcoming duties</MenuItem>
                    </Select>
                  </FormControl>
                  {rotaPublishedNone && (
                    <Typography variant="caption" sx={{ color: "#999", mt: 0.5, display: "block" }}>No rota published yet</Typography>
                  )}
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Email Bounced</InputLabel>
                    <Select value={advancedFilters.emailBounced} label="Email Bounced" onChange={(e) => handleAdvancedFilterChange("emailBounced", e.target.value)}>
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="Yes">Bounced — invalid email</MenuItem>
                      <MenuItem value="No">Not bounced</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          )}

          {!loading && filteredMembers.length > 0 && (
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "space-between",
              flexWrap: "wrap",
              padding: "8px 16px", 
              borderRadius: 2,
              marginBottom: 2
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#0d2c54" }}>
                {filteredMembers.length} members found
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: { xs: 1, sm: 0 } }}>
                {yearFilter !== String(new Date().getFullYear()) && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<NotificationsIcon />}
                  onClick={() => setRenewalConfirmOpen(true)}
                  disabled={filteredMembers.filter(m => m.email && m.email.trim()).length === 0}
                  sx={{
                    borderColor: "#0d2c54",
                    color: "#0d2c54",
                    "&:hover": { borderColor: "#0d2c54", backgroundColor: "rgba(13, 44, 84, 0.04)" }
                  }}
                >
                  Send Renewal
                </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CalendarMonthIcon />}
                  onClick={() => {
                    setRotaDutiesSingleMember(null);
                    setRotaDutiesConfirmOpen(true);
                  }}
                  disabled={filteredMembers.filter(m => m.email && m.email.trim() && m.duty_type && m.duty_type !== 'No Duty').length === 0}
                  sx={{
                    borderColor: "#0d2c54",
                    color: "#0d2c54",
                    "&:hover": { borderColor: "#0d2c54", backgroundColor: "rgba(13, 44, 84, 0.04)" }
                  }}
                >
                  Send Rota Duties
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={() => setOpeningEmailOpen(true)}
                  disabled={filteredMembers.filter(m => m.email && m.email.trim()).length === 0}
                  sx={{
                    borderColor: "#0d2c54",
                    color: "#0d2c54",
                    "&:hover": { borderColor: "#0d2c54", backgroundColor: "rgba(13, 44, 84, 0.04)" }
                  }}
                >
                  Email Opening Times
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setRotaPreviewTab('1st Safety Officer');
                    setRotaPreviewHtml(null);
                    setRotaPreviewSubject(null);
                    setRotaPreviewMessage(null);
                    setRotaPreviewOpen(true);
                    fetchRotaEmailPreview('1st Safety Officer');
                  }}
                  sx={{
                    display: 'none',
                    borderColor: "#0d2c54",
                    color: "#0d2c54",
                    "&:hover": { borderColor: "#0d2c54", backgroundColor: "rgba(13, 44, 84, 0.04)" }
                  }}
                >
                  Preview Email
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<BadgeIcon />}
                  onClick={handlePrintAllCards}
                  disabled={filteredMembers.length === 0}
                  sx={{
                    borderColor: "#0d2c54",
                    color: "#0d2c54",
                    "&:hover": { borderColor: "#0d2c54", backgroundColor: "rgba(13, 44, 84, 0.04)" }
                  }}
                >
                  Print Cards
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<MenuBookIcon />}
                  onClick={handleDownloadDirectory}
                  disabled={filteredMembers.length === 0}
                  sx={{
                    borderColor: "#0d2c54",
                    color: "#0d2c54",
                    "&:hover": { borderColor: "#0d2c54", backgroundColor: "rgba(13, 44, 84, 0.04)" }
                  }}
                >
                  Member Directory
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportXLS}
                  disabled={filteredMembers.length === 0}
                  sx={{
                    borderColor: "#0d2c54",
                    color: "#0d2c54",
                    "&:hover": { borderColor: "#0d2c54", backgroundColor: "rgba(13, 44, 84, 0.04)" }
                  }}
                >
                  Export XLS
                </Button>
              </Box>
            </Box>
          )}

          {error && (
            <Alert
              severity="error"
              sx={{ marginBottom: "16px" }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => { fetchMembers(); fetchAvailableYears(); }}
                >
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "48px" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {filteredMembers.length === 0 ? (
                <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
                  {searchTerm ? "No members found matching your search" : "No members found"}
                </Typography>
              ) : (
                filteredMembers.map((member) => (
                  <MemberCard key={member.id} onClick={() => handleCardClick(member)} elevation={0} sx={{ width: "100%", position: "relative", border: excludedMemberIds.has(member.id) ? "1px solid #ef9a9a" : "1px solid #e0e0e0", backgroundColor: excludedMemberIds.has(member.id) ? "#fff8f8" : undefined }}>
                    <CardContent sx={{ padding: "16px 20px", width: "100%", boxSizing: "border-box" }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, member)}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          "&:hover": { backgroundColor: "#f0f0f0" }
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: 20, color: "#666" }} />
                      </IconButton>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2, pr: 3 }}>
                        <Box sx={{ flex: 1, minWidth: "200px" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: 1 }}>
                            <PersonIcon sx={{ color: "#0d2c54", fontSize: 16 }} />
                            <Typography variant="h6" sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: "#0d2c54" }}>
                              {member.name}, {member.first_name}
                            </Typography>
                            {member.added_by && (
                              <Chip
                                label="Manually Added"
                                size="small"
                                sx={{
                                  backgroundColor: '#e8f5e9',
                                  color: '#2e7d32',
                                  fontWeight: 500,
                                  fontSize: '0.7rem',
                                  height: '20px'
                                }}
                              />
                            )}
                            {excludedMemberIds.has(member.id) && (
                              <Chip
                                label="Email excluded"
                                size="small"
                                onDelete={() => setExcludedMemberIds(prev => { const n = new Set(prev); n.delete(member.id); return n; })}
                                onClick={e => e.stopPropagation()}
                                sx={{
                                  backgroundColor: '#fce4ec',
                                  color: '#c62828',
                                  fontWeight: 500,
                                  fontSize: '0.7rem',
                                  height: '20px',
                                  '& .MuiChip-deleteIcon': { color: '#c62828', fontSize: 14 }
                                }}
                              />
                            )}
                            {member.email_bounced && (
                              <Tooltip title="An email to this address bounced — it may be invalid. Correct the email to clear this warning.">
                                <Chip
                                  icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
                                  label="Email bounced"
                                  size="small"
                                  onClick={e => e.stopPropagation()}
                                  sx={{
                                    backgroundColor: '#fdecea',
                                    color: '#c62828',
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                    '& .MuiChip-icon': { color: '#c62828' }
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                          <Box 
                            sx={{ 
                              display: "inline-flex", 
                              alignItems: "center", 
                              gap: 1, 
                              marginBottom: 0.5,
                              cursor: "pointer",
                              pl: "28px",
                              "&:hover .copy-icon": { opacity: 1 }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(member.email);
                              setSnackbar({ open: true, message: "Email copied!", severity: "success" });
                            }}
                          >
                            <EmailIcon sx={{ color: "#666", fontSize: 16 }} />
                            <Typography variant="body2" color="textSecondary">
                              {member.email}
                            </Typography>
                            <ContentCopyIcon 
                              className="copy-icon"
                              sx={{ 
                                color: "#666", 
                                fontSize: 14, 
                                opacity: 0, 
                                transition: "opacity 0.2s",
                                ml: 0.5
                              }} 
                            />
                          </Box>
                          {member.phone_number && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: 0.5, pl: "28px" }}>
                              <PhoneIcon sx={{ color: "#666", fontSize: 16 }} />
                              <Typography variant="body2" color="textSecondary">
                                {member.phone_number}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
                          <Chip
                            label={member.duty_type || "Member"}
                            size="small"
                            sx={{
                              backgroundColor: member.duty_type?.includes("Safety") ? "#e3f2fd" : 
                                              member.duty_type === "Administrator" ? "#fff3e0" : "#f5f5f5",
                              color: "#0d2c54",
                              fontWeight: 500
                            }}
                          />
                          {member.address && (
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, maxWidth: "250px" }}>
                              <HomeIcon sx={{ color: "#666", fontSize: 14, mt: 0.3 }} />
                              <Typography variant="caption" color="textSecondary" sx={{ textAlign: "right" }}>
                                {member.address.length > 40 ? member.address.substring(0, 40) + "..." : member.address}
                              </Typography>
                            </Box>
                          )}
                          {(member.created_time || member.created_at) && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <CalendarTodayIcon sx={{ color: "#666", fontSize: 14 }} />
                              <Typography variant="caption" color="textSecondary">
                                {(() => {
                                  const originalJoinDate = member.created_time ? new Date(member.created_time) : null;
                                  const recordCreatedDate = member.created_at ? new Date(member.created_at) : null;
                                  const memberYears = memberYearsMap[member.id] || [];
                                  const currentYear = new Date().getFullYear();
                                  const currentYearEntry = memberYears.find(y => y.year === currentYear);
                                  const hasMultipleYears = memberYears.length > 1;
                                  const isReturning = hasMultipleYears && currentYearEntry;
                                  const displayDate = isReturning && currentYearEntry.created_at
                                    ? new Date(currentYearEntry.created_at)
                                    : (originalJoinDate || recordCreatedDate);
                                  return displayDate ? `${isReturning ? "Re-joined" : "Joined"}: ${format(displayDate, "d MMM yyyy")}` : '';
                                })()}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </MemberCard>
                ))
              )}
            </Box>
          )}
        </StyledPaper>
        )}

        {activeTabId === 'settings' && (
        <StyledPaper elevation={3}>
          <Typography 
            variant="h4" 
            align="center" 
            gutterBottom
            sx={{
              fontFamily: "'Chillax', sans-serif",
              fontWeight: 500,
              color: '#0d2c54',
              marginBottom: 3,
            }}
          >
            Season Settings
          </Typography>

          {settingsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "48px" }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <FormSection>
                <SectionTitle variant="h6">Season Dates</SectionTitle>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Configure the swimming season dates.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Registration Year"
                      value={settings.registration_year || ""}
                      onChange={(e) => handleSettingChange("registration_year", e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Registration Open Date"
                      type="date"
                      value={settings.registration_open_date || ""}
                      onChange={(e) => handleSettingChange("registration_open_date", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      helperText="Registration hidden before this date"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Season Start"
                      type="date"
                      value={settings.season_start || ""}
                      onChange={(e) => handleSettingChange("season_start", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Season End"
                      type="date"
                      value={settings.season_end || ""}
                      onChange={(e) => handleSettingChange("season_end", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.season_closed === true || settings.season_closed === "true"}
                          onChange={(e) => handleSettingChange("season_closed", e.target.checked)}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#dbf14e" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#dbf14e" }
                          }}
                        />
                      }
                      label="Pool Closed for the Season"
                      sx={{ mt: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5 }}>
                      When on, the home page shows a &quot;closed for the season&quot; notice
                      instead of the opening times, and member login is turned off. Admin
                      login is unaffected.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Season Closed Notice"
                      value={settings.season_closed_message || ""}
                      onChange={(e) => handleSettingChange("season_closed_message", e.target.value)}
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      helperText="Optional. Shown on the home page and members page when the season is closed. Leave blank for the default wording."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Evening Times Change Date"
                      type="date"
                      value={settings.evening_times_change_date || ""}
                      onChange={(e) => handleSettingChange("evening_times_change_date", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      helperText="Date when evening session times change"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="1st Duty Rota Submission Freeze"
                      type="date"
                      value={settings.first_rota_freeze_date || ""}
                      onChange={(e) => handleSettingChange("first_rota_freeze_date", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      helperText="Deadline for first duty rota submissions"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="2nd Duty Rota Submission Freeze"
                      type="date"
                      value={settings.second_rota_freeze_date || ""}
                      onChange={(e) => handleSettingChange("second_rota_freeze_date", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      helperText="Deadline for second duty rota submissions"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.enable_duty_swaps === true || settings.enable_duty_swaps === "true"}
                          onChange={(e) => handleSettingChange("enable_duty_swaps", e.target.checked)}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#dbf14e" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#dbf14e" }
                          }}
                        />
                      }
                      label="Enable Duty Swaps"
                      sx={{ mt: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5 }}>
                      When enabled, members can check and swap their duties in the members area
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.member_swaps_enabled === true || settings.member_swaps_enabled === "true"}
                          onChange={(e) => handleSettingChange("member_swaps_enabled", e.target.checked)}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#dbf14e" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#dbf14e" }
                          }}
                        />
                      }
                      label="Enable member swap requests (new confirm-by-email flow)"
                      sx={{ mt: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5, mb: 1 }}>
                      When ON, all members can request swaps/covers that the other member confirms by email. Leave OFF and use the test list below to rehearse first.
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      label="Test allow-list (emails)"
                      placeholder="you@example.com, tester@example.com"
                      value={settings.member_swaps_test_emails || ""}
                      onChange={(e) => handleSettingChange("member_swaps_test_emails", e.target.value)}
                      helperText="While the toggle is OFF, only these members see/use member swap requests. Comma- or space-separated."
                      sx={{ ml: 6, maxWidth: 520 }}
                    />
                  </Grid>
                </Grid>
              </FormSection>

              <FormSection title="Chatbot Settings">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Monthly Token Limit"
                      type="number"
                      value={settings.chatbot_monthly_token_limit || 100000}
                      onChange={(e) => handleSettingChange("chatbot_monthly_token_limit", parseInt(e.target.value) || 100000)}
                      fullWidth
                      size="small"
                      InputProps={{
                        inputProps: { min: 0 }
                      }}
                      helperText="Maximum tokens per month (chatbot hidden when exceeded)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Usage this month: {chatbotUsage?.total_tokens?.toLocaleString() || 0} / {(settings.chatbot_monthly_token_limit || 100000).toLocaleString()} tokens
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        ({chatbotUsage?.request_count || 0} requests)
                      </Typography>
                      {chatbotUsage && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#0d2c54', fontWeight: 500 }}>
                          Estimated cost: ${(
                            ((chatbotUsage.prompt_tokens || 0) * 0.00000015) + 
                            ((chatbotUsage.completion_tokens || 0) * 0.0000006)
                          ).toFixed(4)} (GPT-4o-mini)
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </FormSection>

              <FormSection title="Microsoft 365 Distribution Lists">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Sync all current members to the correct distribution list based on their duty type. Members are added to: first-officers, second-officers, or administrators.
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleSyncDistributionLists}
                  disabled={distListSyncing}
                  startIcon={distListSyncing ? <CircularProgress size={20} /> : <SyncIcon />}
                  sx={{
                    backgroundColor: "#0d2c54",
                    color: "#fff",
                    fontWeight: 600,
                    borderRadius: "100px",
                    px: 4,
                    py: 1.5,
                    "&:hover": { backgroundColor: "#1a4a7a" },
                  }}
                >
                  {distListSyncing ? "Syncing..." : "Sync All Members to Distribution Lists"}
                </Button>
                {distListResults && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 2, maxHeight: 300, overflow: "auto" }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Results: {distListResults.synced} synced, {distListResults.skipped} skipped, {distListResults.failed} failed
                    </Typography>
                    {distListResults.details.filter(d => d.status === 'failed').length > 0 && (
                      <>
                        <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>Failed:</Typography>
                        {distListResults.details.filter(d => d.status === 'failed').map((d, i) => (
                          <Typography key={i} variant="caption" display="block" color="error">
                            {d.name} ({d.email}): {d.reason}
                          </Typography>
                        ))}
                      </>
                    )}
                    {distListResults.details.filter(d => d.status === 'skipped').length > 0 && (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mt: 1, display: 'block' }}>Skipped:</Typography>
                        {distListResults.details.filter(d => d.status === 'skipped').map((d, i) => (
                          <Typography key={i} variant="caption" display="block" color="text.secondary">
                            {d.name}: {d.reason}
                          </Typography>
                        ))}
                      </>
                    )}
                  </Box>
                )}
              </FormSection>

              <FormSection title="Pricing Settings">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Price Cutoff Date"
                      type="date"
                      value={settings.price_cutoff_date || ""}
                      onChange={(e) => handleSettingChange("price_cutoff_date", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      helperText="Late prices apply after this date"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Individual (Early)"
                      type="number"
                      value={settings.individual_price_early || ""}
                      onChange={(e) => handleSettingChange("individual_price_early", e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{ startAdornment: "£" }}
                      helperText="Before cutoff date"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Individual (Late)"
                      type="number"
                      value={settings.individual_price_late || ""}
                      onChange={(e) => handleSettingChange("individual_price_late", e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{ startAdornment: "£" }}
                      helperText="After cutoff date"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Family (Early)"
                      type="number"
                      value={settings.family_price_early || ""}
                      onChange={(e) => handleSettingChange("family_price_early", e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{ startAdornment: "£" }}
                      helperText="Before cutoff date"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Family (Late)"
                      type="number"
                      value={settings.family_price_late || ""}
                      onChange={(e) => handleSettingChange("family_price_late", e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{ startAdornment: "£" }}
                      helperText="After cutoff date"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="1st Safety Officer Discount"
                      type="number"
                      value={settings.first_officer_discount || ""}
                      onChange={(e) => handleSettingChange("first_officer_discount", e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{ endAdornment: "%" }}
                      helperText="Discount percentage"
                    />
                  </Grid>
                </Grid>
              </FormSection>

              <Divider sx={{ my: 2 }} />

              <FormSection>
                <SectionTitle variant="h6">1st Safety Officer Training</SectionTitle>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Training day, pool sessions, and deadline for 1st Safety Officers.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Training Day"
                      value={settings.first_officer_training_day || ""}
                      onChange={(e) => handleSettingChange("first_officer_training_day", e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="e.g., 1 March 26 (9am-5pm)"
                      helperText="Single training day for 1st Safety Officers"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#0d2c54" }}>
                      Pool Sessions
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {(settings.first_officer_pool_sessions || "").split(",").filter(d => d.trim()).map((session, index) => (
                        <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                          <TextField
                            value={session.trim()}
                            onChange={(e) => {
                              const sessions = (settings.first_officer_pool_sessions || "").split(",").map(d => d.trim());
                              sessions[index] = e.target.value;
                              handleSettingChange("first_officer_pool_sessions", sessions.join(", "));
                            }}
                            fullWidth
                            size="small"
                            placeholder="e.g., 8 March 26 (10am-12pm)"
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              const sessions = (settings.first_officer_pool_sessions || "").split(",").map(d => d.trim()).filter((_, i) => i !== index);
                              handleSettingChange("first_officer_pool_sessions", sessions.join(", "));
                            }}
                            sx={{ color: "#d32f2f" }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const current = settings.first_officer_pool_sessions || "";
                          handleSettingChange("first_officer_pool_sessions", current ? current + ", New Session" : "New Session");
                        }}
                        sx={{ alignSelf: "flex-start", color: "#0d2c54" }}
                      >
                        Add Pool Session
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Registration Deadline"
                      type="date"
                      value={settings.first_officer_deadline || ""}
                      onChange={(e) => handleSettingChange("first_officer_deadline", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Discount Percentage"
                      type="number"
                      value={settings.first_officer_discount || ""}
                      onChange={(e) => handleSettingChange("first_officer_discount", e.target.value)}
                      fullWidth
                      size="small"
                      helperText="Discount for 1st Safety Officers"
                    />
                  </Grid>
                </Grid>
              </FormSection>

              <Divider sx={{ my: 2 }} />

              <FormSection>
                <SectionTitle variant="h6">2nd Safety Officer Training</SectionTitle>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Theory and pool sessions for 2nd Safety Officers.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#0d2c54" }}>
                      Theory Sessions
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {(settings.second_officer_theory_sessions || "").split(",").filter(d => d.trim()).map((session, index) => {
                        const match = session.trim().match(/^(.+?)\s*\((.+?)\)$/) || [null, session.trim(), ""];
                        const dateStr = match[1] || "";
                        const timeStr = match[2] || "7:30-9:30pm";
                        const parsedDate = parse(dateStr, "d MMMM yy", new Date());
                        const dateValue = isValid(parsedDate) ? parsedDate : null;
                        return (
                          <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <DatePicker
                              value={dateValue}
                              onChange={(newDate) => {
                                const sessions = (settings.second_officer_theory_sessions || "").split(",").map(s => s.trim());
                                const formattedDate = newDate && isValid(newDate) ? format(newDate, "d MMMM yy") : dateStr;
                                sessions[index] = `${formattedDate} (${timeStr})`;
                                handleSettingChange("second_officer_theory_sessions", sessions.join(", "));
                              }}
                              slotProps={{
                                textField: {
                                  size: "small",
                                  sx: { flex: 1 }
                                }
                              }}
                              format="d MMMM yy"
                            />
                            <TextField
                              value={timeStr}
                              onChange={(e) => {
                                const sessions = (settings.second_officer_theory_sessions || "").split(",").map(s => s.trim());
                                sessions[index] = `${dateStr} (${e.target.value})`;
                                handleSettingChange("second_officer_theory_sessions", sessions.join(", "));
                              }}
                              size="small"
                              placeholder="Time range"
                              sx={{ width: 150 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                const sessions = (settings.second_officer_theory_sessions || "").split(",").map(s => s.trim()).filter((_, i) => i !== index);
                                handleSettingChange("second_officer_theory_sessions", sessions.join(", "));
                              }}
                              sx={{ color: "#d32f2f" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        );
                      })}
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const current = settings.second_officer_theory_sessions || "";
                          const newDate = format(new Date(), "d MMMM yy");
                          handleSettingChange("second_officer_theory_sessions", current ? current + `, ${newDate} (7:30-9:30pm)` : `${newDate} (7:30-9:30pm)`);
                        }}
                        sx={{ alignSelf: "flex-start", color: "#0d2c54" }}
                      >
                        Add Theory Session
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#0d2c54" }}>
                      Pool Sessions
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {(settings.second_officer_pool_sessions || "").split(",").filter(d => d.trim()).map((session, index) => {
                        const match = session.trim().match(/^(.+?)\s*\((.+?)\)$/) || [null, session.trim(), ""];
                        const dateStr = match[1] || "";
                        const timeStr = match[2] || "11:15-12:15am";
                        const parsedDate = parse(dateStr, "d MMMM yy", new Date());
                        const dateValue = isValid(parsedDate) ? parsedDate : null;
                        return (
                          <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <DatePicker
                              value={dateValue}
                              onChange={(newDate) => {
                                const sessions = (settings.second_officer_pool_sessions || "").split(",").map(s => s.trim());
                                const formattedDate = newDate && isValid(newDate) ? format(newDate, "d MMMM yy") : dateStr;
                                sessions[index] = `${formattedDate} (${timeStr})`;
                                handleSettingChange("second_officer_pool_sessions", sessions.join(", "));
                              }}
                              slotProps={{
                                textField: {
                                  size: "small",
                                  sx: { flex: 1 }
                                }
                              }}
                              format="d MMMM yy"
                            />
                            <TextField
                              value={timeStr}
                              onChange={(e) => {
                                const sessions = (settings.second_officer_pool_sessions || "").split(",").map(s => s.trim());
                                sessions[index] = `${dateStr} (${e.target.value})`;
                                handleSettingChange("second_officer_pool_sessions", sessions.join(", "));
                              }}
                              size="small"
                              placeholder="Time range"
                              sx={{ width: 150 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                const sessions = (settings.second_officer_pool_sessions || "").split(",").map(s => s.trim()).filter((_, i) => i !== index);
                                handleSettingChange("second_officer_pool_sessions", sessions.join(", "));
                              }}
                              sx={{ color: "#d32f2f" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        );
                      })}
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const current = settings.second_officer_pool_sessions || "";
                          const newDate = format(new Date(), "d MMMM yy");
                          handleSettingChange("second_officer_pool_sessions", current ? current + `, ${newDate} (11:15-12:15am)` : `${newDate} (11:15-12:15am)`);
                        }}
                        sx={{ alignSelf: "flex-start", color: "#0d2c54" }}
                      >
                        Add Pool Session
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Registration Deadline"
                      type="date"
                      value={settings.second_officer_deadline || ""}
                      onChange={(e) => handleSettingChange("second_officer_deadline", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </FormSection>

              <Divider sx={{ my: 2 }} />

              <FormSection>
                <SectionTitle variant="h6">Opening Times</SectionTitle>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Regular pool opening hours.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Evening Start"
                      type="time"
                      value={settings.evening_start || ""}
                      onChange={(e) => handleSettingChange("evening_start", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Evening End"
                      type="time"
                      value={settings.evening_end || ""}
                      onChange={(e) => handleSettingChange("evening_end", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Late Season Evening End"
                      type="time"
                      value={settings.evening_late_season_end || ""}
                      onChange={(e) => handleSettingChange("evening_late_season_end", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Weekend Start"
                      type="time"
                      value={settings.weekend_start || ""}
                      onChange={(e) => handleSettingChange("weekend_start", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Weekend End"
                      type="time"
                      value={settings.weekend_end || ""}
                      onChange={(e) => handleSettingChange("weekend_end", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="School Holiday Start"
                      type="time"
                      value={settings.school_holiday_start || ""}
                      onChange={(e) => handleSettingChange("school_holiday_start", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="School Holiday End"
                      type="time"
                      value={settings.school_holiday_end || ""}
                      onChange={(e) => handleSettingChange("school_holiday_end", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                    />
                  </Grid>
                </Grid>
              </FormSection>

              <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  onClick={handleSaveSettings}
                  disabled={settingsSaving}
                  startIcon={settingsSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                  sx={{
                    backgroundColor: "#dbf14e",
                    color: "#121212",
                    fontWeight: 600,
                    borderRadius: "100px",
                    px: 4,
                    py: 1.5,
                    "&:hover": { backgroundColor: "#c9df3e" },
                  }}
                >
                  {settingsSaving ? "Saving..." : "Save Settings"}
                </Button>
              </Box>
            </>
          )}
        </StyledPaper>
        )}

        {activeTabId === 'chatbot' && (
        <StyledPaper elevation={3}>
          <Typography 
            variant="h4" 
            align="center" 
            gutterBottom
            sx={{
              fontFamily: "'Chillax', sans-serif",
              fontWeight: 500,
              color: '#0d2c54',
              marginBottom: 3,
            }}
          >
            Chatbot Settings
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center' }}>
            Customize the AI chatbot's behavior and responses for different pages. The chatbot uses these prompts to understand context and provide relevant help.
          </Typography>

          {chatbotLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "48px" }}>
              <CircularProgress />
            </Box>
          ) : chatbotPrompts.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No chatbot prompts found. Run the SQL script in <code>scripts/create-chatbot-prompts-table.sql</code> to set up the chatbot prompts table.
            </Alert>
          ) : editingPrompt ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ fontFamily: "'Chillax', sans-serif", color: '#0d2c54' }}>
                  Editing: {editingPrompt.page_name}
                </Typography>
                <Button
                  variant="text"
                  onClick={() => setEditingPrompt(null)}
                  sx={{ color: '#666' }}
                >
                  Cancel
                </Button>
              </Box>
              
              <FormSection>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Greeting Message
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  The first message shown when the chat opens on this page.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={promptForm.greeting_message}
                  onChange={(e) => setPromptForm(prev => ({ ...prev, greeting_message: e.target.value }))}
                  placeholder="Hello! How can I help you today?"
                  sx={{ mb: 3 }}
                />
              </FormSection>

              <FormSection>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  System Prompt (AI Instructions)
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  This tells the AI how to behave and what information to use. Include facts, rules, and guidance for answering questions.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  value={promptForm.system_prompt}
                  onChange={(e) => setPromptForm(prev => ({ ...prev, system_prompt: e.target.value }))}
                  placeholder="You are a helpful assistant..."
                />
              </FormSection>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleSavePrompt}
                  startIcon={<SaveIcon />}
                  sx={{
                    backgroundColor: "#dbf14e",
                    color: "#121212",
                    fontWeight: 600,
                    borderRadius: "100px",
                    px: 4,
                    py: 1.5,
                    "&:hover": { backgroundColor: "#c9df3e" },
                  }}
                >
                  Save Prompt
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              {chatbotPrompts.map((prompt) => (
                <Card 
                  key={prompt.id} 
                  sx={{ 
                    mb: 2, 
                    borderRadius: '12px',
                    border: '1px solid #e0e0e0',
                    opacity: prompt.is_active ? 1 : 0.6
                  }}
                >
                  <CardContent sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 1, sm: 0 }
                  }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="h6" sx={{ fontFamily: "'Chillax', sans-serif", color: '#0d2c54', wordBreak: 'break-word' }}>
                        {prompt.page_name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Page: /{prompt.page_key === 'global' ? '(all pages fallback)' : prompt.page_key}
                      </Typography>
                      {prompt.greeting_message && (
                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: '#666', wordBreak: 'break-word' }}>
                          "{prompt.greeting_message.substring(0, 80)}{prompt.greeting_message.length > 80 ? '...' : ''}"
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={prompt.is_active}
                            onChange={() => handleTogglePromptActive(prompt)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#dbf14e',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#dbf14e',
                              },
                            }}
                          />
                        }
                        label={prompt.is_active ? "Active" : "Inactive"}
                      />
                      <IconButton onClick={() => handleEditPrompt(prompt)}>
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          <Divider sx={{ my: 4 }} />

          <Typography 
            variant="h5" 
            gutterBottom
            sx={{
              fontFamily: "'Chillax', sans-serif",
              fontWeight: 500,
              color: '#0d2c54',
              marginBottom: 2,
            }}
          >
            Question Log
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Anonymous log of questions asked to Lynn. Use this to understand what members are asking about and identify gaps in your information.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showFlaggedOnly}
                  onChange={(e) => {
                    setShowFlaggedOnly(e.target.checked);
                    fetchChatbotQuestions(e.target.checked);
                  }}
                  size="small"
                />
              }
              label="Show flagged only"
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => fetchChatbotQuestions(showFlaggedOnly)}
              >
                Refresh
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setClearQuestionsConfirmOpen(true)}
                disabled={chatbotQuestions.length === 0}
              >
                Clear All
              </Button>
            </Box>
          </Box>

          {chatbotQuestionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : chatbotQuestions.length === 0 ? (
            <Alert severity="info">No questions logged yet.</Alert>
          ) : (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {chatbotQuestions.map((q) => (
                <Card key={q.id} variant="outlined" sx={{ mb: 1 }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {q.question}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        {q.flagged && (
                          <Chip label="Flagged" color="warning" size="small" />
                        )}
                        <Chip 
                          label={q.page || 'unknown'} 
                          size="small" 
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(q.created_at).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          <Dialog open={clearQuestionsConfirmOpen} onClose={() => setClearQuestionsConfirmOpen(false)}>
            <DialogTitle>Clear Question Log</DialogTitle>
            <DialogContent>
              <Typography>Are you sure you want to delete all logged questions? This cannot be undone.</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setClearQuestionsConfirmOpen(false)}>Cancel</Button>
              <Button onClick={handleClearQuestions} color="error" variant="contained">Clear All</Button>
            </DialogActions>
          </Dialog>
        </StyledPaper>
        )}

        {activeTabId === 'help' && (
        <StyledPaper elevation={3}>
          <Typography 
            variant="h4" 
            align="center" 
            gutterBottom
            sx={{
              fontFamily: "'Chillax', sans-serif",
              fontWeight: 500,
              color: '#0d2c54',
              marginBottom: 3,
            }}
          >
            Help Triggers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            Manage the help icons that trigger Lynn with specific questions. Each trigger can be placed next to form fields.
          </Typography>

          {helpTriggersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowNewTriggerForm(true)}
                  sx={{
                    bgcolor: '#dbf14e',
                    color: '#121212',
                    '&:hover': { bgcolor: '#c5d943' },
                  }}
                >
                  Add New Trigger
                </Button>
              </Box>

              {showNewTriggerForm && (
                <Card sx={{ mb: 3, border: '2px solid #dbf14e' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: '#0d2c54' }}>New Help Trigger</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="Trigger Key (unique ID)"
                        value={newTriggerForm.trigger_key}
                        onChange={(e) => setNewTriggerForm(prev => ({ ...prev, trigger_key: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                        placeholder="e.g., duty_type_help"
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label="Label (display name)"
                        value={newTriggerForm.label}
                        onChange={(e) => setNewTriggerForm(prev => ({ ...prev, label: e.target.value }))}
                        placeholder="e.g., Duty Type Selection"
                        size="small"
                        fullWidth
                      />
                      <FormControl size="small" fullWidth>
                        <InputLabel>Page</InputLabel>
                        <Select
                          value={newTriggerForm.page}
                          label="Page"
                          onChange={(e) => setNewTriggerForm(prev => ({ ...prev, page: e.target.value }))}
                        >
                          <MenuItem value="registration">Registration</MenuItem>
                          <MenuItem value="members">Members Portal</MenuItem>
                          <MenuItem value="rules">Rules</MenuItem>
                          <MenuItem value="duties">Duties</MenuItem>
                          <MenuItem value="home">Home</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Question for Lynn"
                        value={newTriggerForm.question}
                        onChange={(e) => setNewTriggerForm(prev => ({ ...prev, question: e.target.value }))}
                        multiline
                        rows={3}
                        fullWidth
                        placeholder="The question that will be sent to Lynn when the user clicks this help icon"
                      />
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={() => { setShowNewTriggerForm(false); setNewTriggerForm({ trigger_key: '', label: '', question: '', page: 'registration' }); }}>
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleCreateTrigger}
                          sx={{ bgcolor: '#dbf14e', color: '#121212', '&:hover': { bgcolor: '#c5d943' } }}
                        >
                          Create Trigger
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {helpTriggers.map((trigger) => (
                  <Card 
                    key={trigger.id}
                    sx={{ 
                      opacity: trigger.is_active ? 1 : 0.6,
                      border: editingTrigger === trigger.id ? '2px solid #dbf14e' : '1px solid #e0e0e0'
                    }}
                  >
                    <CardContent>
                      {editingTrigger === trigger.id ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <TextField
                            label="Label"
                            value={triggerForm.label}
                            onChange={(e) => setTriggerForm(prev => ({ ...prev, label: e.target.value }))}
                            size="small"
                            fullWidth
                          />
                          <FormControl size="small" fullWidth>
                            <InputLabel>Page</InputLabel>
                            <Select
                              value={triggerForm.page}
                              label="Page"
                              onChange={(e) => setTriggerForm(prev => ({ ...prev, page: e.target.value }))}
                            >
                              <MenuItem value="registration">Registration</MenuItem>
                              <MenuItem value="members">Members Portal</MenuItem>
                              <MenuItem value="rules">Rules</MenuItem>
                              <MenuItem value="duties">Duties</MenuItem>
                              <MenuItem value="home">Home</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            label="Question for Lynn"
                            value={triggerForm.question}
                            onChange={(e) => setTriggerForm(prev => ({ ...prev, question: e.target.value }))}
                            multiline
                            rows={3}
                            fullWidth
                          />
                          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button onClick={() => setEditingTrigger(null)}>Cancel</Button>
                            <Button
                              variant="contained"
                              onClick={() => handleUpdateTrigger(trigger.id, triggerForm)}
                              sx={{ bgcolor: '#dbf14e', color: '#121212', '&:hover': { bgcolor: '#c5d943' } }}
                            >
                              Save
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between', 
                            alignItems: { xs: 'flex-start', sm: 'flex-start' }, 
                            gap: { xs: 1, sm: 0 },
                            mb: 1 
                          }}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0d2c54', wordBreak: 'break-word' }}>
                                {trigger.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                Key: {trigger.trigger_key} | Page: {trigger.page}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={trigger.is_active}
                                    onChange={() => handleUpdateTrigger(trigger.id, { is_active: !trigger.is_active })}
                                    sx={{
                                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#dbf14e' },
                                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#dbf14e' },
                                    }}
                                  />
                                }
                                label={trigger.is_active ? "Active" : "Inactive"}
                              />
                              <IconButton onClick={() => { setEditingTrigger(trigger.id); setTriggerForm({ label: trigger.label, question: trigger.question, page: trigger.page }); }}>
                                <EditIcon />
                              </IconButton>
                              <IconButton onClick={() => handleDeleteTrigger(trigger.id)} color="error">
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1, fontStyle: 'italic' }}>
                            "{trigger.question}"
                          </Typography>
                        </>
                      )}
                    </CardContent>
                  </Card>
                ))}
                {helpTriggers.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No help triggers configured. Click "Add New Trigger" to create one.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </StyledPaper>
        )}

        {activeTabId === 'activity' && (
        <StyledPaper elevation={3}>
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{
              fontFamily: "'Chillax', sans-serif",
              fontWeight: 500,
              color: '#0d2c54',
              mb: 3,
            }}
          >
            Activity Log
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              label="From"
              type="date"
              size="small"
              value={activityDateFrom}
              onChange={(e) => setActivityDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={activityDateTo}
              onChange={(e) => setActivityDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              select
              label="Event Type"
              size="small"
              value={activityTypeFilter}
              onChange={(e) => setActivityTypeFilter(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              {Object.entries(activityTypeLabels).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              onClick={fetchGlobalActivityLog}
              disabled={globalActivityLogLoading}
              sx={{
                backgroundColor: '#dbf14e',
                color: '#121212',
                fontWeight: 600,
                borderRadius: '100px',
                '&:hover': { backgroundColor: '#c9df3e' },
              }}
            >
              {globalActivityLogLoading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {filteredGlobalActivityLog.length} event{filteredGlobalActivityLog.length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          {globalActivityLogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : Object.keys(groupedByDay).length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No activity found for the selected period.
            </Typography>
          ) : (
            Object.entries(groupedByDay).map(([day, entries]) => (
              <Box key={day} sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "'Chillax', sans-serif",
                    fontWeight: 600,
                    color: '#0d2c54',
                    borderBottom: '2px solid #dbf14e',
                    pb: 0.5,
                    mb: 1.5,
                  }}
                >
                  {day}
                  <Typography component="span" variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                    ({entries.length} event{entries.length !== 1 ? 's' : ''})
                  </Typography>
                </Typography>
                {entries.map((entry) => {
                  const hasDetails = entry.details && Object.keys(entry.details).length > 0;
                  const isExpanded = expandedActivityIds.has(entry.id);
                  return (
                  <Box
                    key={entry.id}
                    onClick={() => {
                      if (hasDetails) {
                        setExpandedActivityIds(prev => {
                          const next = new Set(prev);
                          if (next.has(entry.id)) next.delete(entry.id);
                          else next.add(entry.id);
                          return next;
                        });
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      py: 1,
                      px: 1.5,
                      mb: 0.5,
                      borderRadius: '8px',
                      backgroundColor: getActivityTypeColor(entry.activity_type),
                      cursor: hasDetails ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      '&:hover': hasDetails ? { opacity: 0.85, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' } : {},
                    }}
                  >
                    <Box sx={{ mt: 0.3 }}>
                      {getActivityTypeIcon(entry.activity_type)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {entry.description}
                          </Typography>
                          {hasDetails && (
                            isExpanded
                              ? <ExpandLessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              : <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(entry.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.3, flexWrap: 'wrap', alignItems: 'center' }}>
                        {entry.member_name && (
                          <Chip
                            label={entry.member_name}
                            size="small"
                            variant="outlined"
                            clickable={!!entry.member_id}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (entry.member_id) {
                                const member = members.find(m => m.id === entry.member_id);
                                if (member) handleCardClick(member);
                              }
                            }}
                            sx={{
                              height: 22,
                              fontSize: '0.75rem',
                              cursor: entry.member_id ? 'pointer' : 'default',
                              '&:hover': entry.member_id ? {
                                backgroundColor: '#dbf14e',
                                borderColor: '#c9df3e',
                              } : {},
                            }}
                          />
                        )}
                        <Chip
                          label={entry.activity_type.replace(/_/g, ' ')}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'capitalize',
                          }}
                        />
                        {entry.performed_by && entry.performed_by !== 'system' && (
                          <Typography variant="caption" color="text.secondary">
                            by {entry.performed_by}
                          </Typography>
                        )}
                      </Box>
                      {isExpanded && hasDetails && (
                        <Box sx={{
                          mt: 1,
                          pt: 1,
                          borderTop: '1px solid rgba(0,0,0,0.1)',
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, whiteSpace: 'pre-line' }}>
                            {formatActivityDetails(entry.activity_type, entry.details)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  );
                })}
              </Box>
            ))
          )}
        </StyledPaper>
        )}

        {activeTabId === 'rota' && (
        <StyledPaper elevation={3}>
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{
              fontFamily: "'Chillax', sans-serif",
              fontWeight: 500,
              color: '#0d2c54',
              mb: 3,
            }}
          >
            Duty Rota
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<SwapHorizIcon />}
              onClick={() => { resetSwapDialog(); setSwapDialogOpen(true); }}
              sx={{
                textTransform: 'none',
                color: '#0d2c54',
                borderColor: '#0d2c54',
                borderRadius: '100px',
                fontWeight: 600,
                px: 3,
              }}
            >
              Record swap
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, alignItems: 'stretch' }}>
            {[
              {
                label: 'First Half',
                startKey: 'first_half_start_date',
                endKey: 'first_half_end_date',
                publishedKey: 'first_half_published',
                boundaryKey: 'first_half_end',
                generatedKey: 'first_half_generated_at',
                defaultStart: settings.season_start,
                defaultEnd: (() => {
                  const freeze = settings.second_rota_freeze_date || settings.season_end;
                  if (!freeze) return '';
                  const d = new Date(freeze);
                  d.setDate(d.getDate() - 1);
                  return d.toISOString().split('T')[0];
                })(),
              },
              {
                label: 'Second Half',
                startKey: 'second_half_start_date',
                endKey: 'second_half_end_date',
                publishedKey: 'second_half_published',
                boundaryKey: 'second_half_start',
                generatedKey: 'second_half_generated_at',
                defaultStart: settings.second_rota_freeze_date || '',
                defaultEnd: settings.season_end,
              }
            ].map(half => {
              const isPublished = !!settings[half.publishedKey];
              const generatedAt = settings[half.generatedKey];
              return (
              <Box key={half.label} sx={{ flex: 1, minWidth: 280, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#f8f9fa' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0d2c54' }}>
                      {half.label}
                    </Typography>
                    {generatedAt && (
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Generated: {new Date(generatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    )}
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isPublished}
                        onChange={async (e) => {
                          const newVal = e.target.checked;
                          handleSettingChange(half.publishedKey, newVal);
                          const endDate = settings[half.endKey] || half.defaultEnd;
                          const startDate = settings[half.startKey] || half.defaultStart;
                          const body = { [half.publishedKey]: newVal };
                          if (half.boundaryKey === 'first_half_end') body.first_half_end = endDate;
                          if (half.boundaryKey === 'second_half_start') body.second_half_start = startDate;
                          try {
                            const resp = await fetch('/api/admin/rota/settings', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` },
                              body: JSON.stringify(body)
                            });
                            if (!resp.ok) throw new Error((await resp.json()).error);
                            setSnackbar({ open: true, message: newVal ? `${half.label} published to members` : `${half.label} hidden from members`, severity: 'success' });
                            // Refresh the "In Published Rota" filter sets so they reflect the
                            // newly (un)published half without needing a page refresh.
                            fetchMembersInPublishedRota();
                          } catch (err) {
                            setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
                          }
                        }}
                        size="small"
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' } }}
                      />
                    }
                    label={<Typography variant="caption" sx={{ color: isPublished ? '#4caf50' : '#999', fontWeight: 600 }}>{isPublished ? 'Published' : 'Hidden'}</Typography>}
                    sx={{ mr: 0 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <TextField
                    type="date"
                    label="From"
                    value={settings[half.startKey] || half.defaultStart || ''}
                    onChange={(e) => {
                      handleSettingChange(half.startKey, e.target.value);
                      fetch('/api/admin/rota/settings', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` },
                        body: JSON.stringify({ [half.startKey]: e.target.value })
                      }).catch(() => {});
                    }}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1, minWidth: 130 }}
                  />
                  <TextField
                    type="date"
                    label="To"
                    value={settings[half.endKey] || half.defaultEnd || ''}
                    onChange={(e) => {
                      handleSettingChange(half.endKey, e.target.value);
                      fetch('/api/admin/rota/settings', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` },
                        body: JSON.stringify({ [half.endKey]: e.target.value })
                      }).catch(() => {});
                    }}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1, minWidth: 130 }}
                  />
                  <Tooltip title={isPublished ? 'Unpublish this half before regenerating' : ''}>
                    <span>
                      <Button
                        variant="contained"
                        disabled={isPublished}
                        onClick={async () => {
                          const startDate = settings[half.startKey] || half.defaultStart;
                          const endDate = settings[half.endKey] || half.defaultEnd;
                          if (!startDate || !endDate) {
                            setSnackbar({ open: true, message: 'Please set start and end dates', severity: 'error' });
                            return;
                          }
                          try {
                            setSnackbar({ open: true, message: `Generating ${half.label.toLowerCase()} rota...`, severity: 'info' });
                            const res = await fetch('/api/admin/rota/generate', {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}`
                              },
                              body: JSON.stringify({ startDate, endDate })
                            });
                            const data = await res.json();
                            if (!res.ok) throw new Error(data.error);
                            setSnackbar({ open: true, message: data.message, severity: 'success' });
                            setSettings(prev => ({ ...prev, [half.generatedKey]: new Date().toISOString() }));
                            if (settings.season_start && settings.season_end) fetchRotaRange(settings.season_start, settings.season_end);
                            fetchRotaDutyCounts();
                          } catch (err) {
                            setSnackbar({ open: true, message: err.message, severity: 'error' });
                          }
                        }}
                        sx={{
                          backgroundColor: "#0d2c54",
                          color: "#fff",
                          borderRadius: "100px",
                          textTransform: "none",
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          "&:hover": { backgroundColor: "#1a4778" },
                          "&.Mui-disabled": { backgroundColor: "#ccc", color: "#888" },
                        }}
                      >
                        Generate
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={isPublished ? 'Unpublish this half before clearing' : ''}>
                    <span>
                      <Button
                        variant="outlined"
                        disabled={isPublished}
                        onClick={async () => {
                          const startDate = settings[half.startKey] || half.defaultStart;
                          const endDate = settings[half.endKey] || half.defaultEnd;
                          if (!startDate || !endDate) {
                            setSnackbar({ open: true, message: 'Please set start and end dates', severity: 'error' });
                            return;
                          }
                          if (!window.confirm(`Are you sure you want to clear all rota assignments for the ${half.label.toLowerCase()} (${startDate} to ${endDate})? This cannot be undone.`)) return;
                          try {
                            setSnackbar({ open: true, message: `Clearing ${half.label.toLowerCase()} rota...`, severity: 'info' });
                            const res = await fetch('/api/admin/rota/clear', {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}`
                              },
                              body: JSON.stringify({ startDate, endDate })
                            });
                            const data = await res.json();
                            if (!res.ok) throw new Error(data.error);
                            setSnackbar({ open: true, message: data.message, severity: 'success' });
                            setSettings(prev => ({ ...prev, [half.generatedKey]: '' }));
                            const refreshStart = settings.season_start || startDate;
                            const refreshEnd = settings.season_end || endDate;
                            await fetchRotaRange(refreshStart, refreshEnd);
                            fetchRotaDutyCounts();
                          } catch (err) {
                            setSnackbar({ open: true, message: err.message, severity: 'error' });
                          }
                        }}
                        sx={{
                          borderColor: "#d32f2f",
                          color: "#d32f2f",
                          borderRadius: "100px",
                          textTransform: "none",
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          "&:hover": { backgroundColor: "#fef2f2", borderColor: "#b71c1c" },
                          "&.Mui-disabled": { borderColor: "#ccc", color: "#aaa" },
                        }}
                      >
                        Clear
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={async () => {
                      const startDate = settings[half.startKey] || half.defaultStart;
                      const endDate = settings[half.endKey] || half.defaultEnd;
                      if (!startDate || !endDate) {
                        setSnackbar({ open: true, message: 'Please set start and end dates', severity: 'error' });
                        return;
                      }
                      try {
                        setSnackbar({ open: true, message: `Exporting ${half.label.toLowerCase()} rota CSV...`, severity: 'info' });
                        const months = [];
                        const cur = new Date(startDate + 'T12:00:00');
                        const end = new Date(endDate + 'T12:00:00');
                        while (cur <= end) {
                          months.push({ year: cur.getFullYear(), month: cur.getMonth() + 1 });
                          cur.setMonth(cur.getMonth() + 1);
                        }
                        const allAssignments = [];
                        for (const m of months) {
                          const resp = await fetch(`/api/rota/month?year=${m.year}&month=${m.month}&admin=1`, {
                            headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` }
                          });
                          if (resp.ok) {
                            const d = await resp.json();
                            if (d.assignments) allAssignments.push(...d.assignments);
                          }
                        }
                        const filtered = allAssignments.filter(a => a.session_date >= startDate && a.session_date <= endDate);
                        if (filtered.length === 0) {
                          setSnackbar({ open: true, message: 'No assignments to export', severity: 'warning' });
                          return;
                        }
                        const grouped = {};
                        for (const a of filtered) {
                          const key = `${a.session_date}|${a.session_time}`;
                          if (!grouped[key]) grouped[key] = { date: a.session_date, session: a.session_time };
                          if (a.role_type === '1st_safety_officer') grouped[key].first = a.member_name || '';
                          if (a.role_type === '2nd_safety_officer') grouped[key].second = a.member_name || '';
                          if (a.role_type === 'administrator' && a.slot_number === 1) grouped[key].admin1 = a.member_name || '';
                          if (a.role_type === 'administrator' && a.slot_number === 2) grouped[key].admin2 = a.member_name || '';
                        }
                        const sortedKeys = Object.keys(grouped).sort();
                        const header = 'Date,Day,Session,1st Safety Officer,2nd Safety Officer,Administrator 1,Administrator 2';
                        const rows = sortedKeys.map(key => {
                          const r = grouped[key];
                          const d = new Date(r.date + 'T12:00:00');
                          const day = d.toLocaleDateString('en-GB', { weekday: 'short' });
                          const dateStr = d.toLocaleDateString('en-GB');
                          return `${dateStr},${day},${r.session},"${r.first || ''}","${r.second || ''}","${r.admin1 || ''}","${r.admin2 || ''}"`;
                        });
                        const csv = [header, ...rows].join('\n');
                        const blob = new Blob([csv], { type: 'text/csv' });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `CCSC_Rota_${half.label.replace(' ', '_')}.csv`;
                        link.click();
                        URL.revokeObjectURL(url);
                        setSnackbar({ open: true, message: 'CSV export complete', severity: 'success' });
                      } catch (err) {
                        setSnackbar({ open: true, message: err.message, severity: 'error' });
                      }
                    }}
                    sx={{
                      textTransform: 'none',
                      color: '#0d2c54',
                      borderColor: '#0d2c54',
                      borderRadius: '100px',
                      fontWeight: 600,
                    }}
                  >
                    CSV
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={async () => {
                      const startDate = settings[half.startKey] || half.defaultStart;
                      const endDate = settings[half.endKey] || half.defaultEnd;
                      if (!startDate || !endDate) {
                        setSnackbar({ open: true, message: 'Please set start and end dates', severity: 'error' });
                        return;
                      }
                      try {
                        setSnackbar({ open: true, message: `Generating ${half.label.toLowerCase()} rota PDF...`, severity: 'info' });
                        const halfType = half.label === 'First Half' ? 'early' : 'late';
                        const resp = await fetch(`/api/admin/rota/export-pdf?startDate=${startDate}&endDate=${endDate}&halfLabel=${halfType}`, {
                          headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` }
                        });
                        if (!resp.ok) {
                          const err = await resp.json();
                          throw new Error(err.error || 'Failed to generate PDF');
                        }
                        const blob = await resp.blob();
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        const filename = resp.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || `CCSC_Rota_${halfType}.pdf`;
                        link.download = filename;
                        link.click();
                        URL.revokeObjectURL(url);
                        setSnackbar({ open: true, message: 'PDF export complete', severity: 'success' });
                      } catch (err) {
                        setSnackbar({ open: true, message: err.message, severity: 'error' });
                      }
                    }}
                    sx={{
                      textTransform: 'none',
                      color: '#0d2c54',
                      borderColor: '#0d2c54',
                      borderRadius: '100px',
                      fontWeight: 600,
                    }}
                  >
                    PDF
                  </Button>
                </Box>
              </Box>
              );
            })}
            <IconButton
              size="small"
              onClick={() => setRotaEditDialog('config')}
              title="Rota Settings"
            >
              <SettingsIcon />
            </IconButton>
          </Box>

          {(() => {
            const holidays = settings.rota_school_holidays
              ? (typeof settings.rota_school_holidays === 'string' ? JSON.parse(settings.rota_school_holidays) : settings.rota_school_holidays)
              : [];
            const saveRotaSettings = async (newHolidays) => {
              try {
                const resp = await fetch('/api/admin/rota/settings', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` },
                  body: JSON.stringify({ rota_school_holidays: newHolidays })
                });
                const data = await resp.json();
                if (!resp.ok) throw new Error(data.error);
                setSnackbar({ open: true, message: "Holiday periods saved", severity: "success" });
              } catch (err) {
                setSnackbar({ open: true, message: `Error saving: ${err.message}`, severity: "error" });
              }
            };
            return (
              <Box sx={{ mb: 2, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                <Box
                  onClick={() => setRotaHolidaysExpanded(!rotaHolidaysExpanded)}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, cursor: 'pointer', '&:hover': { backgroundColor: '#f0f1f3' }, transition: 'background-color 0.15s' }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0d2c54" }}>
                    School Holiday Periods <span style={{ fontWeight: 400, color: '#999', fontSize: '0.75rem' }}>(double sessions: afternoon + evening)</span>
                    {holidays.length > 0 && <Chip label={holidays.length} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#e3f2fd', color: '#0d2c54' }} />}
                  </Typography>
                  {rotaHolidaysExpanded ? <ExpandLessIcon sx={{ color: '#999' }} /> : <ExpandMoreIcon sx={{ color: '#999' }} />}
                </Box>
                <Collapse in={rotaHolidaysExpanded}>
                <Box sx={{ px: 2, pb: 2 }}>
                {holidays.map((period, idx) => (
                  <Box key={idx} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
                    <TextField
                      type="date"
                      label="From"
                      value={period.from || ''}
                      onChange={(e) => {
                        const updated = [...holidays];
                        updated[idx] = { ...updated[idx], from: e.target.value };
                        handleSettingChange("rota_school_holidays", JSON.stringify(updated));
                        saveRotaSettings(updated);
                      }}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      type="date"
                      label="To"
                      value={period.to || ''}
                      onChange={(e) => {
                        const updated = [...holidays];
                        updated[idx] = { ...updated[idx], to: e.target.value };
                        handleSettingChange("rota_school_holidays", JSON.stringify(updated));
                        saveRotaSettings(updated);
                      }}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        const updated = holidays.filter((_, i) => i !== idx);
                        handleSettingChange("rota_school_holidays", JSON.stringify(updated));
                        saveRotaSettings(updated);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const updated = [...holidays, { from: '', to: '' }];
                    handleSettingChange("rota_school_holidays", JSON.stringify(updated));
                  }}
                  sx={{ textTransform: 'none', color: '#0d2c54' }}
                >
                  Add Holiday Period
                </Button>
                </Box>
                </Collapse>
              </Box>
            );
          })()}

          {(() => {
            const closedDates = Array.isArray(settings.pool_closed_dates) ? settings.pool_closed_dates : [];
            const saveClosedDates = async (newDates) => {
              try {
                const resp = await fetch('/api/admin/rota/settings', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` },
                  body: JSON.stringify({ pool_closed_dates: newDates })
                });
                const data = await resp.json();
                if (!resp.ok) throw new Error(data.error);
                setSnackbar({ open: true, message: "Pool closed dates saved", severity: "success" });
              } catch (err) {
                setSnackbar({ open: true, message: `Error saving: ${err.message}`, severity: "error" });
              }
            };
            const today = new Date().toISOString().split('T')[0];
            const futureDates = closedDates.filter(d => d >= today).sort();
            const pastCount = closedDates.length - futureDates.length;
            return (
              <Box sx={{ mb: 2, backgroundColor: '#fff8e1', borderRadius: 2, border: '1px solid #ffe082', overflow: 'hidden' }}>
                <Box
                  onClick={() => setRotaClosedDatesExpanded(!rotaClosedDatesExpanded)}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, cursor: 'pointer', '&:hover': { backgroundColor: '#fff3cd' }, transition: 'background-color 0.15s' }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#e65100" }}>
                    Pool Closed Dates <span style={{ fontWeight: 400, color: '#999', fontSize: '0.75rem' }}>(sessions will not be generated for these dates)</span>
                    {futureDates.length > 0 && <Chip label={futureDates.length} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#ffe0b2', color: '#e65100' }} />}
                  </Typography>
                  {rotaClosedDatesExpanded ? <ExpandLessIcon sx={{ color: '#999' }} /> : <ExpandMoreIcon sx={{ color: '#999' }} />}
                </Box>
                <Collapse in={rotaClosedDatesExpanded}>
                <Box sx={{ px: 2, pb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {futureDates.map(dt => {
                    const dateObj = new Date(dt + 'T12:00:00');
                    return (
                      <Chip
                        key={dt}
                        label={dateObj.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                        size="small"
                        onDelete={() => {
                          const updated = closedDates.filter(d => d !== dt);
                          handleSettingChange("pool_closed_dates", updated);
                          saveClosedDates(updated);
                        }}
                        sx={{
                          fontSize: '0.75rem',
                          backgroundColor: '#fff',
                          border: '1px solid #ffe082',
                          color: '#e65100',
                        }}
                      />
                    );
                  })}
                  {futureDates.length === 0 && (
                    <Typography variant="body2" sx={{ color: '#999', fontSize: '0.8rem', fontStyle: 'italic' }}>
                      No closed dates set
                    </Typography>
                  )}
                </Box>
                {pastCount > 0 && (
                  <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 1 }}>
                    {pastCount} past date{pastCount !== 1 ? 's' : ''} hidden
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    label="Add closed date"
                    inputProps={{ min: today }}
                    sx={{ width: 180 }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const val = e.target.value;
                        if (val && !closedDates.includes(val)) {
                          const updated = [...closedDates, val].sort();
                          handleSettingChange("pool_closed_dates", updated);
                          saveClosedDates(updated);
                          e.target.value = '';
                        }
                      }
                    }}
                    id="pool-closed-date-input"
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const input = document.getElementById('pool-closed-date-input');
                      const val = input?.value;
                      if (val && !closedDates.includes(val)) {
                        const updated = [...closedDates, val].sort();
                        handleSettingChange("pool_closed_dates", updated);
                        saveClosedDates(updated);
                        input.value = '';
                      }
                    }}
                    sx={{ textTransform: 'none', color: '#e65100', borderColor: '#e65100', borderRadius: '100px', fontWeight: 600, minWidth: 60 }}
                  >
                    Add
                  </Button>
                </Box>
                </Box>
                </Collapse>
              </Box>
            );
          })()}

          {Array.isArray(rotaConflicts) && rotaConflicts.length > 0 && (
            <Box sx={{ mb: 2, backgroundColor: '#fdecea', borderRadius: 2, border: '1px solid #f5b1aa', overflow: 'hidden' }}>
              <Box
                onClick={() => setRotaConflictsExpanded(!rotaConflictsExpanded)}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, cursor: 'pointer', '&:hover': { backgroundColor: '#fbe0dd' }, transition: 'background-color 0.15s' }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#c62828" }}>
                  Duty Conflicts <span style={{ fontWeight: 400, color: '#999', fontSize: '0.75rem' }}>(assigned member has since declared the date unavailable)</span>
                  <Chip label={rotaConflicts.length} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#f5b1aa', color: '#7f1d14' }} />
                </Typography>
                {rotaConflictsExpanded ? <ExpandLessIcon sx={{ color: '#999' }} /> : <ExpandMoreIcon sx={{ color: '#999' }} />}
              </Box>
              <Collapse in={rotaConflictsExpanded}>
                <Box sx={{ px: 2, pb: 2 }}>
                  {rotaConflicts.map((c) => {
                    const d = new Date(c.session_date + 'T12:00:00');
                    const dateLabel = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
                    const reasonLabel = c.reason?.type === 'holiday_period'
                      ? `On holiday ${new Date(c.reason.from + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}–${new Date(c.reason.to + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                      : 'Declared this date unavailable';
                    return (
                      <Box key={c.assignment_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: '1px solid #f7d4cf', flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0d2c54', minWidth: 160 }}>{c.member_name}</Typography>
                        <Typography variant="body2" sx={{ color: '#444', minWidth: 230 }}>
                          {dateLabel} · {(c.session_time || '').slice(0, 5)} · {ROLE_TYPE_LABEL[c.role_type] || c.role_type}
                        </Typography>
                        <Chip size="small" label={reasonLabel} sx={{ height: 20, fontSize: '0.7rem', backgroundColor: '#fff', border: '1px solid #f5b1aa', color: '#c62828' }} />
                        {c.published && (
                          <Tooltip title="This half is published — change it via Swap">
                            <Chip size="small" label="Published" sx={{ height: 20, fontSize: '0.7rem', backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })}
                  <Typography variant="caption" sx={{ color: '#999', mt: 1, display: 'block' }}>
                    These duties were assigned before the member declared the date unavailable. Reassign them (published halves: use Swap) or mark the session closed if it's been cancelled.
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Chip
              label="Today & future"
              variant={rotaFilterMode === 'future' ? 'filled' : 'outlined'}
              onClick={() => {
                setRotaFilterMode('future');
                setRotaFilterStart('');
                setRotaFilterEnd('');
              }}
              sx={{
                backgroundColor: rotaFilterMode === 'future' ? '#0d2c54' : 'transparent',
                color: rotaFilterMode === 'future' ? '#fff' : '#0d2c54',
                borderColor: '#0d2c54',
                fontWeight: 600,
                '&:hover': { backgroundColor: rotaFilterMode === 'future' ? '#1a4778' : 'rgba(13,44,84,0.08)' },
              }}
            />
            <Chip
              label="All"
              variant={rotaFilterMode === 'all' ? 'filled' : 'outlined'}
              onClick={() => {
                setRotaFilterMode('all');
                setRotaFilterStart('');
                setRotaFilterEnd('');
              }}
              sx={{
                backgroundColor: rotaFilterMode === 'all' ? '#0d2c54' : 'transparent',
                color: rotaFilterMode === 'all' ? '#fff' : '#0d2c54',
                borderColor: '#0d2c54',
                fontWeight: 600,
                '&:hover': { backgroundColor: rotaFilterMode === 'all' ? '#1a4778' : 'rgba(13,44,84,0.08)' },
              }}
            />
            {(() => {
              const unassignedCount = rotaMembers.filter(m => m.duty_type && !(rotaTotalDutyCounts[m.id] > 0)).length;
              return (
                <Chip
                  label={`Unassigned (${unassignedCount})`}
                  variant={showUnassigned ? 'filled' : 'outlined'}
                  onClick={() => setShowUnassigned(!showUnassigned)}
                  sx={{
                    backgroundColor: showUnassigned ? '#e65100' : 'transparent',
                    color: showUnassigned ? '#fff' : '#e65100',
                    borderColor: '#e65100',
                    fontWeight: 600,
                    '&:hover': { backgroundColor: showUnassigned ? '#bf360c' : 'rgba(230,81,0,0.08)' },
                  }}
                />
              );
            })()}
            <TextField
              type="date"
              label="From"
              size="small"
              value={rotaFilterStart}
              onChange={(e) => { setRotaFilterStart(e.target.value); setRotaFilterMode('custom'); }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
            <TextField
              type="date"
              label="To"
              size="small"
              value={rotaFilterEnd}
              onChange={(e) => { setRotaFilterEnd(e.target.value); setRotaFilterMode('custom'); }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
            {rotaFilterMode === 'custom' && (rotaFilterStart || rotaFilterEnd) && (
              <Chip
                label="Clear filter"
                size="small"
                onDelete={() => { setRotaFilterStart(''); setRotaFilterEnd(''); setRotaFilterMode('future'); }}
                sx={{ color: '#666' }}
              />
            )}
            <Box sx={{ flex: 1 }} />
            <TextField
              size="small"
              placeholder="Search members..."
              value={rotaSearch}
              onChange={(e) => setRotaSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#999' }} /></InputAdornment>,
                endAdornment: rotaSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setRotaSearch('')}><span style={{ fontSize: 14 }}>✕</span></IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ minWidth: 200 }}
            />
            <Tooltip title={rotaInfoPanelOpen ? 'Hide member info panel' : 'Show member info panel'}>
              <Chip
                icon={<InfoIcon sx={{ fontSize: 16 }} />}
                label="Info"
                variant={rotaInfoPanelOpen ? 'filled' : 'outlined'}
                onClick={() => { setRotaInfoPanelOpen(!rotaInfoPanelOpen); if (rotaInfoPanelOpen) setRotaHoveredMemberId(null); }}
                sx={{
                  backgroundColor: rotaInfoPanelOpen ? '#0d2c54' : 'transparent',
                  color: rotaInfoPanelOpen ? '#fff' : '#0d2c54',
                  borderColor: '#0d2c54',
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: rotaInfoPanelOpen ? '#fff' : '#0d2c54' },
                  '&:hover': { backgroundColor: rotaInfoPanelOpen ? '#1a4778' : 'rgba(13,44,84,0.08)' },
                }}
              />
            </Tooltip>
          </Box>

          {rotaLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (() => {
            const today = new Date().toISOString().split('T')[0];
            let filtered = rotaAllAssignments;
            if (rotaFilterMode === 'future') {
              filtered = filtered.filter(a => a.session_date >= today);
            } else if (rotaFilterMode === 'custom') {
              if (rotaFilterStart) filtered = filtered.filter(a => a.session_date >= rotaFilterStart);
              if (rotaFilterEnd) filtered = filtered.filter(a => a.session_date <= rotaFilterEnd);
            }

            if (rotaSearch.trim()) {
              const searchTerms = rotaSearch.toLowerCase().split(/[\s,]+/).filter(Boolean);
              const matchingKeys = new Set();
              for (const a of filtered) {
                const name = (a.member_name || '').toLowerCase();
                if (searchTerms.some(term => name.includes(term))) {
                  matchingKeys.add(`${a.session_date}|${a.session_time}`);
                }
              }
              filtered = filtered.filter(a => matchingKeys.has(`${a.session_date}|${a.session_time}`));
            }

            if (filtered.length === 0) {
              return (
                <Box sx={{ textAlign: 'center', py: 6, color: '#999' }}>
                  <Typography variant="h6">No rota assignments found</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {rotaAllAssignments.length === 0
                      ? 'Set a date range above and click "Generate" to create assignments.'
                      : 'Try adjusting the date filter to see assignments.'}
                  </Typography>
                </Box>
              );
            }

            const grouped = {};
            for (const a of filtered) {
              const key = `${a.session_date}|${a.session_time}`;
              if (!grouped[key]) grouped[key] = { date: a.session_date, session: a.session_time, assignments: [] };
              grouped[key].assignments.push(a);
            }

            // Closed dates are skipped at generation, so they have no assignment
            // rows and would otherwise be invisible. Inject a synthetic "closed"
            // row for any closed date in range that isn't already shown.
            if (!rotaSearch.trim()) {
              const closedDates = Array.isArray(settings.pool_closed_dates) ? settings.pool_closed_dates : [];
              const datesWithRows = new Set(Object.values(grouped).map(r => r.date));
              const allDates = rotaAllAssignments.map(a => a.session_date);
              const minDate = allDates.length ? allDates.reduce((a, b) => (a < b ? a : b)) : null;
              const maxDate = allDates.length ? allDates.reduce((a, b) => (a > b ? a : b)) : null;
              for (const cd of closedDates) {
                if (datesWithRows.has(cd)) continue;
                if (rotaFilterMode === 'future' && cd < today) continue;
                if (rotaFilterMode === 'custom') {
                  if (rotaFilterStart && cd < rotaFilterStart) continue;
                  if (rotaFilterEnd && cd > rotaFilterEnd) continue;
                }
                // Keep within the generated rota's span so stray dates don't appear.
                if (minDate && cd < minDate) continue;
                if (maxDate && cd > maxDate) continue;
                grouped[`${cd}|closed`] = { date: cd, session: '', assignments: [] };
              }
            }
            const roles = [
              { roleType: '1st_safety_officer', label: '1st Safety Officer', dutyType: '1st Safety Officer', slot: 1, sortKey: 'first' },
              { roleType: '2nd_safety_officer', label: '2nd Safety Officer', dutyType: '2nd Safety Officer', slot: 1, sortKey: 'second' },
              { roleType: 'administrator', label: 'Admin 1', dutyType: 'Administrator', slot: 1, sortKey: 'admin1' },
              { roleType: 'administrator', label: 'Admin 2', dutyType: 'Administrator', slot: 2, sortKey: 'admin2' },
            ];

            const getMemberName = (row, role) => {
              const a = row.assignments.find(a => a.role_type === role.roleType && a.slot_number === role.slot);
              return (a && a.member_name) ? a.member_name.toLowerCase() : '';
            };

            const getDayOfWeek = (dateStr) => new Date(dateStr + 'T12:00:00').getDay();
            const dayOrder = [1, 2, 3, 4, 5, 6, 0];

            const sortedRows = Object.values(grouped).sort((a, b) => {
              let cmp = 0;
              if (rotaSort.column === 'date') {
                cmp = a.date.localeCompare(b.date) || a.session.localeCompare(b.session);
              } else if (rotaSort.column === 'day_of_week') {
                const dayA = dayOrder.indexOf(getDayOfWeek(a.date));
                const dayB = dayOrder.indexOf(getDayOfWeek(b.date));
                cmp = dayA - dayB || a.date.localeCompare(b.date) || a.session.localeCompare(b.session);
              } else {
                const role = roles.find(r => r.sortKey === rotaSort.column);
                if (role) {
                  const nameA = getMemberName(a, role);
                  const nameB = getMemberName(b, role);
                  if (!nameA && !nameB) cmp = a.date.localeCompare(b.date);
                  else if (!nameA) cmp = 1;
                  else if (!nameB) cmp = -1;
                  else cmp = nameA.localeCompare(nameB);
                }
              }
              return rotaSort.direction === 'desc' ? -cmp : cmp;
            });

            const handleRotaSortClick = (col) => {
              if (col === 'date') {
                setRotaSort(prev => {
                  if (prev.column === 'date' && prev.direction === 'asc') return { column: 'date', direction: 'desc' };
                  if (prev.column === 'date' && prev.direction === 'desc') return { column: 'day_of_week', direction: 'asc' };
                  if (prev.column === 'day_of_week' && prev.direction === 'asc') return { column: 'day_of_week', direction: 'desc' };
                  return { column: 'date', direction: 'asc' };
                });
              } else {
                setRotaSort(prev => ({
                  column: col,
                  direction: prev.column === col && prev.direction === 'asc' ? 'desc' : 'asc',
                }));
              }
            };

            const sortArrow = (col) => {
              if (col === 'date') {
                if (rotaSort.column === 'date') return rotaSort.direction === 'asc' ? ' ↑' : ' ↓';
                if (rotaSort.column === 'day_of_week') return rotaSort.direction === 'asc' ? ' ↑ (day)' : ' ↓ (day)';
                return ' ↕';
              }
              if (rotaSort.column !== col) return ' ↕';
              return rotaSort.direction === 'asc' ? ' ↑' : ' ↓';
            };

            const hoveredMember = rotaInfoPanelOpen && rotaHoveredMemberId
              ? rotaMembers.find(m => m.id === rotaHoveredMemberId)
              : null;

            const dayLabels = [
              { field: 'monday_duty', label: 'Monday' },
              { field: 'tuesday_duty', label: 'Tuesday' },
              { field: 'wednesday_duty', label: 'Wednesday' },
              { field: 'friday_duty', label: 'Friday' },
              { field: 'saturday_duty', label: 'Saturday' },
              { field: 'sunday_duty', label: 'Sunday' },
            ];
            const holDayLabels = [
              { field: 'school_hol_tuesday_duty', label: 'Tuesday' },
              { field: 'school_hol_thursday_duty', label: 'Thursday' },
            ];
            const closedSet = new Set(Array.isArray(settings.pool_closed_dates) ? settings.pool_closed_dates : []);

            return (
              <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, overflowX: 'auto', minWidth: 0 }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#0d2c54', color: '#fff' }}>
                      <th
                        onClick={() => handleRotaSortClick('date')}
                        style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap', position: 'sticky', left: 0, backgroundColor: '#0d2c54', zIndex: 2, borderRight: '2px solid rgba(255,255,255,0.2)', cursor: 'pointer', userSelect: 'none' }}
                      >Date / Session{sortArrow('date')}</th>
                      {roles.map(r => (
                        <th
                          key={r.label}
                          onClick={() => handleRotaSortClick(r.sortKey)}
                          style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                        >{r.label}{sortArrow(r.sortKey)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row, idx) => {
                      const d = new Date(row.date + 'T12:00:00');
                      const dayStr = d.toLocaleDateString('en-GB', { weekday: 'short' });
                      const dateStr = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                      const rowClosed = closedSet.has(row.date);

                      return (
                        <tr key={`${row.date}-${row.session}`} style={{
                          backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa',
                          borderBottom: '1px solid #eee',
                        }}>
                          <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', fontWeight: 500, position: 'sticky', left: 0, backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa', zIndex: 1, borderRight: '2px solid #eee' }}>
                            <span style={{ color: isWeekend ? '#0d2c54' : '#333' }}>{dayStr}</span>{' '}
                            <span style={{ color: '#666' }}>{dateStr}</span>
                            {rowClosed && <span style={{ marginLeft: 6, color: '#c62828', fontWeight: 700, fontSize: '0.7rem' }}>CLOSED</span>}
                            <br />
                            <span style={{ color: '#999', fontSize: '0.75rem' }}>{row.session.replace(/:00/g, '').replace('-', ' – ')}</span>
                            <br />
                            {(() => {
                              const reminderKey = `${row.date}|${row.session}`;
                              const isSending = rotaReminderSending === reminderKey;
                              const hasAnyAssigned = row.assignments.some(a => a.member_id);
                              return (
                                <Tooltip title="Send email + SMS reminder to all assigned members for this session">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={isSending ? <CircularProgress size={12} /> : <NotificationsIcon sx={{ fontSize: 14 }} />}
                                      disabled={isSending || !hasAnyAssigned}
                                      onClick={() => handleSendSessionReminders(row.date, row.session)}
                                      sx={{
                                        mt: 0.5,
                                        fontSize: '0.68rem',
                                        py: '2px',
                                        px: '8px',
                                        minWidth: 0,
                                        borderColor: '#0d2c54',
                                        color: '#0d2c54',
                                        lineHeight: 1.4,
                                        '&:hover': { backgroundColor: 'rgba(13,44,84,0.06)', borderColor: '#0d2c54' },
                                        '& .MuiButton-startIcon': { mr: '4px' },
                                      }}
                                    >
                                      {isSending ? 'Sending…' : 'Remind'}
                                    </Button>
                                  </span>
                                </Tooltip>
                              );
                            })()}
                            <br />
                            <Button
                              size="small"
                              onClick={() => toggleSessionClosed(row.date)}
                              sx={{ mt: 0.5, fontSize: '0.62rem', py: '1px', px: '6px', minWidth: 0, textTransform: 'none', color: rowClosed ? '#2e7d32' : '#b26a00' }}
                            >
                              {rowClosed ? 'Reopen session' : 'Mark closed'}
                            </Button>
                          </td>
                          {roles.map((role, ri) => {
                            if (rowClosed) return ri === 0
                              ? <td key="closed" colSpan={roles.length} style={{ padding: '8px 12px', color: '#c62828', fontStyle: 'italic', backgroundColor: '#fdecea' }}>Pool closed — session cancelled (no duties)</td>
                              : null;
                            const assignment = row.assignments.find(a =>
                              a.role_type === role.roleType && a.slot_number === role.slot
                            );
                            const dayOfWeek = new Date(row.date + 'T12:00:00').getDay();
                            const isHolidaySession = row.assignments.some(a => a.session_type === 'school_holiday');
                            const dayFieldMap = { 0: 'sunday_duty', 1: 'monday_duty', 2: 'tuesday_duty', 3: 'wednesday_duty', 5: 'friday_duty', 6: 'saturday_duty' };
                            // Mirror the generator's availability: school-holiday Tue/Thu use the
                            // separate school_hol_* flags; otherwise the weekday flag (a weekday
                            // with no regular session, e.g. Thursday, means nobody is available).
                            const isAvailableDay = (m) => {
                              if (isHolidaySession) {
                                if (dayOfWeek === 2) return m.school_hol_tuesday_duty === 'Yes';
                                if (dayOfWeek === 4) return m.school_hol_thursday_duty === 'Yes';
                                return true;
                              }
                              const dayField = dayFieldMap[dayOfWeek];
                              return dayField ? m[dayField] === 'Yes' : false;
                            };
                            const matchingMembers = rotaMembers
                              .filter(m => {
                                if (m.duty_type !== role.dutyType) return false;
                                if (!isAvailableDay(m)) return false;
                                if (m.unavailable_dates) {
                                  const unavail = Array.isArray(m.unavailable_dates) ? m.unavailable_dates : [];
                                  if (unavail.includes(row.date)) return false;
                                }
                                return true;
                              })
                              .map(m => ({
                                ...m,
                                count: rotaTotalDutyCounts[m.id] || 0
                              }))
                              .sort((a, b) => a.count - b.count);
                            const fixed = isRotaDateFixed(row.date);

                            return (
                              <td
                                key={`${role.label}-${row.date}-${row.session}`}
                                style={{ padding: '4px 8px' }}
                                onMouseEnter={rotaInfoPanelOpen && assignment?.member_id ? () => setRotaHoveredMemberId(assignment.member_id) : undefined}
                              >
                                <Tooltip title={fixed ? 'Published — change this via Swap' : ''} disableHoverListener={!fixed}>
                                <span style={{ display: 'block' }}>
                                <FormControl size="small" fullWidth sx={{ minWidth: 140 }}>
                                  <Select
                                    disabled={fixed}
                                    value={assignment?.member_id || ''}
                                    displayEmpty
                                    onChange={(e) => {
                                      const memberId = e.target.value;
                                      if (!memberId && assignment?.id) {
                                        handleRotaDelete(assignment.id);
                                        return;
                                      }
                                      const member = rotaMembers.find(m => m.id === memberId);
                                      handleRotaAssign({
                                        id: assignment?.id || undefined,
                                        session_date: row.date,
                                        session_time: row.session,
                                        session_type: assignment?.session_type || 'regular',
                                        role_type: role.roleType,
                                        slot_number: role.slot,
                                        member_id: memberId,
                                        member_name: member ? `${member.first_name} ${member.name}` : '',
                                      });
                                    }}
                                    sx={{
                                      fontSize: '0.8rem',
                                      '& .MuiSelect-select': { py: '6px' },
                                      backgroundColor: assignment?.member_id ? '#fff' : '#fff3e0',
                                    }}
                                    renderValue={(val) => {
                                      if (!val) return <em style={{ color: '#999' }}>Unassigned</em>;
                                      const count = rotaTotalDutyCounts[val] || 0;
                                      return <span>{assignment?.member_name || ''} <span style={{ color: '#999', fontSize: '0.75rem' }}>({count})</span></span>;
                                    }}
                                  >
                                    <MenuItem value="">
                                      <em>Unassigned</em>
                                    </MenuItem>
                                    {matchingMembers.map(m => (
                                      <MenuItem key={m.id} value={m.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                        <span>{m.first_name} {m.name}</span>
                                        <Chip
                                          label={m.count}
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            backgroundColor: m.count === 0 ? '#e8f5e9' : '#f5f5f5',
                                            color: m.count === 0 ? '#2e7d32' : '#666',
                                          }}
                                        />
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                </span>
                                </Tooltip>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
              {rotaInfoPanelOpen && (
                /* Spacer reserves the column; the inner box is viewport-fixed so
                   it stays put regardless of any ancestor overflow/scroll. */
                <Box sx={{ width: 320, minWidth: 320, flexShrink: 0 }}>
                <Box sx={{
                  position: 'fixed',
                  top: 72,
                  right: 'calc(max(0px, (100vw - 1600px) / 2) + 24px)',
                  width: 320,
                  maxHeight: 'calc(100vh - 90px)',
                  overflowY: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                  zIndex: 5,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                }}>
                  {hoveredMember ? (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0d2c54', mb: 0.5 }}>
                        {hoveredMember.first_name} {hoveredMember.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 2 }}>
                        {hoveredMember.duty_type || 'No duty type'}
                        {rotaTotalDutyCounts[hoveredMember.id] != null && (
                          <span> &middot; {rotaTotalDutyCounts[hoveredMember.id]} duties assigned</span>
                        )}
                      </Typography>

                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0d2c54', mb: 1 }}>
                        Weekly Availability
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                        {dayLabels.map(d => {
                          const available = hoveredMember[d.field] === 'Yes';
                          return (
                            <Box key={d.field} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{
                                width: 8, height: 8, borderRadius: '50%',
                                backgroundColor: available ? '#4caf50' : '#ef5350',
                                flexShrink: 0,
                              }} />
                              <Typography variant="body2" sx={{ color: available ? '#333' : '#999', fontSize: '0.8rem' }}>
                                {d.label}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>

                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0d2c54', mb: 1 }}>
                        School Holiday Availability
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                        {holDayLabels.map(d => {
                          const available = hoveredMember[d.field] === 'Yes';
                          return (
                            <Box key={d.field} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{
                                width: 8, height: 8, borderRadius: '50%',
                                backgroundColor: available ? '#4caf50' : '#ef5350',
                                flexShrink: 0,
                              }} />
                              <Typography variant="body2" sx={{ color: available ? '#333' : '#999', fontSize: '0.8rem' }}>
                                {d.label}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>

                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0d2c54', mb: 1 }}>
                        Unavailable Dates
                      </Typography>
                      {(() => {
                        const dates = Array.isArray(hoveredMember.unavailable_dates) ? hoveredMember.unavailable_dates : [];
                        const today = new Date().toISOString().split('T')[0];
                        const futureDates = dates.filter(d => d >= today).sort();
                        if (futureDates.length === 0) {
                          return (
                            <Typography variant="body2" sx={{ color: '#999', fontSize: '0.8rem', fontStyle: 'italic' }}>
                              No unavailable dates set
                            </Typography>
                          );
                        }
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {futureDates.map(dt => {
                              const dateObj = new Date(dt + 'T12:00:00');
                              return (
                                <Chip
                                  key={dt}
                                  label={dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', weekday: 'short' })}
                                  size="small"
                                  sx={{
                                    fontSize: '0.7rem',
                                    height: 24,
                                    backgroundColor: '#ffebee',
                                    color: '#c62828',
                                    border: '1px solid #ef9a9a',
                                  }}
                                />
                              );
                            })}
                          </Box>
                        );
                      })()}
                    </Box>
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center', color: '#999' }}>
                      <InfoIcon sx={{ fontSize: 40, color: '#ddd', mb: 1 }} />
                      <Typography variant="body2">
                        Hover over a member name in the table to see their availability
                      </Typography>
                    </Box>
                  )}
                </Box>
                </Box>
              )}
              </Box>
            );
          })()}

          {showUnassigned && (() => {
            const unassigned = rotaMembers.filter(m => m.duty_type && !(rotaTotalDutyCounts[m.id] > 0));
            if (unassigned.length === 0) return (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#e8f5e9', borderRadius: 2, border: '1px solid #a5d6a7' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                  All members with duty types are assigned
                </Typography>
              </Box>
            );

            const grouped = {};
            unassigned.forEach(m => {
              if (!grouped[m.duty_type]) grouped[m.duty_type] = [];
              grouped[m.duty_type].push(m);
            });

            return (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff8e1', borderRadius: 2, border: '1px solid #ffe082' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e65100', mb: 1 }}>
                  Members Not Yet Assigned ({unassigned.length})
                </Typography>
                {Object.entries(grouped).map(([dutyType, members]) => (
                  <Box key={dutyType} sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#bf360c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {dutyType}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {members.map(m => (
                        <Chip
                          key={m.id}
                          label={`${m.first_name} ${m.name}`}
                          size="small"
                          sx={{ fontSize: '0.75rem', backgroundColor: '#fff', border: '1px solid #ffe082' }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            );
          })()}

          {/* Record-swap dialog */}
          <Dialog
            open={swapDialogOpen}
            onClose={() => { setSwapDialogOpen(false); resetSwapDialog(); }}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: '#0d2c54' }}>
              Record a duty swap or cover
              <IconButton
                onClick={() => { setSwapDialogOpen(false); resetSwapDialog(); }}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f1f6ff', borderRadius: 1, border: '1px solid #d6e4ff' }}>
                <Typography variant="body2" sx={{ color: '#0d2c54', fontWeight: 600, mb: 0.5 }}>
                  Swap two duties — or hand one duty to someone else (a “cover”).
                </Typography>
                <Typography variant="body2" sx={{ color: '#555' }}>
                  Fill in <strong>both</strong> duty dates to swap them between the two members. To cover instead,
                  fill in just <strong>one</strong> duty date and leave the other blank — that duty moves to the member
                  on the blank side, who keeps any duties of their own (or may have none). You can fill the fields in any order.
                </Typography>
              </Box>
              <Tooltip title="Select this to record past duty swaps">
                <FormControlLabel
                  control={<Checkbox size="small" checked={swapShowPast} onChange={(e) => setSwapShowPast(e.target.checked)} />}
                  label="Show past duties?"
                  sx={{ mb: 1 }}
                />
              </Tooltip>
              {(() => {
                const labelFor = (m) => m ? `${m.first_name || ''} ${m.name || ''}`.trim() : '';
                const dutyTypeOf = (a) => a ? ROLE_TYPE_TO_DUTY_TYPE[a.role_type] : null;
                const allMembers = (rotaMembers || []).filter(m => m && m.id && m.first_name);
                const hasA = !!swapAssignmentA, hasB = !!swapAssignmentB;
                const movingDuty = hasA && !hasB ? swapAssignmentA : (!hasA && hasB ? swapAssignmentB : null);
                const moveDutyType = dutyTypeOf(movingDuty);
                // Upcoming duties only, unless "Show past duties?" is ticked
                // (lets admins record swaps that already happened).
                const todayStr = new Date().toISOString().slice(0, 10);
                const dateOk = (a) => swapShowPast || (a.session_date || '') >= todayStr;
                const assignmentsForA = (rotaAllAssignments || [])
                  .filter(a => swapMemberA && a.member_id === swapMemberA.id && dateOk(a))
                  .sort((x, y) => (x.session_date || '').localeCompare(y.session_date || ''));
                const assignmentsForB = (rotaAllAssignments || [])
                  .filter(a => swapMemberB && a.member_id === swapMemberB.id && dateOk(a))
                  .sort((x, y) => (x.session_date || '').localeCompare(y.session_date || ''));
                // When only one duty is chosen, the member on the other side is the
                // taker and must be able to do that role — filter that side's options.
                const memberAOptions = allMembers.filter(m =>
                  m.id !== (swapMemberB && swapMemberB.id) &&
                  (!(hasB && !hasA && moveDutyType) || (m.duty_type || '').trim() === moveDutyType)
                );
                const memberBOptions = allMembers.filter(m =>
                  m.id !== (swapMemberA && swapMemberA.id) &&
                  (!(hasA && !hasB && moveDutyType) || (m.duty_type || '').trim() === moveDutyType)
                );
                const v = swapCoverValidation();
                const giver = hasA ? swapMemberA : swapMemberB;
                const taker = hasA ? swapMemberB : swapMemberA;
                return (
                  <>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                      <Autocomplete
                        size="small"
                        fullWidth
                        options={memberAOptions}
                        getOptionLabel={labelFor}
                        isOptionEqualToValue={(opt, val) => opt && val && opt.id === val.id}
                        value={swapMemberA}
                        onChange={(_e, val) => { setSwapMemberA(val); setSwapAssignmentA(null); }}
                        renderInput={(params) => <TextField {...params} label="Member A" />}
                      />
                      <Autocomplete
                        size="small"
                        fullWidth
                        disabled={!swapMemberA}
                        options={assignmentsForA}
                        getOptionLabel={formatAssignmentLabel}
                        isOptionEqualToValue={(opt, val) => opt && val && opt.id === val.id}
                        value={swapAssignmentA}
                        onChange={(_e, val) => setSwapAssignmentA(val)}
                        renderInput={(params) => <TextField {...params} label="A's duty (optional)" />}
                        noOptionsText={swapMemberA ? 'No duties for this member' : 'Select Member A first'}
                      />
                      <Autocomplete
                        size="small"
                        fullWidth
                        options={memberBOptions}
                        getOptionLabel={labelFor}
                        isOptionEqualToValue={(opt, val) => opt && val && opt.id === val.id}
                        value={swapMemberB}
                        onChange={(_e, val) => { setSwapMemberB(val); setSwapAssignmentB(null); }}
                        renderInput={(params) => (
                          <TextField {...params} label={hasA && !hasB && moveDutyType ? `Member B (${moveDutyType})` : 'Member B'} />
                        )}
                      />
                      <Autocomplete
                        size="small"
                        fullWidth
                        disabled={!swapMemberB}
                        options={assignmentsForB}
                        getOptionLabel={formatAssignmentLabel}
                        isOptionEqualToValue={(opt, val) => opt && val && opt.id === val.id}
                        value={swapAssignmentB}
                        onChange={(_e, val) => setSwapAssignmentB(val)}
                        renderInput={(params) => <TextField {...params} label="B's duty (optional)" />}
                        noOptionsText={swapMemberB ? 'No duties for this member' : 'Select Member B first'}
                      />
                    </Box>
                    {v.error && swapMemberA && swapMemberB && (hasA || hasB) && (
                      <Alert severity="warning" sx={{ mt: 2 }}>{v.error}</Alert>
                    )}
                    {v.mode === 'cover' && movingDuty && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Cover: <strong>{labelFor(taker)}</strong> will take <strong>{labelFor(giver)}</strong>’s duty on {formatAssignmentLabel(movingDuty)} (they keep their own duties).
                      </Alert>
                    )}
                    {v.mode === 'swap' && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Swap: {labelFor(swapMemberA)} and {labelFor(swapMemberB)} exchange the two selected duties.
                      </Alert>
                    )}
                  </>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => { setSwapDialogOpen(false); resetSwapDialog(); }}
                disabled={swapSaving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={swapSaving || !swapCoverValidation().mode}
                onClick={handleSubmitSwap}
                sx={{ backgroundColor: '#0d2c54', color: '#fff', '&:hover': { backgroundColor: '#1a4778' }, '&.Mui-disabled': { backgroundColor: '#c5ccd6', color: '#fff' } }}
              >
                {swapSaving ? 'Saving…' : (swapCoverValidation().mode === 'cover' ? 'Record cover' : 'Save swap')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Post-save: ask whether to email both members their updated duties */}
          <Dialog
            open={swapNotifyOpen}
            onClose={() => { setSwapNotifyOpen(false); setSwapNotifyMemberIds(null); }}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: '#0d2c54' }}>
              Notify both members?
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2">
                Send the updated duty schedule by email to both members involved in this swap?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setSwapNotifyOpen(false); setSwapNotifyMemberIds(null); }}>No</Button>
              <Button variant="contained" onClick={handleNotifyAfterSwap} sx={{ backgroundColor: '#0d2c54' }}>
                Yes, send
              </Button>
            </DialogActions>
          </Dialog>
        </StyledPaper>
        )}

        {activeTabId === 'users' && (
        <StyledPaper elevation={3}>
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{
              fontFamily: "'Chillax', sans-serif",
              fontWeight: 600,
              color: '#0d2c54',
              mb: 3,
            }}
          >
            Admin Users
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { setNewUserEmail(''); setNewUserPerms([]); setAddUserDialog(true); }}
              sx={{
                backgroundColor: '#dbf14e',
                color: '#121212',
                borderRadius: '100px',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#c5d946' },
              }}
            >
              Add User
            </Button>
          </Box>

          {adminUsersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {adminUsers.map((user) => {
                const userPerms = user.role === 'full'
                  ? PERMISSION_DEFS.map(p => p.id)
                  : (user.role || '').split(',').filter(Boolean);
                const isFullAccess = user.role === 'full';

                const updateUserPerms = async (newPerms, allChecked) => {
                  const newRole = allChecked ? 'full' : newPerms.join(',') || 'members';
                  setAdminUsers(prev => prev.map(u => u.id === user.id ? { ...u, role: newRole } : u));
                  try {
                    await fetch(`/api/admin/users/${user.id}`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` },
                      body: JSON.stringify({ email: user.email, role: newRole }),
                    });
                  } catch (err) { console.error('Failed to update user:', err); }
                };

                return (
                <Box
                  key={user.id}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        backgroundColor: isFullAccess ? '#0d2c54' : '#5c7a9a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 18,
                        flexShrink: 0,
                      }}
                    >
                      {(user.email || '').charAt(0).toUpperCase()}
                    </Box>
                    <TextField
                      size="small"
                      value={user.email}
                      onChange={(e) => {
                        setAdminUsers(prev => prev.map(u => u.id === user.id ? { ...u, email: e.target.value } : u));
                      }}
                      onBlur={async (e) => {
                        const newEmail = e.target.value.trim();
                        if (!newEmail) return;
                        try {
                          await fetch(`/api/admin/users/${user.id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` },
                            body: JSON.stringify({ email: newEmail, role: user.role }),
                          });
                        } catch (err) { console.error('Failed to update user:', err); }
                      }}
                      sx={{ flex: 1, '& .MuiInputBase-input': { fontWeight: 600, color: '#0d2c54' } }}
                    />
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      {user.last_login && (
                        <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                          Last login: {new Date(user.last_login).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title="Delete user">
                      <IconButton
                        size="small"
                        onClick={async () => {
                          if (!window.confirm(`Remove ${user.email} as an admin user?`)) return;
                          try {
                            await fetch(`/api/admin/users/${user.id}`, {
                              method: 'DELETE',
                              headers: { 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` },
                            });
                            setAdminUsers(prev => prev.filter(u => u.id !== user.id));
                          } catch (err) { console.error('Failed to delete user:', err); }
                        }}
                        sx={{ color: '#999', '&:hover': { color: '#d32f2f' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', ml: 7 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={isFullAccess}
                          onChange={(e) => {
                            if (e.target.checked) {
                              updateUserPerms(PERMISSION_DEFS.map(p => p.id), true);
                            } else {
                              updateUserPerms([], false);
                            }
                          }}
                        />
                      }
                      label={<Typography variant="body2" sx={{ fontWeight: 600, color: '#0d2c54' }}>All</Typography>}
                    />
                    {PERMISSION_DEFS.map(p => (
                      <FormControlLabel
                        key={p.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={isFullAccess || userPerms.includes(p.id)}
                            onChange={(e) => {
                              let newPerms;
                              if (e.target.checked) {
                                newPerms = [...userPerms.filter(x => x !== p.id), p.id];
                              } else {
                                newPerms = userPerms.filter(x => x !== p.id);
                              }
                              const assignablePerms = PERMISSION_DEFS.map(pd => pd.id);
                              const allChecked = assignablePerms.every(ap => newPerms.includes(ap));
                              updateUserPerms(newPerms, allChecked);
                            }}
                          />
                        }
                        label={<Typography variant="body2">{p.label}</Typography>}
                      />
                    ))}
                  </Box>
                </Box>
                );
              })}
              {adminUsers.length === 0 && (
                <Typography variant="body2" sx={{ textAlign: 'center', color: '#999', py: 4 }}>
                  No admin users found
                </Typography>
              )}
            </Box>
          )}

          <Dialog open={addUserDialog} onClose={() => setAddUserDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: '#0d2c54' }}>
              Add Admin User
              <IconButton onClick={() => setAddUserDialog(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <TextField
                label="Email Address"
                fullWidth
                size="small"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0d2c54', mb: 1 }}>
                Permissions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newUserPerms.length === PERMISSION_DEFS.length}
                      indeterminate={newUserPerms.length > 0 && newUserPerms.length < PERMISSION_DEFS.length}
                      onChange={(e) => {
                        setNewUserPerms(e.target.checked ? PERMISSION_DEFS.map(p => p.id) : []);
                      }}
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontWeight: 600 }}>All Permissions</Typography>}
                />
                {PERMISSION_DEFS.map(p => (
                  <FormControlLabel
                    key={p.id}
                    control={
                      <Checkbox
                        checked={newUserPerms.includes(p.id)}
                        onChange={(e) => {
                          setNewUserPerms(prev =>
                            e.target.checked ? [...prev, p.id] : prev.filter(x => x !== p.id)
                          );
                        }}
                      />
                    }
                    label={<Typography variant="body2">{p.label}</Typography>}
                    sx={{ ml: 2 }}
                  />
                ))}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddUserDialog(false)}>Cancel</Button>
              <Button
                variant="contained"
                disabled={!newUserEmail.trim()}
                onClick={async () => {
                  const assignablePerms = PERMISSION_DEFS.map(p => p.id);
                  const allChecked = assignablePerms.every(p => newUserPerms.includes(p));
                  const role = allChecked ? 'full' : (newUserPerms.length > 0 ? newUserPerms.join(',') : 'members');
                  try {
                    const response = await fetch('/api/admin/users', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` },
                      body: JSON.stringify({ email: newUserEmail.trim(), role }),
                    });
                    if (response.ok) {
                      setAddUserDialog(false);
                      fetchAdminUsers();
                    } else {
                      const err = await response.json();
                      alert(err.error || 'Failed to add user');
                    }
                  } catch (err) { console.error('Failed to add user:', err); }
                }}
                sx={{ backgroundColor: '#0d2c54', '&:hover': { backgroundColor: '#1a4778' } }}
              >
                Add User
              </Button>
            </DialogActions>
          </Dialog>
        </StyledPaper>
        )}

        <Dialog open={rotaEditDialog === 'config'} onClose={() => setRotaEditDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: '#0d2c54' }}>
            Rota Settings
            <IconButton onClick={() => setRotaEditDialog(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0d2c54", mb: 1 }}>
              Pool Open Days
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
              {[
                { label: 'Sun', value: 0 },
                { label: 'Mon', value: 1 },
                { label: 'Tue', value: 2 },
                { label: 'Wed', value: 3 },
                { label: 'Thu', value: 4 },
                { label: 'Fri', value: 5 },
                { label: 'Sat', value: 6 },
              ].map(day => {
                const currentDays = settings.rota_pool_days
                  ? (typeof settings.rota_pool_days === 'string' ? JSON.parse(settings.rota_pool_days) : settings.rota_pool_days)
                  : [1, 2, 3, 5, 6, 0];
                const isSelected = currentDays.includes(day.value);
                return (
                  <Chip
                    key={day.value}
                    label={day.label}
                    clickable
                    onClick={() => {
                      const newDays = isSelected
                        ? currentDays.filter(d => d !== day.value)
                        : [...currentDays, day.value].sort();
                      handleSettingChange("rota_pool_days", newDays);
                      fetch('/api/admin/rota/settings', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` },
                        body: JSON.stringify({ rota_pool_days: newDays })
                      });
                    }}
                    sx={{
                      fontWeight: 600,
                      backgroundColor: isSelected ? "#0d2c54" : "#eee",
                      color: isSelected ? "#fff" : "#333",
                      "&:hover": { backgroundColor: isSelected ? "#1a4778" : "#ddd" },
                    }}
                  />
                );
              })}
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0d2c54", mb: 1 }}>
              School Holiday Periods (double sessions)
            </Typography>
            <Typography variant="caption" sx={{ color: "#999", display: 'block', mb: 1 }}>
              During these dates, pool days will have an additional afternoon session.
            </Typography>
            {(() => {
              const holidays = settings.rota_school_holidays
                ? (typeof settings.rota_school_holidays === 'string' ? JSON.parse(settings.rota_school_holidays) : settings.rota_school_holidays)
                : [];
              return (
                <>
                  {holidays.map((period, idx) => (
                    <Box key={idx} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
                      <TextField
                        type="date"
                        label="From"
                        value={period.from || ''}
                        onChange={(e) => {
                          const updated = [...holidays];
                          updated[idx] = { ...updated[idx], from: e.target.value };
                          handleSettingChange("rota_school_holidays", JSON.stringify(updated));
                        }}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        type="date"
                        label="To"
                        value={period.to || ''}
                        onChange={(e) => {
                          const updated = [...holidays];
                          updated[idx] = { ...updated[idx], to: e.target.value };
                          handleSettingChange("rota_school_holidays", JSON.stringify(updated));
                        }}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1 }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          const updated = holidays.filter((_, i) => i !== idx);
                          handleSettingChange("rota_school_holidays", JSON.stringify(updated));
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const updated = [...holidays, { from: '', to: '' }];
                      handleSettingChange("rota_school_holidays", JSON.stringify(updated));
                    }}
                    sx={{ textTransform: 'none', color: '#0d2c54' }}
                  >
                    Add Holiday Period
                  </Button>
                </>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={async () => {
                const holidays = settings.rota_school_holidays
                  ? (typeof settings.rota_school_holidays === 'string' ? JSON.parse(settings.rota_school_holidays) : settings.rota_school_holidays)
                  : [];
                const poolDays = settings.rota_pool_days
                  ? (typeof settings.rota_pool_days === 'string' ? JSON.parse(settings.rota_pool_days) : settings.rota_pool_days)
                  : [1, 2, 3, 5, 6, 0];
                try {
                  const resp = await fetch('/api/admin/rota/settings', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionStorage.getItem('adminToken')}` },
                    body: JSON.stringify({ rota_pool_days: poolDays, rota_school_holidays: holidays })
                  });
                  if (!resp.ok) throw new Error((await resp.json()).error);
                  setSnackbar({ open: true, message: "Rota settings saved!", severity: "success" });
                } catch (err) {
                  setSnackbar({ open: true, message: `Error saving: ${err.message}`, severity: "error" });
                }
                setRotaEditDialog(null);
              }}
              startIcon={<SaveIcon />}
              sx={{
                backgroundColor: "#dbf14e",
                color: "#121212",
                fontWeight: 600,
                borderRadius: "100px",
                "&:hover": { backgroundColor: "#c9df3e" },
              }}
            >
              Save Settings
            </Button>
          </DialogActions>
        </Dialog>

      </Box>

      <Dialog 
        key={selectedMember?.id || 'member-dialog'}
        open={!!selectedMember} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          fontFamily: "'Chillax', sans-serif",
          fontWeight: 600,
          color: "#0d2c54",
          pb: 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Edit Member: {selectedMember?.first_name} {selectedMember?.name}
            {selectedMember?.added_by && (
              <Chip
                label="Manually Added"
                size="small"
                sx={{
                  backgroundColor: '#e8f5e9',
                  color: '#2e7d32',
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  height: '20px'
                }}
              />
            )}
          </Box>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Tabs 
          value={dialogTab} 
          onChange={(e, v) => setDialogTab(v)}
          sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Details" />
          <Tab label="Duties" />
          <Tab label="Email History" />
          <Tab label="Activity Log" />
        </Tabs>
        <DialogContent dividers>
          {dialogTab === 0 && (
          <>
          <FormSection>
            <SectionTitle variant="h6">Contact Details</SectionTitle>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Basic contact information for this member.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>{renderField("first_name")}</Grid>
              <Grid item xs={12} sm={6}>{renderField("name")}</Grid>
              <Grid item xs={12}>{renderField("email")}</Grid>
              {editForm.email_bounced && (
                <Grid item xs={12}>
                  <Alert severity="warning" icon={<ErrorOutlineIcon />} sx={{ py: 0.5 }}>
                    An email to this address bounced{editForm.email_bounced_at ? ` on ${new Date(editForm.email_bounced_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''} — it may be misspelled or invalid. Correcting the email above clears this warning.
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>{renderField("alt_email")}</Grid>
              <Grid item xs={12} sm={6}>{renderField("phone_number")}</Grid>
              <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editForm.incl_phone_in_rota === "Yes"}
                      onChange={(e) => handleFieldChange("incl_phone_in_rota", e.target.checked ? "Yes" : "No")}
                      sx={{
                        color: "#0d2c54",
                        "&.Mui-checked": { color: "#0d2c54" }
                      }}
                    />
                  }
                  label="Include Phone in Rota"
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.75,
                    m: 0,
                    height: 40,
                    backgroundColor: editForm.incl_phone_in_rota === "Yes" ? "#e8f5e9" : "#fff"
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editForm.incl_sms_reminders !== "No"}
                      onChange={(e) => handleFieldChange("incl_sms_reminders", e.target.checked ? "Yes" : "No")}
                      sx={{
                        color: "#0d2c54",
                        "&.Mui-checked": { color: "#0d2c54" }
                      }}
                    />
                  }
                  label="SMS Duty Reminders"
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.75,
                    m: 0,
                    height: 40,
                    backgroundColor: editForm.incl_sms_reminders !== "No" ? "#e8f5e9" : "#fff"
                  }}
                />
              </Grid>
              <Grid item xs={12}>{renderField("address")}</Grid>
            </Grid>
          </FormSection>

          <Divider sx={{ my: 2 }} />

          <FormSection>
            <SectionTitle variant="h6">Admin Notes</SectionTitle>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Private notes visible only to administrators.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
              value={editForm.admin_notes || ''}
              onChange={(e) => handleFieldChange("admin_notes", e.target.value)}
              placeholder="Add notes about this member..."
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fffef5',
                  fontSize: '0.9rem',
                }
              }}
            />
          </FormSection>

          <Divider sx={{ my: 2 }} />

          <FormSection>
            <SectionTitle variant="h6">Membership Years</SectionTitle>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Years this member has been a member of the club.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              {membershipYears.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic" }}>
                  No membership years recorded
                </Typography>
              ) : (
                membershipYears.map((year) => (
                  <Chip
                    key={year}
                    label={year}
                    onDelete={() => handleRemoveYear(year)}
                    sx={{
                      backgroundColor: "#e3f2fd",
                      color: "#0d2c54",
                      fontWeight: 500,
                      "& .MuiChip-deleteIcon": {
                        color: "#0d2c54",
                        "&:hover": { color: "#d32f2f" }
                      }
                    }}
                  />
                ))
              )}
            </Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#0d2c54" }}>
              Add Year
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {[...Array(10)].map((_, i) => {
                const year = currentYear - i;
                const alreadyAdded = membershipYears.includes(year);
                return (
                  <Chip
                    key={year}
                    label={year}
                    clickable={!alreadyAdded}
                    onClick={() => !alreadyAdded && handleAddYear(year)}
                    icon={alreadyAdded ? null : <AddIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      backgroundColor: alreadyAdded ? "#e0e0e0" : "#f5f5f5",
                      color: alreadyAdded ? "#999" : "#333",
                      fontWeight: 500,
                      cursor: alreadyAdded ? "default" : "pointer",
                      "&:hover": {
                        backgroundColor: alreadyAdded ? "#e0e0e0" : "#dbf14e",
                      },
                    }}
                  />
                );
              })}
            </Box>
          </FormSection>

          <Divider sx={{ my: 2 }} />

          <FormSection>
            <SectionTitle variant="h6">Duty Information</SectionTitle>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Member's duty type and training sessions.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#0d2c54" }}>
                  Duty Type
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {["Administrator", "1st Safety Officer", "2nd Safety Officer"].map((type) => (
                    <Chip
                      key={type}
                      label={type}
                      clickable
                      onClick={() => handleFieldChange("duty_type", type)}
                      sx={{
                        backgroundColor: editForm.duty_type === type ? "#0d2c54" : "#f5f5f5",
                        color: editForm.duty_type === type ? "#fff" : "#333",
                        fontWeight: 500,
                        "&:hover": {
                          backgroundColor: editForm.duty_type === type ? "#0d2c54" : "#e0e0e0",
                        },
                      }}
                    />
                  ))}
                </Box>
              </Grid>
              
              {editForm.duty_type === "1st Safety Officer" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#0d2c54" }}>
                      Training Weekend
                    </Typography>
                    {editForm.first_training_weekend && (
                      <Typography variant="body2" sx={{ mb: 1, color: '#666', fontStyle: 'italic' }}>
                        Current: {editForm.first_training_weekend}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {(SAFETY_OFFICER_DATES?.FIRST_OFFICER?.TRAINING_DATES || []).map((date) => (
                        <Chip
                          key={date}
                          label={date}
                          clickable
                          onClick={() => handleFieldChange("first_training_weekend", date)}
                          sx={{
                            backgroundColor: editForm.first_training_weekend === date ? "#0d2c54" : "#f5f5f5",
                            color: editForm.first_training_weekend === date ? "#fff" : "#333",
                            fontWeight: 500,
                            "&:hover": {
                              backgroundColor: editForm.first_training_weekend === date ? "#0d2c54" : "#e0e0e0",
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#0d2c54" }}>
                      Pool Session
                    </Typography>
                    {editForm.first_pool_session && (
                      <Typography variant="body2" sx={{ mb: 1, color: '#666', fontStyle: 'italic' }}>
                        Current: {editForm.first_pool_session}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {(SAFETY_OFFICER_DATES?.FIRST_OFFICER?.POOL_SESSIONS || []).map((session) => {
                        const isSelected = editForm.first_pool_session === `${session.DATE} at ${session.TIME}` || 
                          (editForm.first_pool_session && editForm.first_pool_session.startsWith(session.DATE));
                        return (
                          <Chip
                            key={session.DATE}
                            label={session.DATE}
                            clickable
                            onClick={() => handleFieldChange("first_pool_session", `${session.DATE} at ${session.TIME}`)}
                            sx={{
                              backgroundColor: isSelected ? "#0d2c54" : "#f5f5f5",
                              color: isSelected ? "#fff" : "#333",
                              fontWeight: 500,
                              "&:hover": {
                                backgroundColor: isSelected ? "#0d2c54" : "#e0e0e0",
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Grid>
                </>
              )}
              
              {editForm.duty_type === "2nd Safety Officer" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#0d2c54" }}>
                      Theory Session
                    </Typography>
                    {editForm.second_theory_session && (
                      <Typography variant="body2" sx={{ mb: 1, color: '#666', fontStyle: 'italic' }}>
                        Current: {editForm.second_theory_session}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {(SAFETY_OFFICER_DATES?.SECOND_OFFICER?.THEORY_SESSIONS || []).map((session) => {
                        const isSelected = editForm.second_theory_session === session.DATE || 
                          (editForm.second_theory_session && editForm.second_theory_session.startsWith(session.DATE));
                        return (
                          <Chip
                            key={session.DATE}
                            label={session.DATE}
                            clickable
                            onClick={() => handleFieldChange("second_theory_session", session.DATE)}
                            sx={{
                              backgroundColor: isSelected ? "#0d2c54" : "#f5f5f5",
                              color: isSelected ? "#fff" : "#333",
                              fontWeight: 500,
                              "&:hover": {
                                backgroundColor: isSelected ? "#0d2c54" : "#e0e0e0",
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#0d2c54" }}>
                      Pool Session
                    </Typography>
                    {editForm.second_pool_session && (
                      <Typography variant="body2" sx={{ mb: 1, color: '#666', fontStyle: 'italic' }}>
                        Current: {editForm.second_pool_session}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {(SAFETY_OFFICER_DATES?.SECOND_OFFICER?.POOL_SESSIONS || []).map((session) => {
                        const isSelected = editForm.second_pool_session === session.DATE || 
                          (editForm.second_pool_session && editForm.second_pool_session.startsWith(session.DATE));
                        return (
                          <Chip
                            key={session.DATE}
                            label={session.DATE}
                            clickable
                            onClick={() => handleFieldChange("second_pool_session", session.DATE)}
                            sx={{
                              backgroundColor: isSelected ? "#0d2c54" : "#f5f5f5",
                              color: isSelected ? "#fff" : "#333",
                              fontWeight: 500,
                              "&:hover": {
                                backgroundColor: isSelected ? "#0d2c54" : "#e0e0e0",
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </FormSection>

          <Divider sx={{ my: 2 }} />

          <FormSection>
            <SectionTitle variant="h6">Weekly Availability</SectionTitle>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Select the days this member is available for duty.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {[
                { field: "monday_duty", label: "Monday" },
                { field: "tuesday_duty", label: "Tuesday" },
                { field: "wednesday_duty", label: "Wednesday" },
                { field: "friday_duty", label: "Friday" },
                { field: "saturday_duty", label: "Saturday" },
                { field: "sunday_duty", label: "Sunday" }
              ].map(({ field, label }) => (
                <FormControlLabel
                  key={field}
                  control={
                    <Checkbox
                      checked={editForm[field] === "Yes"}
                      onChange={(e) => handleFieldChange(field, e.target.checked ? "Yes" : "No")}
                      sx={{
                        color: "#0d2c54",
                        "&.Mui-checked": { color: "#0d2c54" }
                      }}
                    />
                  }
                  label={label}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.5,
                    m: 0,
                    backgroundColor: editForm[field] === "Yes" ? "#e8f5e9" : "#fff"
                  }}
                />
              ))}
            </Box>
          </FormSection>

          <Divider sx={{ my: 2 }} />

          <FormSection>
            <SectionTitle variant="h6">School Holiday Availability</SectionTitle>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Available during school holidays.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {[
                { field: "school_hol_tuesday_duty", label: "Tuesday (School Hols)" },
                { field: "school_hol_thursday_duty", label: "Thursday (School Hols)" }
              ].map(({ field, label }) => (
                <FormControlLabel
                  key={field}
                  control={
                    <Checkbox
                      checked={editForm[field] === "Yes"}
                      onChange={(e) => handleFieldChange(field, e.target.checked ? "Yes" : "No")}
                      sx={{
                        color: "#0d2c54",
                        "&.Mui-checked": { color: "#0d2c54" }
                      }}
                    />
                  }
                  label={label}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.5,
                    m: 0,
                    backgroundColor: editForm[field] === "Yes" ? "#e8f5e9" : "#fff"
                  }}
                />
              ))}
            </Box>
          </FormSection>

          <Divider sx={{ my: 2 }} />

          <FormSection>
            <SectionTitle variant="h6">I cannot do duties on:</SectionTitle>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Select the dates on which this member is not able to help out.
            </Typography>
            
            <UnavailabilityCalendar
              selectedDates={holidayPeriods}
              onChange={handleHolidayPeriodsChange}
              seasonStart={SEASON_CONFIG.SEASON_START}
              seasonEnd={SEASON_CONFIG.SEASON_END}
            />
          </FormSection>

          <Divider sx={{ my: 2 }} />

          <FormSection>
            <SectionTitle variant="h6">Household Members</SectionTitle>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Household members who swim with this account. Minors show their calculated age.
            </Typography>

            {familyMembers.length === 0 ? (
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic", mb: 2 }}>
                No family members registered
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                {familyMembers.map((member) => (
                  <Paper
                    key={member.id}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: editingFamilyMember === member.id 
                        ? "2px solid #0d2c54" 
                        : member.isMinor ? "1px solid #42a5f5" : "1px solid #e0e0e0",
                      backgroundColor: editingFamilyMember === member.id 
                        ? "#f5f5f5" 
                        : member.isMinor ? "#e3f2fd" : "#fff"
                    }}
                  >
                    {editingFamilyMember === member.id ? (
                      <Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Name"
                              value={familyMemberForm.name}
                              onChange={(e) => setFamilyMemberForm(prev => ({ ...prev, name: e.target.value }))}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Date of Birth"
                              type="date"
                              value={familyMemberForm.dob}
                              onChange={(e) => setFamilyMemberForm(prev => ({ ...prev, dob: e.target.value }))}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Swimming Status</InputLabel>
                              <Select
                                value={familyMemberForm.status}
                                label="Swimming Status"
                                onChange={(e) => setFamilyMemberForm(prev => ({ ...prev, status: e.target.value }))}
                              >
                                <MenuItem value="">None</MenuItem>
                                <MenuItem value="Swimming permission">Swimming permission</MenuItem>
                                <MenuItem value="Accompanied by adult">Accompanied by adult</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                        <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleSaveFamilyMember}
                            sx={{
                              backgroundColor: "#dbf14e",
                              color: "#121212",
                              "&:hover": { backgroundColor: "#c9df3e" }
                            }}
                          >
                            Save
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setEditingFamilyMember(null);
                              setFamilyMemberForm({ name: "", dob: "", status: "" });
                            }}
                          >
                            Cancel
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFamilyMember(member.id)}
                            color="error"
                            sx={{ ml: "auto" }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          {member.isMinor && <ChildCareIcon sx={{ color: "#1976d2" }} />}
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {member.name}
                              {member.age !== null && (
                                <Chip
                                  size="small"
                                  label={`${member.age} years old`}
                                  sx={{ 
                                    ml: 1,
                                    fontSize: "0.75rem",
                                    height: 22,
                                    backgroundColor: member.isMinor ? "#bbdefb" : "#e0e0e0"
                                  }}
                                />
                              )}
                            </Typography>
                            {member.status && (
                              <Typography variant="caption" color="textSecondary">
                                {member.status}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => handleEditFamilyMember(member)}
                            sx={{ mr: 0.5 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFamilyMember(member.id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            )}

            <Box sx={{ 
              border: "1px solid #e0e0e0", 
              borderRadius: 2, 
              p: 2,
              backgroundColor: "#fafafa"
            }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#0d2c54" }}>
                Add New Household Member
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Name"
                    value={editingFamilyMember === null ? familyMemberForm.name : ""}
                    onChange={(e) => {
                      if (editingFamilyMember === null) {
                        setFamilyMemberForm(prev => ({ ...prev, name: e.target.value }));
                      }
                    }}
                    disabled={editingFamilyMember !== null}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date of Birth"
                    type="date"
                    value={editingFamilyMember === null ? familyMemberForm.dob : ""}
                    onChange={(e) => {
                      if (editingFamilyMember === null) {
                        setFamilyMemberForm(prev => ({ ...prev, dob: e.target.value }));
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                    helperText="For calculating age"
                    disabled={editingFamilyMember !== null}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small" disabled={editingFamilyMember !== null}>
                    <InputLabel>Swimming Status</InputLabel>
                    <Select
                      value={editingFamilyMember === null ? familyMemberForm.status : ""}
                      label="Swimming Status"
                      onChange={(e) => {
                        if (editingFamilyMember === null) {
                          setFamilyMemberForm(prev => ({ ...prev, status: e.target.value }));
                        }
                      }}
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="Swimming permission">Swimming permission</MenuItem>
                      <MenuItem value="Accompanied by adult">Accompanied by adult</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddFamilyMember}
                  startIcon={<AddIcon />}
                  disabled={editingFamilyMember !== null || !familyMemberForm.name.trim()}
                >
                  Add Member
                </Button>
              </Box>
            </Box>
          </FormSection>

          <Divider sx={{ my: 2 }} />

          <FormSection>
            <SectionTitle variant="h6">Status</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>{renderField("payment_success")}</Grid>
              <Grid item xs={12} sm={4}>{renderField("do_not_delete")}</Grid>
              <Grid item xs={12} sm={4}>{renderField("last_edited_by")}</Grid>
            </Grid>
          </FormSection>
          </>
          )}

          {dialogTab === 1 && (
            <Box>
              <Typography variant="h6" sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: "#0d2c54", mb: 2 }}>
                Duties for {selectedMember?.first_name} {selectedMember?.name}
              </Typography>
              {memberDutiesLoading ? (
                <Typography variant="body2" color="textSecondary">Loading duties…</Typography>
              ) : (() => {
                const today = new Date().toISOString().split('T')[0];
                const all = memberDuties || [];
                const upcoming = all.filter(d => d.session_date >= today);
                const past = all.filter(d => d.session_date < today).reverse();
                const fmt = (d) => {
                  const dt = new Date(d.session_date + 'T12:00:00');
                  const date = dt.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
                  const time = (d.session_time || '').slice(0, 5);
                  return `${date} · ${time} · ${ROLE_TYPE_LABEL[d.role_type] || d.role_type}`;
                };
                if (all.length === 0) return <Typography variant="body2" color="textSecondary">No duties on the rota.</Typography>;
                return (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0d2c54', mb: 0.5 }}>
                      Upcoming ({upcoming.length})
                    </Typography>
                    {upcoming.length === 0 ? (
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>None upcoming.</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 3 }}>
                        {upcoming.map((d, i) => (
                          <Typography key={i} variant="body2" sx={{ color: '#333' }}>• {fmt(d)}</Typography>
                        ))}
                      </Box>
                    )}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#888', mb: 0.5 }}>
                      Past ({past.length})
                    </Typography>
                    {past.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">None.</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {past.map((d, i) => (
                          <Typography key={i} variant="body2" sx={{ color: '#999' }}>• {fmt(d)}</Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </Box>
          )}

          {dialogTab === 2 && (
            <Box>
              <Typography variant="h6" sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: "#0d2c54", mb: 2 }}>
                Email history for {selectedMember?.email}
              </Typography>
              {emailHistoryLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : emailHistory.length === 0 ? (
                <Typography color="textSecondary" sx={{ textAlign: "center", py: 4 }}>
                  No emails found for this member
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {emailHistory.map((email) => (
                    <Paper 
                      key={email.id} 
                      elevation={0} 
                      sx={{ 
                        border: "1px solid #e0e0e0", 
                        borderRadius: 2,
                        backgroundColor: email.direction === 'received' ? "#e3f2fd" : (email.wasCc ? "#fff8e1" : "#fff"),
                        overflow: "hidden"
                      }}
                    >
                      <Box 
                        onClick={() => setExpandedEmailId(expandedEmailId === email.id ? null : email.id)}
                        sx={{ 
                          p: 2, 
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "rgba(0,0,0,0.02)" }
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Chip 
                              label={email.direction === 'received' ? "Received" : "Sent"} 
                              size="small" 
                              sx={{ 
                                backgroundColor: email.direction === 'received' ? "#90caf9" : "#c8e6c9", 
                                fontSize: 11,
                                fontWeight: 600
                              }} 
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0d2c54" }}>
                              {email.subject || "(No subject)"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {email.wasCc && (
                              <Chip label="CC" size="small" sx={{ backgroundColor: "#ffecb3", fontSize: 11 }} />
                            )}
                            <Typography variant="caption" color="textSecondary">
                              {format(new Date(email.date), "d MMM yyyy, HH:mm")}
                            </Typography>
                            {expandedEmailId === email.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </Box>
                        </Box>
                        {expandedEmailId !== email.id && (
                          <Typography variant="body2" color="textSecondary" sx={{ 
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical"
                          }}>
                            {email.preview}
                          </Typography>
                        )}
                        {email.direction === 'sent' && email.to.length > 1 && (
                          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
                            To: {email.to.join(", ")}
                          </Typography>
                        )}
                      </Box>
                      {expandedEmailId === email.id && (
                        <Box sx={{ 
                          px: 2, 
                          pb: 2, 
                          borderTop: "1px solid #e0e0e0",
                          backgroundColor: "#fafafa"
                        }}>
                          {email.bodyType === 'html' ? (
                            <Box 
                              sx={{ 
                                mt: 2,
                                '& a': { color: '#1976d2' },
                                '& img': { maxWidth: '100%' },
                                fontSize: 14,
                                lineHeight: 1.6
                              }}
                              dangerouslySetInnerHTML={{ __html: email.body }} 
                            />
                          ) : (
                            <Typography variant="body2" sx={{ mt: 2, whiteSpace: "pre-wrap", fontSize: 14 }}>
                              {email.body || email.preview}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {dialogTab === 3 && (
            <Box>
              <Typography variant="h6" sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: "#0d2c54", mb: 2 }}>
                Activity log for {selectedMember?.first_name} {selectedMember?.name}
              </Typography>
              {activityLogLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={32} sx={{ color: "#0d2c54" }} />
                </Box>
              ) : activityLog.length === 0 ? (
                <Typography color="textSecondary" sx={{ py: 2 }}>No activity recorded for this member yet.</Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {activityLog.map((activity) => (
                    <Paper 
                      key={activity.id} 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        border: "1px solid #e0e0e0", 
                        borderRadius: 2,
                        backgroundColor: activity.activity_type === 'payment_attempt' ? '#fff3e0' :
                                        activity.activity_type === 'payment_success' ? '#e8f5e9' :
                                        activity.activity_type === 'payment_failed' ? '#ffebee' :
                                        '#fafafa'
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                        <Chip 
                          label={activity.activity_type?.replace(/_/g, ' ').toUpperCase() || 'Activity'} 
                          size="small"
                          sx={{ 
                            backgroundColor: activity.activity_type === 'payment_attempt' ? '#ff9800' :
                                            activity.activity_type === 'payment_success' ? '#4caf50' :
                                            activity.activity_type === 'payment_failed' ? '#f44336' :
                                            activity.activity_type === 'details_update' ? '#2196f3' :
                                            activity.activity_type === 'registration' ? '#9c27b0' :
                                            activity.activity_type === 'admin_update' ? '#607d8b' :
                                            activity.activity_type === 'renewal' ? '#00bcd4' :
                                            '#757575',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: 11
                          }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {activity.created_at ? new Date(activity.created_at).toLocaleString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'Unknown date'}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: "#0d2c54" }}>
                        {activity.description}
                      </Typography>
                      {activity.performed_by && activity.performed_by !== 'system' && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                          By: {activity.performed_by}
                        </Typography>
                      )}
                      {activity.details && Object.keys(activity.details).length > 0 && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, fontSize: 13, whiteSpace: 'pre-line' }}>
                          {formatActivityDetails(activity.activity_type, activity.details)}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", justifyContent: "space-between" }}>
          <Button 
            onClick={() => {
              setMemberToDelete(selectedMember);
              setDeleteConfirmOpen(true);
            }}
            variant="outlined"
            startIcon={<DeleteIcon />}
            sx={{
              borderColor: "#d32f2f",
              color: "#d32f2f",
              fontWeight: 600,
              borderRadius: "100px",
              textTransform: "none",
              "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.04)", borderColor: "#b71c1c" }
            }}
          >
            Delete Member
          </Button>
          {selectedMember?.email && selectedMember.email.trim() && (
          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            onClick={() => openSingleMemberEmail(selectedMember)}
            sx={{
              borderColor: "#0d2c54",
              color: "#0d2c54",
              fontWeight: 600,
              borderRadius: "100px",
              textTransform: "none",
              "&:hover": { backgroundColor: "rgba(13, 44, 84, 0.04)", borderColor: "#1a4778" }
            }}
          >
            Send Email
          </Button>
          )}
          <Button 
            onClick={handleDone} 
            variant="contained"
            sx={{
              backgroundColor: "#dbf14e",
              color: "#121212",
              fontWeight: 600,
              borderRadius: "100px",
              border: "2px solid #121212",
              px: 4,
              "&:hover": { backgroundColor: "#c9df3e" }
            }}
          >
            Done!
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {!(menuMember && memberYearsMap[menuMember.id] && memberYearsMap[menuMember.id].some(y => y.year === new Date().getFullYear())) && (
        <MenuItem onClick={() => handleSendRenewalNotification([menuMember?.id])}>
          <NotificationsIcon sx={{ mr: 1, fontSize: 20, color: "#0d2c54" }} />
          Send Renewal Notification
        </MenuItem>
        )}
        {menuMember?.email && menuMember.email.trim() && menuMember.duty_type && menuMember.duty_type !== 'No Duty' && (
        <MenuItem onClick={() => {
          setRotaDutiesSingleMember(menuMember);
          setRotaDutiesConfirmOpen(true);
          handleMenuClose();
        }}>
          <CalendarMonthIcon sx={{ mr: 1, fontSize: 20, color: "#0d2c54" }} />
          Send Rota Duties
        </MenuItem>
        )}
        {menuMember?.email && menuMember.email.trim() && !singleEmailMember && (
        <MenuItem onClick={() => {
          setExcludedMemberIds(prev => {
            const n = new Set(prev);
            if (n.has(menuMember.id)) { n.delete(menuMember.id); } else { n.add(menuMember.id); }
            return n;
          });
          handleMenuClose();
        }} sx={{ color: excludedMemberIds.has(menuMember?.id) ? "#0d2c54" : "#c62828" }}>
          {excludedMemberIds.has(menuMember?.id)
            ? <><AddIcon sx={{ mr: 1, fontSize: 20 }} />Include in bulk email</>
            : <><CloseIcon sx={{ mr: 1, fontSize: 20 }} />Exclude from bulk email</>
          }
        </MenuItem>
        )}
        {menuMember?.email && menuMember.email.trim() && (
        <MenuItem onClick={() => openSingleMemberEmail(menuMember)}>
          <EmailIcon sx={{ mr: 1, fontSize: 20, color: "#0d2c54" }} />
          Send Email
        </MenuItem>
        )}
        <MenuItem onClick={() => handlePrintMembershipCard([menuMember?.id])}>
          <BadgeIcon sx={{ mr: 1, fontSize: 20, color: "#0d2c54" }} />
          Print Membership Card
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: "#d32f2f" }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
          Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={renewalConfirmOpen}
        onClose={() => setRenewalConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: "#0d2c54" }}>
          Send Renewal Invitations
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to send renewal invitation emails to <strong>{filteredMembers.filter(m => m.email && m.email.trim()).length}</strong> member(s) with email addresses?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: "#666" }}>
            Emails will be sent with a 2-second delay between each to avoid rate limits.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button onClick={() => setRenewalConfirmOpen(false)} sx={{ color: "#666" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setRenewalConfirmOpen(false);
              handleSendRenewalNotification(filteredMembers.filter(m => m.email && m.email.trim()).map(m => m.id));
            }}
            sx={{
              backgroundColor: "#dbf14e",
              color: "#121212",
              fontWeight: 600,
              borderRadius: "100px",
              border: "2px solid #121212",
              "&:hover": { backgroundColor: "#c9df3e" }
            }}
          >
            Send {filteredMembers.filter(m => m.email && m.email.trim()).length} Email(s)
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rotaDutiesConfirmOpen}
        onClose={() => { setRotaDutiesConfirmOpen(false); setRotaDutiesSingleMember(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: "#0d2c54" }}>
          Send Rota Duty Schedule
        </DialogTitle>
        <DialogContent>
          {rotaDutiesSingleMember ? (
            <Typography>
              Send rota duty schedule email to <strong>{rotaDutiesSingleMember.first_name} {rotaDutiesSingleMember.last_name || rotaDutiesSingleMember.name || ''}</strong>?
            </Typography>
          ) : (
            <Typography>
              Send rota duty schedule emails to <strong>{filteredMembers.filter(m => m.email && m.email.trim() && m.duty_type && m.duty_type !== 'No Duty').length}</strong> member(s) with duties and email addresses?
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 1, color: "#666" }}>
            Each email lists the member's upcoming published duties along with the names and contact details of others on duty for the same sessions. Members with no upcoming duties will be skipped.
          </Typography>
          {!rotaDutiesSingleMember && (
            <Typography variant="body2" sx={{ mt: 1, color: "#666" }}>
              Emails will be sent with a 2-second delay between each to avoid rate limits.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button onClick={() => { setRotaDutiesConfirmOpen(false); setRotaDutiesSingleMember(null); }} sx={{ color: "#666" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const ids = rotaDutiesSingleMember
                ? [rotaDutiesSingleMember.id]
                : filteredMembers.filter(m => m.email && m.email.trim() && m.duty_type && m.duty_type !== 'No Duty').map(m => m.id);
              setRotaDutiesConfirmOpen(false);
              setRotaDutiesSingleMember(null);
              handleSendRotaDuties(ids);
            }}
            sx={{
              backgroundColor: "#dbf14e",
              color: "#121212",
              fontWeight: 600,
              borderRadius: "100px",
              border: "2px solid #121212",
              "&:hover": { backgroundColor: "#c9df3e" }
            }}
          >
            {rotaDutiesSingleMember
              ? 'Send Email'
              : `Send ${filteredMembers.filter(m => m.email && m.email.trim() && m.duty_type && m.duty_type !== 'No Duty').length} Email(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rotaPreviewOpen}
        onClose={() => setRotaPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: "#0d2c54", pb: 0 }}>
          Rota Email Preview
        </DialogTitle>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs
            value={rotaPreviewTab}
            onChange={(_, newVal) => {
              setRotaPreviewTab(newVal);
              fetchRotaEmailPreview(newVal);
            }}
            textColor="inherit"
            TabIndicatorProps={{ style: { backgroundColor: "#dbf14e", height: 3 } }}
          >
            <Tab label="1st Safety Officer" value="1st Safety Officer" sx={{ fontWeight: 600, fontSize: 13 }} />
            <Tab label="2nd Safety Officer" value="2nd Safety Officer" sx={{ fontWeight: 600, fontSize: 13 }} />
            <Tab label="Administrator" value="Administrator" sx={{ fontWeight: 600, fontSize: 13 }} />
          </Tabs>
        </Box>
        <DialogContent sx={{ flex: 1, p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {rotaPreviewLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <CircularProgress sx={{ color: "#0d2c54" }} />
            </Box>
          ) : rotaPreviewMessage ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">{rotaPreviewMessage}</Alert>
            </Box>
          ) : rotaPreviewHtml ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {rotaPreviewSubject && (
                <Box sx={{ px: 3, py: 1.5, borderBottom: '1px solid #eee', backgroundColor: '#fafafa' }}>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    <strong>Subject:</strong> {rotaPreviewSubject}
                  </Typography>
                </Box>
              )}
              <iframe
                srcDoc={rotaPreviewHtml}
                title="Email Preview"
                sandbox="allow-same-origin"
                style={{ flex: 1, border: 'none', width: '100%' }}
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button onClick={() => setRotaPreviewOpen(false)} sx={{ color: "#666" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: "#0d2c54" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{memberToDelete?.first_name} {memberToDelete?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button onClick={handleDeleteCancel} sx={{ color: "#666" }}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained"
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              "&:hover": { backgroundColor: "#b71c1c" }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openingEmailOpen}
        onClose={() => !openingEmailSending && setOpeningEmailOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: "#0d2c54" }}>
          Email Opening Times
        </DialogTitle>
        <DialogContent>
          <Typography>
            Send the pool opening times to <strong>{openingRecipientList().length}</strong> member(s) with email addresses?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: "#666" }}>
            Each email contains a summary of the upcoming opening dates, a note that the next two weeks are always on the home page, and the full schedule attached as a PDF.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: "#666" }}>
            Emails will be sent with a 2-second delay between each to avoid rate limits.
          </Typography>
          <Button size="small" startIcon={<FileDownloadIcon />} onClick={previewOpeningPdf} sx={{ textTransform: 'none', mt: 1, color: '#0d2c54' }}>
            Preview PDF attachment
          </Button>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button onClick={() => setOpeningEmailOpen(false)} disabled={openingEmailSending} sx={{ color: "#666" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={sendOpeningTimesEmail}
            disabled={openingEmailSending || openingRecipientList().length === 0}
            sx={{
              backgroundColor: "#dbf14e",
              color: "#121212",
              fontWeight: 600,
              borderRadius: "100px",
              border: "2px solid #121212",
              "&:hover": { backgroundColor: "#c9df3e" }
            }}
          >
            {openingEmailSending ? 'Sending…' : `Send ${openingRecipientList().length} Email(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={composeOpen} onClose={() => { setComposeOpen(false); setSingleEmailMember(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: "'Chillax', sans-serif", color: "#0d2c54", fontWeight: 600 }}>
          {singleEmailMember ? `Email ${singleEmailMember.first_name || ''} ${singleEmailMember.name || ''}`.trim() : 'Compose Email'}
        </DialogTitle>
        <DialogContent>
          {singleEmailMember ? (
            <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
              This email will be sent to <strong>{singleEmailMember.first_name || ''} {singleEmailMember.name || ''}</strong> ({singleEmailMember.email}).
            </Alert>
          ) : (() => {
            const withEmail = filteredMembers.filter(m => m.email && m.email.trim());
            const effective = withEmail.filter(m => !excludedMemberIds.has(m.id));
            const excluded = withEmail.filter(m => excludedMemberIds.has(m.id));
            const noEmail = filteredMembers.length - withEmail.length;
            const isScheduled = !!(composeForm.scheduleDate && composeForm.scheduleTime);
            const severity = effective.length === 0 ? "warning" : "info";
            const notes = [];
            if (noEmail > 0) notes.push(`${noEmail} skipped — no email address`);
            if (!isScheduled && excluded.length > 0) notes.push(
              <span key="excl">{excluded.length} manually excluded — <Button size="small" sx={{ p: 0, minWidth: 'auto', textTransform: 'none', fontSize: 'inherit', fontWeight: 600, color: '#0d2c54', verticalAlign: 'baseline', lineHeight: 'inherit' }} onClick={() => setExcludedMemberIds(new Set())}>undo all</Button></span>
            );
            return (
              <>
                <Alert severity={severity} sx={{ mb: 1, mt: 1 }}>
                  {effective.length === 0
                    ? "No recipients — adjust the filters or remove exclusions."
                    : isScheduled
                    ? <><strong>{effective.length} member{effective.length !== 1 ? 's' : ''}</strong> match the current filters today and have email addresses{notes.length > 0 && <> ({notes.reduce((a, n, i) => [a, i > 0 ? '; ' : '', n])})</>}. The recipient list will be re-evaluated at the scheduled send time — this number may change.</>
                    : <><strong>{effective.length} member{effective.length !== 1 ? 's' : ''}</strong> matching the current filters will receive this email{notes.length > 0 && <> ({notes.reduce((a, n, i) => [a, i > 0 ? '; ' : '', n])})</>}.</>
                  }
                </Alert>
                {!isScheduled && (
                  <Box sx={{ mb: 2 }}>
                    <Button
                      size="small"
                      onClick={() => setRecipientsExpanded(e => !e)}
                      endIcon={recipientsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{ color: '#0d2c54', textTransform: 'none', fontWeight: 600, p: 0, fontSize: '0.8rem' }}
                    >
                      {recipientsExpanded ? 'Hide' : 'View'} recipients
                    </Button>
                    <Collapse in={recipientsExpanded}>
                      <Box sx={{ mt: 1, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                        <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
                          {effective.length === 0 && (
                            <Box sx={{ px: 2, py: 1.5 }}>
                              <Typography variant="body2" sx={{ color: '#999' }}>No active recipients.</Typography>
                            </Box>
                          )}
                          {effective.map((m, idx) => (
                            <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.75, borderBottom: idx < effective.length - 1 ? '1px solid #f5f5f5' : 'none', '&:hover': { backgroundColor: '#fafafa' } }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{m.first_name || ''} {m.name || ''}</Typography>
                                <Typography variant="caption" sx={{ color: '#888' }} noWrap>{m.email}</Typography>
                              </Box>
                              <Tooltip title="Exclude from this send">
                                <IconButton size="small" onClick={() => setExcludedMemberIds(prev => { const n = new Set(prev); n.add(m.id); return n; })} sx={{ ml: 1, color: '#bbb', '&:hover': { color: '#d32f2f' } }}>
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ))}
                        </Box>
                        {excluded.length > 0 && (
                          <>
                            <Divider />
                            <Box sx={{ backgroundColor: '#fafafa', px: 2, py: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#999', display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Excluded ({excluded.length})
                              </Typography>
                              {excluded.map(m => (
                                <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', py: 0.25 }}>
                                  <Typography variant="body2" sx={{ flex: 1, color: '#bbb', textDecoration: 'line-through' }}>{m.first_name || ''} {m.name || ''}</Typography>
                                  <Button size="small" onClick={() => setExcludedMemberIds(prev => { const n = new Set(prev); n.delete(m.id); return n; })} sx={{ color: '#0d2c54', textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, minWidth: 'auto', py: 0 }}>
                                    Include
                                  </Button>
                                </Box>
                              ))}
                            </Box>
                          </>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                )}
              </>
            );
          })()}
          <TextField
            fullWidth
            label="Subject"
            value={composeForm.subject}
            onChange={(e) => setComposeForm(prev => ({ ...prev, subject: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" sx={{ mb: 0.5, color: "#666" }}>Email Body</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: '#999', mr: 0.5 }}>Insert:</Typography>
            {[
              { label: 'First Name', value: '{firstName}' },
              { label: 'Last Name', value: '{lastName}' },
              { label: 'Login Button', value: '{loginLink}' },
              { label: 'Login URL', value: '{loginUrl}' },
            ].map((p) => (
              <Chip
                key={p.value}
                label={p.label}
                size="small"
                clickable
                onClick={() => insertPlaceholder(p.value)}
                sx={{
                  height: 26,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  backgroundColor: '#f0f4ff',
                  border: '1px solid #c5cfe0',
                  '&:hover': { backgroundColor: '#dbf14e', borderColor: '#c9df3e' },
                }}
              />
            ))}
            <Typography variant="caption" sx={{ color: '#999', ml: 1, mr: 0.5 }}>Deep links:</Typography>
            {[
              { label: 'Update Details', value: '{deepLink:update-details}' },
              { label: 'Duty Details', value: '{deepLink:duty-details}' },
              { label: 'Duty Rota', value: '{deepLink:duty-rota}' },
              { label: 'Renew', value: '{deepLink:renew}' },
            ].map((p) => (
              <Chip
                key={p.value}
                label={p.label}
                size="small"
                clickable
                onClick={() => insertPlaceholder(p.value)}
                sx={{
                  height: 26,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  backgroundColor: '#fff4e6',
                  border: '1px solid #e0c9a5',
                  '&:hover': { backgroundColor: '#dbf14e', borderColor: '#c9df3e' },
                }}
              />
            ))}
          </Box>
          <Box sx={{ mb: 2, '.ql-container': { minHeight: 200, fontFamily: "'General Sans', sans-serif", fontSize: '15px' }, '.ql-editor': { minHeight: 200 }, '.ql-toolbar': { borderRadius: '8px 8px 0 0', backgroundColor: '#fafafa' }, '.ql-container.ql-snow': { borderRadius: '0 0 8px 8px' } }}>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={composeForm.body}
              onChange={(value) => setComposeForm(prev => ({ ...prev, body: value }))}
              placeholder="Write your message here..."
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline'],
                  [{ 'color': [] }],
                  [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                  ['link'],
                  ['clean']
                ]
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ color: "#999", display: 'block', mt: -1, mb: 1 }}>
            The email will be wrapped in the CCSC branded template with header and footer. Add your own greeting and sign-off in your message.
            <strong> Login Button</strong> inserts a styled button. <strong>Login URL</strong> inserts a plain link — use this to embed the URL in your own text.
          </Typography>
          {!singleEmailMember && (<>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#666", mb: 1 }}>
            Schedule (optional) — leave blank to send immediately. Times are in your local timezone.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              type="date"
              label="Send Date"
              value={composeForm.scheduleDate}
              onChange={(e) => setComposeForm(prev => ({ ...prev, scheduleDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />
            <TextField
              type="time"
              label="Send Time"
              value={composeForm.scheduleTime}
              onChange={(e) => setComposeForm(prev => ({ ...prev, scheduleTime: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
          </Box>

          {scheduledEmails.filter(e => e.status === 'pending').length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0d2c54", mb: 1 }}>
                Scheduled Emails
              </Typography>
              {scheduledEmails.filter(e => e.status === 'pending').map(email => (
                <Paper key={email.id} variant="outlined" sx={{ p: 2, mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center", borderRadius: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{email.subject}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Scheduled: {new Date(email.scheduled_at).toLocaleString('en-GB')} · By: {email.created_by}
                    </Typography>
                  </Box>
                  <Button size="small" color="error" onClick={() => handleCancelScheduledEmail(email.id)}>
                    Cancel
                  </Button>
                </Paper>
              ))}
            </Box>
          )}
          </>)}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setComposeOpen(false); setSingleEmailMember(null); }} sx={{ color: "#666" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmPrepare}
            disabled={composeSending || composeConfirmLoading || !composeForm.subject.trim() || !composeForm.body.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()}
            sx={{
              backgroundColor: composeForm.scheduleDate && composeForm.scheduleTime ? "#0d2c54" : "#dbf14e",
              color: composeForm.scheduleDate && composeForm.scheduleTime ? "#fff" : "#121212",
              borderRadius: "100px",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { backgroundColor: composeForm.scheduleDate && composeForm.scheduleTime ? "#1a4778" : "#c9df3e" },
            }}
          >
            {composeConfirmLoading ? <CircularProgress size={20} /> : (composeForm.scheduleDate && composeForm.scheduleTime ? "Review & Schedule" : "Review & Send")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email send confirmation dialog */}
      <Dialog
        open={composeConfirmOpen}
        onClose={() => setComposeConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "'Chillax', sans-serif", color: "#0d2c54", fontWeight: 600 }}>
          {composeConfirmData?.type === 'scheduled' ? 'Confirm Scheduled Email' : 'Confirm Email Send'}
        </DialogTitle>
        <DialogContent>
          {composeConfirmData && (<>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>Subject</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{composeConfirmData.subject}</Typography>
            </Box>

            {composeConfirmData.type === 'single' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This email will be sent to <strong>{composeConfirmData.sampleNames[0] || 'this member'}</strong> ({composeConfirmData.email}).
              </Alert>
            )}

            {composeConfirmData.type === 'immediate' && (
              <Alert severity={composeConfirmData.count === 0 ? "warning" : "info"} sx={{ mb: 2 }}>
                {composeConfirmData.count === 0
                  ? "No members with email addresses match the current filter. No emails will be sent."
                  : <>This will be sent immediately to <strong>{composeConfirmData.count} member{composeConfirmData.count !== 1 ? 's' : ''}</strong> with email addresses.{composeConfirmData.excludedCount > 0 && <> ({composeConfirmData.excludedCount} manually excluded.)</>}</>
                }
              </Alert>
            )}

            {composeConfirmData.type === 'scheduled' && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Scheduled for <strong>{composeConfirmData.scheduledAt?.toLocaleString('en-GB', { dateStyle: 'full', timeStyle: 'short' })}</strong>.
                  Based on today's members, this would go to <strong>{composeConfirmData.count} member{composeConfirmData.count !== 1 ? 's' : ''}</strong> with email addresses.
                </Alert>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  The recipient list will be re-evaluated at the scheduled send time and may differ from today's count if members are added, removed, or their details change before then.
                </Alert>
              </>
            )}

            {composeConfirmData.count > 0 && composeConfirmData.sampleNames.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                  {composeConfirmData.type === 'single' ? 'Recipient' : `Sample recipients (${composeConfirmData.sampleNames.length} of ${composeConfirmData.count})`}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {composeConfirmData.sampleNames.map((name, i) => (
                    <Chip key={i} label={name} size="small" sx={{ backgroundColor: '#f0f4ff', border: '1px solid #c5cfe0' }} />
                  ))}
                  {composeConfirmData.count > 5 && (
                    <Chip label={`+${composeConfirmData.count - 5} more`} size="small" sx={{ backgroundColor: '#f5f5f5', color: '#666' }} />
                  )}
                </Box>
              </Box>
            )}
          </>)}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setComposeConfirmOpen(false)} sx={{ color: "#666" }}>
            Go Back
          </Button>
          <Button
            variant="contained"
            disabled={composeSending || (composeConfirmData?.type !== 'single' && composeConfirmData?.count === 0)}
            onClick={() => {
              setComposeConfirmOpen(false);
              handleSendCustomEmail();
            }}
            sx={{
              backgroundColor: composeConfirmData?.type === 'scheduled' ? "#0d2c54" : "#dbf14e",
              color: composeConfirmData?.type === 'scheduled' ? "#fff" : "#121212",
              borderRadius: "100px",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { backgroundColor: composeConfirmData?.type === 'scheduled' ? "#1a4778" : "#c9df3e" },
            }}
          >
            {composeSending ? <CircularProgress size={20} /> : composeConfirmData?.type === 'scheduled' ? 'Confirm & Schedule' : 'Confirm & Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <div className="admin-footer">
        <span className="admin-footer-text">
          © Comberton Community Swimming Club, {currentYear}.
        </span>
      </div>
    </div>
  );
}
